const YI2CFloatFrame=(()=>{
// Display-only floating frame. Never used to produce the canvas print file.
const FRAME_PROFILE = Object.freeze({face:12,gap:5,depth:54,rebate:42,foot:37});
// Existing fulfilment IDs and customer-facing names are intentionally unchanged.
const FRAME_FINISHES = Object.freeze({
  black:{name:'Black',base:'#29292a',grain:'#111112',light:'#555456',strength:0.46},
  white:{name:'White',base:'#efefed',grain:'#b9b7b2',light:'#ffffff',strength:0.20},
  darkwood:{name:'Dark wood',base:'#493f39',grain:'#251f1c',light:'#74665a',strength:0.39},
  barewood:{name:'Bare wood',base:'#e8cfa9',grain:'#b79770',light:'#f8e5c6',strength:0.34},
});
function frameGeometry(widthMm,heightMm,viewportWidth,viewportHeight,margin=32) {
  if (![widthMm,heightMm,viewportWidth,viewportHeight].every(n=>Number.isFinite(n)&&n>0)) throw Error('Invalid frame dimensions');
  const inset=FRAME_PROFILE.face+FRAME_PROFILE.gap,outerWidth=widthMm+2*inset,outerHeight=heightMm+2*inset;
  const scale=Math.min(Math.max(1,viewportWidth-2*margin)/outerWidth,Math.max(1,viewportHeight-2*margin)/outerHeight);
  const outer={x:(viewportWidth-outerWidth*scale)/2,y:(viewportHeight-outerHeight*scale)/2,width:outerWidth*scale,height:outerHeight*scale};
  const shrink=(r,n)=>({x:r.x+n,y:r.y+n,width:r.width-2*n,height:r.height-2*n});
  return {mode:'box',scale,canvasWidthMm:widthMm,canvasHeightMm:heightMm,outerWidthMm:outerWidth,outerHeightMm:outerHeight,
    framePixels:12*scale,gapPixels:5*scale,frameOuterRectangle:outer,gapRectangle:shrink(outer,12*scale),frontFace:shrink(outer,inset*scale)};
}
const timberTiles=new Map();
function timberTile(id,makeCanvas) {
  if(timberTiles.has(id))return timberTiles.get(id);
  const material=FRAME_FINISHES[id]||FRAME_FINISHES.black,c=makeCanvas();c.width=384;c.height=48;
  const ctx=c.getContext('2d');ctx.fillStyle=material.base;ctx.fillRect(0,0,c.width,c.height);
  let seed=1817;const random=()=>{seed=(Math.imul(seed,1664525)+1013904223)>>>0;return seed/4294967296;};
  // 4 px/mm. Fine longitudinal pores rather than oversized decorative wood stripes.
  for(let i=0;i<470;i++){
    const x=random()*384,y=random()*48,length=2+random()*25;
    ctx.beginPath();ctx.moveTo(x,y);ctx.bezierCurveTo(x+length*.3,y-.5,x+length*.7,y+.55,x+length,y+(random()-.5));
    ctx.strokeStyle=i%4===0?material.light:material.grain;ctx.globalAlpha=(.15+random()*.65)*material.strength;
    ctx.lineWidth=.3+random()*.65;ctx.stroke();
  }
  ctx.globalAlpha=1;timberTiles.set(id,c);return c;
}
function drawFrame(ctx,geometry,finish,makeCanvas=()=>document.createElement('canvas')) {
  const material=FRAME_FINISHES[finish]||FRAME_FINISHES.black;
  const o=geometry.frameOuterRectangle,g=geometry.gapRectangle,f=geometry.frontFace,p=geometry.framePixels,scale=geometry.scale;
  ctx.save();ctx.fillStyle=material.base;ctx.shadowColor='rgba(20,18,16,.19)';ctx.shadowBlur=8*scale;ctx.shadowOffsetY=3*scale;
  ctx.fillRect(o.x,o.y,o.width,o.height);ctx.restore();
  // Four mitred trapezoids, each with the grain running along the rail.
  const rails=[[o.x,o.y,0,o.width],[o.x+o.width,o.y,Math.PI/2,o.height],[o.x+o.width,o.y+o.height,Math.PI,o.width],[o.x,o.y+o.height,-Math.PI/2,o.height]];
  const tile=timberTile(finish,makeCanvas);
  for(let rail=0;rail<rails.length;rail++){
    const [x,y,rotation,length]=rails[rail];ctx.save();ctx.translate(x,y);ctx.rotate(rotation);
    ctx.beginPath();ctx.moveTo(0,0);ctx.lineTo(length,0);ctx.lineTo(length-p,p);ctx.lineTo(p,p);ctx.closePath();ctx.clip();
    ctx.save();ctx.scale(scale/4,scale/4);ctx.fillStyle=ctx.createPattern(tile,'repeat');ctx.translate(rail*37,0);ctx.fillRect(-rail*37,0,length*4/scale,48);ctx.restore();
    const shade=ctx.createLinearGradient(0,0,0,p);shade.addColorStop(0,'rgba(255,255,255,.13)');shade.addColorStop(.12,'rgba(255,255,255,.025)');shade.addColorStop(.82,'rgba(0,0,0,0)');shade.addColorStop(1,'rgba(0,0,0,.16)');
    ctx.fillStyle=shade;ctx.fillRect(0,0,length,p);ctx.restore();
  }
  ctx.save();ctx.lineWidth=.22*scale;ctx.strokeStyle='rgba(18,16,14,.28)';
  const corners=[[o.x,o.y,g.x,g.y],[o.x+o.width,o.y,g.x+g.width,g.y],[o.x+o.width,o.y+o.height,g.x+g.width,g.y+g.height],[o.x,o.y+o.height,g.x,g.y+g.height]];
  for(const [x,y,ix,iy]of corners){ctx.beginPath();ctx.moveTo(x,y);ctx.lineTo(ix,iy);ctx.stroke();}
  ctx.strokeRect(o.x,o.y,o.width,o.height);ctx.restore();
  // The 5mm recess exposes the same finished timber, not a black liner.
  // Its restrained shading is derived from that material on every rail.
  const recessTone=factor=>{const rgb=material.base.slice(1).match(/../g).map(v=>Math.round(parseInt(v,16)*factor));return `rgba(${rgb.join(',')},.6)`;};
  ctx.fillStyle=material.base;ctx.fillRect(g.x,g.y,g.width,g.height);
  const gapRails=[[g.x,g.y,0,g.width],[g.x+g.width,g.y,Math.PI/2,g.height],[g.x+g.width,g.y+g.height,Math.PI,g.width],[g.x,g.y+g.height,-Math.PI/2,g.height]];
  for(let rail=0;rail<gapRails.length;rail++){
    const [x,y,angle,length]=gapRails[rail],gap=geometry.gapPixels;ctx.save();ctx.translate(x,y);ctx.rotate(angle);
    ctx.beginPath();ctx.moveTo(0,0);ctx.lineTo(length,0);ctx.lineTo(length-gap,gap);ctx.lineTo(gap,gap);ctx.closePath();ctx.clip();
    ctx.save();ctx.scale(scale/4,scale/4);ctx.fillStyle=ctx.createPattern(tile,'repeat');ctx.translate(rail*37,0);ctx.fillRect(-rail*37,0,length*4/scale,gap*4/scale);ctx.restore();
    const gradient=ctx.createLinearGradient(0,0,0,gap);gradient.addColorStop(0,recessTone(.60));gradient.addColorStop(.6,recessTone(.90));gradient.addColorStop(1,recessTone(.78));ctx.fillStyle=gradient;ctx.fillRect(0,0,length,gap);ctx.restore();
  }
  return f;
}
function frameFace(face,size,finish,outputWidth,makeCanvas=()=>document.createElement('canvas')) {
  const c=makeCanvas(),w=size.widthInches*25.4,h=size.heightInches*25.4;
  c.width=Math.max(1,Math.round(outputWidth));c.height=Math.max(1,Math.round(c.width*(h+34)/(w+34)));
  const ctx=c.getContext('2d'),geometry=frameGeometry(w,h,c.width,c.height,0);drawFrame(ctx,geometry,finish,makeCanvas);
  const f=geometry.frontFace;ctx.drawImage(face,f.x,f.y,f.width,f.height);return c;
}

return {frameGeometry,drawFrame,frameFace,FRAME_FINISHES};})();
/* Same-origin opaque Express draft handles; shared with the verified Express page. */
(() => {
  "use strict";
  const prefix = "yi2c-express-draft-v1:",
    ttl = 60 * 60 * 1000;
  const valid = (token) => /^[0-9a-f]{64}$/.test(token || "");
  window.YI2CExpressDraft = {
    create() {
      const bytes = crypto.getRandomValues(new Uint8Array(32));
      return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
    },
    read(token) {
      if (!valid(token)) return null;
      try {
        const value = JSON.parse(
          sessionStorage.getItem(prefix + token) || "null",
        );
        if (!value || value.expiresAt <= Date.now()) {
          sessionStorage.removeItem(prefix + token);
          return null;
        }
        return value;
      } catch {
        return null;
      }
    },
    write(token, value) {
      if (!valid(token)) throw new Error("INVALID_DRAFT");
      sessionStorage.setItem(
        prefix + token,
        JSON.stringify({ ...value, expiresAt: Date.now() + ttl }),
      );
      return token;
    },
    remove(token) {
      if (valid(token)) sessionStorage.removeItem(prefix + token);
    },
    valid,
  };
})();
/* Desktop presentation adapter. All changes invoke the pinned builder's existing handlers. */
window.__yi2cMountDesktop = function (root, bridge) {
  const mq = matchMedia("(min-width:900px)");
  if (!root.__photoDesktopResize) {
    root.__photoDesktopResize = true;
    mq.addEventListener("change", () => {
      if (mq.matches) window.__yi2cMountDesktop(root, bridge);
    });
  }
  if (window.__yi2cMountPosterMobile?.(root, bridge)) return;
  if (!mq.matches || root.querySelector("[data-photo-desktop-ui]")) return;
  if (window.__yi2cMountPosterDesktop?.(root, bridge)) return;
  const { state: s, elements: a } = bridge;
  const host = document.createElement("div");
  host.dataset.photoDesktopUi = "";
  const section = (title, content, extra = "") =>
    `<section class="yi2c-desktop-option-section" ${extra}><div class="yi2c-desktop-section-head"><strong>${title}</strong></div>${content}</section>`;
  const cards = (key, items, cls = "yi2c-desktop-segmented") =>
    `<div class="${cls}" data-card-count="${items.length}">${items.map(([value, label, detail]) => `<button type="button" data-choice="${key}" data-value="${value}" ${key === "product" ? `data-desktop-product="${value}"` : ""} aria-pressed="false"><strong>${label}</strong>${detail ? `<span>${detail}</span>` : ""}</button>`).join("")}</div>`;
  host.innerHTML = `<aside class="yi2c-desktop-order-panel" aria-label="Print and review choices">
    <div class="photo-options-scroll">
      <div class="yi2c-desktop-order-head"><span class="yi2c-kicker">Choose your finish</span><h2>Make it yours</h2><p data-photo-subtitle></p></div>
      ${section(
        "Choose an outcome",
        cards("product", [
          ["canvas", "Canvas"],
          ["poster", "Poster"],
        ]) +
          `<p class="yi2c-outcome-description" data-photo-description></p><p class="yi2c-desktop-promo">3 FOR 2 — Choose three. Pay for two.</p>`,
      )}
      ${section(
        "Supply your image",
        `<div class="yi2c-supply-choices"><div><button type="button" data-choice="supply" data-value="upload" aria-pressed="false" aria-describedby="yi2c-upload-help"><strong>Upload</strong></button><p id="yi2c-upload-help">Choose a photograph from this device.</p></div><div><button type="button" data-choice="supply" data-value="separate" aria-pressed="false" aria-describedby="yi2c-email-help"><strong>Email</strong></button><p id="yi2c-email-help">Send a large file after checkout.</p></div></div>` +
          `<div class="photo-file" hidden><img alt="Selected photograph"><div><strong data-photo-filename></strong><small data-photo-validation></small></div><button type="button" data-photo-replace>Change</button></div><button type="button" class="photo-retained" data-photo-retained hidden>Use your previously uploaded photograph</button><p class="photo-inline-status" data-photo-upload-status role="status"></p><progress data-photo-upload-progress max="100" hidden aria-label="Photograph upload progress"></progress><button type="button" class="yi2c-text-btn" data-photo-upload-retry hidden>Retry photograph</button>`,
      )}
      ${section(
        "Positioning",
        cards("position", [
          ["self", "Position it yourself"],
          ["studio", "Let our studio decide"],
        ]),
      )}
      ${section(
        "Size",
        `<div class="photo-custom-heading"><span>Custom size</span>${cards(
          "unit",
          [
            ["in", "Inches"],
            ["cm", "CM"],
          ],
        )}</div><div class="photo-dimensions"><label>Width<input type="number" inputmode="decimal" step="any" data-photo-width></label><span>×</span><label>Height<input type="number" inputmode="decimal" step="any" data-photo-height></label></div><p class="photo-inline-status" data-photo-size-message></p>${cards(
          "orientation",
          [
            ["portrait", "Portrait"],
            ["landscape", "Landscape"],
          ],
        )}<div data-photo-size-groups></div>`,
      )}
      ${section(
        "Canvas style",
        cards(
          "finish",
          [
            ["slim", "Slim canvas", "0.75″"],
            ["chunky", "Chunky canvas", "1.75″"],
            ["box", "Floating frame", "5mm gap"],
          ],
          "yi2c-desktop-finish-grid",
        ) +
          `<div data-photo-frame hidden>${cards("frame", [
            ["black", "Black"],
            ["white", "White"],
            ["darkwood", "Dark wood"],
            ["barewood", "Bare wood"],
          ])}</div>`,
        "data-photo-canvas",
      )}
      ${section(
        "Canvas sides",
        cards("sides", [
          ["gallery", "Gallery"],
          ["mirror", "Mirror"],
          ["white", "White"],
          ["black", "Black"],
          ["colour", "Any colour"],
        ]) + '<p data-photo-mirror-note hidden>Mirror wrap — included with floating frames</p><div data-photo-colour-slot></div>',
        "data-photo-canvas",
      )}
      ${section(
        "Colour treatment",
        cards("treatment", [
          ["original", "Original"],
          ["bw", "Black & White"],
          ["sepia", "Sepia"],
        ]),
      )}
      <section class="yi2c-desktop-option-section" data-photo-kit-section><label class="yi2c-desktop-hanger"><input type="checkbox" data-photo-kit><span>Add hanging kit</span><strong data-photo-kit-price>+£1.25</strong></label></section>
      <section class="yi2c-desktop-option-section"><label class="yi2c-desktop-comment"><strong>Add a comment for us</strong><textarea rows="3" maxlength="500" data-photo-comment placeholder="Anything our Yorkshire studio should know?"></textarea></label></section>
    </div>
    <div class="yi2c-desktop-order-footer"><p class="photo-summary" data-photo-summary></p><div class="yi2c-desktop-total"><span>Current total</span><strong data-photo-total>—</strong></div><button type="button" class="btn btn-primary btn-block" data-photo-review disabled>Review your canvas</button><div class="yi2c-delivery-reassurance"><strong>Free UK Express 24 delivery</strong><span data-photo-delivery>Checking estimated delivery…</span><small>Estimated date, not a guarantee.</small></div><p data-photo-required role="status"></p></div>
  </aside>
  <section class="yi2c-screen is-desktop-review-overlay" data-screen="finish" data-photo-modal role="dialog" aria-modal="true" aria-labelledby="photo-review-title" hidden><header class="yi2c-topbar"><button type="button" class="btn" data-photo-edit>Keep editing</button><div class="yi2c-top-copy"><strong id="photo-review-title">Review your canvas</strong></div></header><div class="yi2c-canvas-stage"><div class="photo-review-artwork"><canvas data-photo-review-canvas></canvas><div data-photo-review-email hidden><strong>Email your image</strong><p>Artwork supplied separately<br>Our studio will position and check it before printing.</p></div><div class="photo-review-caption"><strong data-photo-review-caption-title></strong><small>Your original image and crop settings are safely saved.</small></div></div></div><div class="yi2c-finish-content"><div class="yi2c-desktop-review-summary"><h2>Your print</h2><dl data-photo-review-summary></dl></div><div class="yi2c-delivery-reassurance"><strong>Free UK Express 24 delivery</strong><span data-photo-review-delivery></span><small>Estimated date, not a guarantee.</small></div><div class="yi2c-desktop-total"><span>Complete price</span><strong data-photo-review-total></strong></div><button type="button" class="btn btn-primary btn-block" data-photo-basket>Add to basket</button><p data-photo-basket-status role="status"></p></div></section>`;
  root.append(host);
  root.classList.add("has-photo-desktop-panel");
  const q = (sel) => host.querySelector(sel),
    qa = (sel) => [...host.querySelectorAll(sel)];
  const native = (sel) =>
    [...root.querySelectorAll(sel)].find((el) => !host.contains(el));
  const fire = (el, type = "change") =>
    el?.dispatchEvent(new Event(type, { bubbles: true }));
  const set = (el, value, type = "input") => {
    if (!el) return;
    el.value = value;
    fire(el, type);
  };
  const click = (sel) => {
    const el = native(sel);
    if (!el) return;
    el.disabled = false;
    el.click();
  };
  const text = (sel, value) => {
    const el = q(sel);
    if (el.textContent !== String(value ?? "")) el.textContent = value ?? "";
  };
  // Move the original node: its handlers, validation and bridge references stay intact.
  const colourHome = document.createComment("photo-side-colour-home");
  a.edgeColourWrap.before(colourHome);
  q("[data-photo-colour-slot]").append(a.edgeColourWrap);
  const label = {
    slim: "Slim",
    chunky: "Chunky",
    box: "Floating frame",
    unframed: "Unframed",
    gallery: "Gallery",
    mirror: "Mirror",
    white: "White",
    black: "Black",
    colour: "Any colour",
    original: "Original",
    bw: "Black & White",
    sepia: "Sepia",
  };
  let sizeKey = "",
    savedFocus = null,
    lastSummary = "",
    updateQueued = false;
  const modal = q("[data-photo-modal]");
  const update = () => {
    updateQueued = false;
    const size = bridge.size(),
      product = s.productType === "poster" ? "Poster" : "Canvas";
    const orientation = !size
      ? "Choose dimensions"
      : size.widthInches === size.heightInches
        ? "Square"
        : size.widthInches > size.heightInches
          ? "Landscape"
          : "Portrait";
    text(
      "[data-photo-subtitle]",
      `${product} · ${size?.label || "Choose a size"} · ${orientation}`,
    );
    text(
      "[data-photo-description]",
      s.productType === "poster"
        ? "Printed on 230gsm matte paper."
        : "Printed and hand-finished in Yorkshire.",
    );
    const choices = {
      product: s.productType,
      supply: s.imageMethod,
      position: s.designerCrop ? "studio" : "self",
      unit: s.customUnit,
      orientation: orientation.toLowerCase(),
      finish: s.canvasStyle,
      sides: s.edgeStyle,
      treatment: s.printColour,
      frame: s.frameColour,
    };
    qa("[data-choice]").forEach((el) => {
      const selected = String(choices[el.dataset.choice] === el.dataset.value);
      if (el.getAttribute("aria-pressed") !== selected)
        el.setAttribute("aria-pressed", selected);
      el.disabled =
        el.dataset.choice === "position" && s.imageMethod === "separate";
    });
    const shownDimension = (n) =>
      Number((n * (s.customUnit === "cm" ? 2.54 : 1)).toFixed(2));
    for (const [sel, value] of [
      [
        "[data-photo-width]",
        s.customMode
          ? a.customWidthInput.value
          : shownDimension(size.widthInches),
      ],
      [
        "[data-photo-height]",
        s.customMode
          ? a.customHeightInput.value
          : shownDimension(size.heightInches),
      ],
      ["[data-photo-comment]", s.orderNotes],
    ])
      if (document.activeElement !== q(sel) && q(sel).value !== String(value))
        q(sel).value = value;
    qa("[data-photo-canvas]").forEach(
      (el) => (el.hidden = s.productType !== "canvas"),
    );
    q("[data-photo-kit-section]").hidden =
      s.productType !== "canvas" || s.canvasStyle === "unframed";
    q("[data-photo-frame]").hidden = s.canvasStyle !== "box";
    q('[data-choice="sides"]').parentElement.hidden = s.canvasStyle === 'box';
    q('[data-photo-mirror-note]').hidden = s.canvasStyle !== 'box';

    q("[data-photo-kit]").checked = s.hangingKit;
    text(
      "[data-photo-kit-price]",
      a.hangingKitPriceLabel?.textContent || "+£1.25",
    );
    text(
      "[data-photo-size-message]",
      s.customMode
        ? a.customMessage.textContent
        : "Choose a standard size or enter your exact dimensions.",
    );
    const key = JSON.stringify([
      s.productType,
      s.orientation,
      s.customMode,
      s.selectedStandardSizeId,
    ]);
    if (key !== sizeKey) {
      sizeKey = key;
      const groups = [
        ["A Size", ["a5", "a4", "a3", "a2", "a1"]],
        ["Square", ["8x8", "12x12", "16x16", "20x20", "30x30"]],
        ["Rectangle", ["8x10", "12x16", "16x20", "25x30", "30x40"]],
        ["Panoramic", ["9x16", "10x24", "12x30", "20x40"]],
      ];
      const wrap = q("[data-photo-size-groups]");
      wrap.replaceChildren();
      for (const [name, ids] of groups) {
        if (s.productType === "poster" && name === "Panoramic") continue;
        const title = document.createElement("p");
        title.className = "photo-size-group-title";
        title.textContent = name;
        const grid = document.createElement("div");
        grid.className = "yi2c-desktop-size-grid";
        for (const id of s.productType === "poster" && name === "Square"
          ? [
              "8x8",
              "12x12",
              "16x16",
              "20x20",
              "24x24",
              "30x30",
              "33x33",
              "40x40",
            ]
          : ids) {
          const original = native(`[data-size-id="${id}"]`);
          if (
            !original ||
            (s.productType === "poster" &&
              original.textContent.includes("Unavailable"))
          )
            continue;
          const button = document.createElement("button");
          button.type = "button";
          button.dataset.photoSize = id;
          button.innerHTML = original.innerHTML;
          button.setAttribute(
            "aria-pressed",
            String(!s.customMode && s.selectedStandardSizeId === id),
          );
          grid.append(button);
        }
        wrap.append(title, grid);
      }
    }
    const uploadBusy = ["uploading", "preparing"].includes(s.photoPreviewPhase);
    const progress = q("[data-photo-upload-progress]");
    progress.hidden = !uploadBusy;
    const amount = a.uploadProgressTrack?.getAttribute("aria-valuenow");
    if (s.photoPreviewPhase === "uploading" && amount !== null) progress.value = Number(amount);
    else progress.removeAttribute("value");
    q("[data-photo-upload-retry]").hidden = s.photoPreviewPhase !== "failed";
    host.querySelectorAll('[data-choice="supply"], [data-photo-replace]').forEach(button => { button.disabled = uploadBusy; });
    q(".photo-file").hidden = !s.image;
    q("[data-photo-retained]").hidden =
      !a.retainedUpload || a.retainedUpload.hidden;
    if (s.image) {
      const image = q(".photo-file img");
      if (image.getAttribute("src") !== s.objectUrl)
        image.src = s.objectUrl || a.uploadedThumbnail.src;
      text(
        "[data-photo-filename]",
        s.cleanedOriginalFilename || s.file?.name || "Your photograph",
      );
      text(
        "[data-photo-validation]",
        a.uploadedState?.textContent || s.originalUploadStatus,
      );
    }
    q("[data-photo-validation]").dataset.success = String(
      s.originalUploadStatus === "verified",
    );
    if (s.image && s.originalUploadStatus === "verified" && !uploadBusy)
      text("[data-photo-validation]", "✓ Original safely uploaded");
    text(
      "[data-photo-upload-status]",
      s.photoPreviewPhase
        ? a.uploadStatus.textContent
        : s.image && s.originalUploadStatus === "verified"
        ? ""
        : s.imageMethod === "upload" && s.originalUploadStatus === "failed"
          ? a.uploadedState?.textContent ||
            "Upload failed — choose your photograph again to retry."
          : a.uploadStatus.textContent,
    );
    text("[data-photo-total]", a.totalPrice.textContent);
    const summary = [
      product,
      size?.label,
      s.productType === "canvas" ? label[s.canvasStyle]+(s.canvasStyle==='box'?' · '+({black:'Black',white:'White',darkwood:'Dark wood',barewood:'Bare wood'}[s.frameColour]||s.frameColour):'') : null,
      s.productType === "canvas" ? label[s.edgeStyle] : null,
      label[s.printColour],
      s.productType === "canvas" && s.hangingKit ? "Hanging kit" : null,
    ]
      .filter(Boolean)
      .join(" · ");
    text("[data-photo-summary]", summary);
    q("[data-photo-summary]").title = summary;
    const required =
      s.imageMethod !== "separate" && s.photoPreviewPhase && s.photoPreviewPhase !== "ready"
        ? (s.photoPreviewPhase === "failed" ? "Retry your photograph to continue" : "Wait for your photograph and preview to finish")
        : !s.image && s.imageMethod !== "separate"
        ? "Upload an image or choose Email to continue"
        : !size || !bridge.validSize() || a.totalPrice.textContent === "—"
          ? "Choose a valid size to continue"
          : s.imageMethod === "upload" && s.originalUploadStatus !== "verified"
            ? "Wait for your photograph to finish uploading"
            : "";
    q("[data-photo-review]").disabled = !!required || s.addingToCart;
    text("[data-photo-review]", `Review your ${s.productType}`);
    text("[data-photo-required]", required);
    text("[data-photo-basket-status]", a.cartStatus.textContent);
    q("[data-photo-basket-status]").setAttribute("role", a.cartStatus.classList.contains("is-error") ? "alert" : "status");
    q("[data-photo-basket]").setAttribute("aria-busy", String(s.addingToCart));
    q("[data-photo-basket]").disabled = s.addingToCart || !!required;
    text(
      "[data-photo-basket]",
      s.addingToCart
        ? (s.editingConfigurationId ? "Updating basket…" : "Adding to basket…")
        : s.editingConfigurationId
          ? "Update basket"
          : "Add to basket",
    );
    const header =
      document.querySelector("#shared-builder-header") || document.querySelector(".yi2c-builder-header") ||
      root.querySelector("header") ||
      root.querySelector(".yi2c-prototype-header");
    const top = Math.max(
      0,
      header?.getBoundingClientRect().bottom ||
        root.getBoundingClientRect().top ||
        64,
    );
    host.style.setProperty("--photo-header-bottom", `${top}px`);
    if (!modal.hidden && lastSummary !== summary) {
      lastSummary = summary;
      renderReview();
    }
  };
  const schedule = () => {
    if (!updateQueued) {
      updateQueued = true;
      requestAnimationFrame(update);
    }
  };
  host.addEventListener("click", (event) => {
    const size = event.target.closest("[data-photo-size]");
    if (size) {
      click(`[data-size-id="${size.dataset.photoSize}"]`);
      schedule();
      return;
    }
    const button = event.target.closest("[data-choice]");
    if (!button) return;
    const { choice, value } = button.dataset;
    const selectors = {
      product: `[data-product-type="${value}"]`,
      unit: `[data-unit="${value}"]`,
      orientation: `[data-orientation="${value}"]`,
      finish: `[data-style="${value}"]`,
      sides: `[data-edge="${value}"]`,
      treatment: `[data-print-colour="${value}"]`,
      frame: `[data-frame-colour="${value}"]`,
      position: `[data-position-choice="${value === "self" ? "myself" : "studio"}"]`,
    };
    if (choice === "supply") {
      if (value === "separate") a.sendLargeFileButton.click();
      else a.choosePhotoButton.click();
    } else if (choice === "position") {
      a.designerCropInput.checked = value === "studio";
      fire(a.designerCropInput);
    } else click(selectors[choice]);
    schedule();
  });
  const primeCustom = () => {
    if (!s.customMode) {
      set(a.customHeightInput, q("[data-photo-height]").value);
      set(a.customWidthInput, q("[data-photo-width]").value);
    }
  };
  q("[data-photo-width]").addEventListener("input", (event) => {
    primeCustom();
    set(a.customWidthInput, event.target.value);
    schedule();
  });
  q("[data-photo-height]").addEventListener("input", (event) => {
    primeCustom();
    set(a.customHeightInput, event.target.value);
    schedule();
  });
  q("[data-photo-comment]").addEventListener("input", (event) => {
    set(a.orderNotesInput, event.target.value);
    schedule();
  });
  q("[data-photo-kit]").addEventListener("change", (event) => {
    a.hangingKitInput.checked = event.target.checked;
    fire(a.hangingKitInput);
    schedule();
  });
  q("[data-photo-upload-retry]").onclick = () => a.uploadRetryButton?.click();
  q("[data-photo-replace]").onclick = () => a.changePhotoButton.click();
  q("[data-photo-retained]").onclick = () => {
    a.useRetainedUploadButton?.click();
    schedule();
  };
  function renderReview() {
    const size = bridge.size(),
      rows = [
        ["Product", s.productType === "poster" ? "Poster" : "Canvas"],
        [
          "Orientation",
          size.widthInches === size.heightInches
            ? "Square"
            : size.widthInches > size.heightInches
              ? "Landscape"
              : "Portrait",
        ],
        ["Size", s.productType === 'canvas' ? canvasReviewSize(s,size) : `${s.customMode ? "Custom · " : ""}${size?.label || ""}`],
        ...(s.productType === "canvas"
          ? [
              ["Canvas finish", s.canvasStyle === 'box' ? 'Floating frame · ' + ({black:'Black',white:'White',darkwood:'Dark wood',barewood:'Bare wood'}[s.frameColour] || 'Colour not recorded') : label[s.canvasStyle]],
              [
                "Sides",
                label[s.edgeStyle] +
                  (s.edgeStyle === "colour" ? ` ${s.edgeColour}` : ""),
              ],
              ["Hanging kit", s.hangingKit ? "Yes" : "No"],
            ]
          : []),
        ["Colour treatment", label[s.printColour]],
        [
          "Positioning",
          s.designerCrop ? "Let our studio decide" : "Position it yourself",
        ],
        ["Comment", s.orderNotes || "None"],
      ];
    const dl = q("[data-photo-review-summary]");
    dl.replaceChildren();
    for (const [key, value] of rows) {
      const row = document.createElement("div"),
        dt = document.createElement("dt"),
        dd = document.createElement("dd");
      dt.textContent = key;
      dd.textContent = value;
      row.append(dt, dd);
      dl.append(row);
    }
    text("#photo-review-title", `Review your ${s.productType}`);
    text(
      "[data-photo-review-caption-title]",
      s.productType === "poster"
        ? "Preview of your finished poster"
        : "Front of your finished canvas",
    );
    text("[data-photo-review-total]", a.totalPrice.textContent);
    text(
      "[data-photo-review-delivery]",
      q("[data-photo-delivery]").textContent,
    );
    const canvas = q("[data-photo-review-canvas]");
    canvas.hidden = s.imageMethod === "separate";
    q("[data-photo-review-email]").hidden = s.imageMethod !== "separate";
    renderReviewArtwork();
    q(".photo-review-artwork").style.aspectRatio = size
      ? String(bridge.reviewAspect())
      : "4/3";
  }
function canvasReviewSize(state,size) {
  if (!size) return '';
  const id=String(state.selectedStandardSizeId||'').toUpperCase();
  const a=!state.customMode&&/^A[0-5]$/.test(id),metric=a||(state.customMode&&state.customUnit==='cm');
  const number=n=>String(Math.round((n+Number.EPSILON)*100)/100);
  return (a?id+' · ':'')+number(size.widthInches*(metric?2.54:1))+' × '+number(size.heightInches*(metric?2.54:1))+(metric?' cm':' in');
}
  function renderReviewArtwork() {
    const canvas = q("[data-photo-review-canvas]");
    if (modal.hidden || canvas.hidden) return;
    const box = canvas.getBoundingClientRect(),
      size = bridge.size();
    const width = Math.min(
      box.width,
      box.height * bridge.reviewAspect(),
    );
    if (!width) return;
    const face = bridge.reviewFace(width);
    if (!face) return;
    canvas.width = face.width;
    canvas.height = face.height;
    canvas.getContext("2d").drawImage(face, 0, 0);
  }
  const reviewResize = new ResizeObserver(() => {
    if (!modal.hidden) renderReviewArtwork();
  });
  reviewResize.observe(q(".photo-review-artwork"));
  window.addEventListener("resize", renderReviewArtwork);
  const close = () => {
    modal.hidden = true;
    savedFocus?.focus({ preventScroll: true });
  };
  q("[data-photo-review]").onclick = () => {
    savedFocus = document.activeElement;
    modal.hidden = false;
    renderReview();
    requestAnimationFrame(renderReviewArtwork);
    q("[data-photo-edit]").focus({ preventScroll: true });
  };
  q("[data-photo-edit]").onclick = close;
  q("[data-photo-basket]").onclick = () => {
    a.addToCartButton.click();
    schedule();
  };
  modal.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
    if (event.key === "Tab") {
      const focusable = [
        ...modal.querySelectorAll("button:not(:disabled),a,input,textarea"),
      ];
      const i = focusable.indexOf(document.activeElement);
      if (event.shiftKey && i === 0) {
        event.preventDefault();
        focusable.at(-1).focus();
      } else if (!event.shiftKey && i === focusable.length - 1) {
        event.preventDefault();
        focusable[0].focus();
      }
    }
  });
  const observer = new MutationObserver((records) => {
    if (records.some((record) => !host.contains(record.target))) schedule();
  });
  observer.observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ["class", "hidden", "aria-pressed", "disabled", "src"],
  });
  root.addEventListener("input", schedule);
  root.addEventListener("change", schedule);
  window.addEventListener("resize", schedule);
  fetch("/apps/yi2c/delivery-estimate", {
    headers: { Accept: "application/json" },
  })
    .then((r) => (r.ok ? r.json() : Promise.reject()))
    .then((data) =>
      text(
        "[data-photo-delivery]",
        data.displayDate
          ? `Estimated delivery: ${data.displayDate}`
          : "Estimated delivery date shown in your basket",
      ),
    )
    .catch(() =>
      text(
        "[data-photo-delivery]",
        "Estimated delivery date shown in your basket",
      ),
    );
  mq.addEventListener(
    "change",
    () => {
      if (!mq.matches) {
        observer.disconnect();
        root.removeEventListener("input", schedule);
        root.removeEventListener("change", schedule);
        window.removeEventListener("resize", schedule);
        colourHome.replaceWith(a.edgeColourWrap);
        root.classList.remove("has-photo-desktop-panel");
        host.remove();
      }
    },
    { once: true },
  );
  update();
};

(() => {
  (() => {
    "use strict";
    const y = {
        pricingVersion: "2026-08-authoritative-1",
        customMultiplier: 1.3,
        boxFramePerimeterRate: 0.35,
        boxFrameFixed: 8,
        unframedMultiplier: 0.7,
        hangingKitPrice: 1.25,
        hangingKitPairPrice: 2.5,
        hangingKitPairThresholdInches: 40,
        slimGalleryAllowanceInches: 1,
        chunkyGalleryAllowanceInches: 2,
        minimumShortSideInches: 5,
        minimumLongSideInches: 5,
        maximumLongSideCm: 285,
        maximumShortSideInches: 55,
        oversizeThresholdCm: 140,
        outOfGaugeThresholdCm: 190,
        oversizeDeliveryPrice: 12,
        outOfGaugeDeliveryPrice: 30,
        standardMatchToleranceInches: 0.08,
        internalReviewPpi: 55,
        maximumPreviewPixelRatio: 2,
        minimumZoom: 1,
        maximumZoom: 4,
      },
      it = { a5: 5.99, a4: 8.99, a3: 11.99, a2: 15.99, a1: 25.99 },
      ve = {
        a5: [14.8 / 2.54, 21 / 2.54],
        a4: [21 / 2.54, 29.7 / 2.54],
        a3: [29.7 / 2.54, 42 / 2.54],
        a2: [42 / 2.54, 59.4 / 2.54],
        a1: [59.4 / 2.54, 84.1 / 2.54],
      },
      re = [
        {
          id: "8x10",
          group: "Rectangle",
          label: '8 \xD7 10"',
          widthInches: 8,
          heightInches: 10,
          price: 17.99,
        },
        {
          id: "12x16",
          group: "Rectangle",
          label: '12 \xD7 16"',
          widthInches: 12,
          heightInches: 16,
          price: 25.99,
        },
        {
          id: "16x20",
          group: "Rectangle",
          label: '16 \xD7 20"',
          widthInches: 16,
          heightInches: 20,
          price: 32.49,
        },
        {
          id: "25x30",
          group: "Rectangle",
          label: '25 \xD7 30"',
          widthInches: 25,
          heightInches: 30,
          price: 54.49,
        },
        {
          id: "30x40",
          group: "Rectangle",
          label: '30 \xD7 40"',
          widthInches: 30,
          heightInches: 40,
          price: 90.99,
        },
        {
          id: "8x8",
          group: "Square",
          label: '8 \xD7 8"',
          widthInches: 8,
          heightInches: 8,
          price: 17.49,
        },
        {
          id: "12x12",
          group: "Square",
          label: '12 \xD7 12"',
          widthInches: 12,
          heightInches: 12,
          price: 21.99,
        },
        {
          id: "16x16",
          group: "Square",
          label: '16 \xD7 16"',
          widthInches: 16,
          heightInches: 16,
          price: 31.99,
        },
        {
          id: "20x20",
          group: "Square",
          label: '20 \xD7 20"',
          widthInches: 20,
          heightInches: 20,
          price: 38.99,
        },
        {
          id: "24x24",
          group: "Square",
          label: '24 \xD7 24"',
          widthInches: 24,
          heightInches: 24,
          price: 49.99,
        },
        {
          id: "30x30",
          group: "Square",
          label: '30 \xD7 30"',
          widthInches: 30,
          heightInches: 30,
          price: 61.99,
        },
        {
          id: "33x33",
          group: "Square",
          label: '33 \xD7 33"',
          widthInches: 33,
          heightInches: 33,
          price: 69.99,
        },
        {
          id: "40x40",
          group: "Square",
          label: '40 \xD7 40"',
          widthInches: 40,
          heightInches: 40,
          price: 105.99,
        },
        {
          id: "9x16",
          group: "Panoramic",
          label: '9 \xD7 16"',
          widthInches: 9,
          heightInches: 16,
          price: 25.49,
        },
        {
          id: "10x24",
          group: "Panoramic",
          label: '10 \xD7 24"',
          widthInches: 10,
          heightInches: 24,
          price: 31.49,
        },
        {
          id: "12x30",
          group: "Panoramic",
          label: '12 \xD7 30"',
          widthInches: 12,
          heightInches: 30,
          price: 35.99,
        },
        {
          id: "20x40",
          group: "Panoramic",
          label: '20 \xD7 40"',
          widthInches: 20,
          heightInches: 40,
          price: 72.49,
        },
        {
          id: "a5",
          group: "A Sizes",
          label: "A5",
          metricLabel: "14.8 \xD7 21cm",
          widthInches: 14.8 / 2.54,
          heightInches: 21 / 2.54,
          price: 14.99,
        },
        {
          id: "a4",
          group: "A Sizes",
          label: "A4",
          metricLabel: "21 \xD7 29.7cm",
          widthInches: 21 / 2.54,
          heightInches: 29.7 / 2.54,
          price: 19.99,
        },
        {
          id: "a3",
          group: "A Sizes",
          label: "A3",
          metricLabel: "29.7 \xD7 42cm",
          widthInches: 29.7 / 2.54,
          heightInches: 42 / 2.54,
          price: 27.99,
        },
        {
          id: "a2",
          group: "A Sizes",
          label: "A2",
          metricLabel: "42 \xD7 59.4cm",
          widthInches: 42 / 2.54,
          heightInches: 59.4 / 2.54,
          price: 39.99,
        },
        {
          id: "a1",
          group: "A Sizes",
          label: "A1",
          metricLabel: "59.4 \xD7 84.1cm",
          widthInches: 59.4 / 2.54,
          heightInches: 84.1 / 2.54,
          price: 59.99,
        },
        {
          id: "a0",
          group: "A Sizes",
          label: "A0",
          metricLabel: "84.1 \xD7 118.9cm",
          widthInches: 84.1 / 2.54,
          heightInches: 118.9 / 2.54,
          price: 96.99,
        },
      ],
      Gt = ["12x12", "12x16", "16x20", "16x16", "25x30"],
      Le = {
        black: "#1d1d1d",
        white: "#f7f5ee",
        darkwood: "#4a3529",
        barewood: "#c3a57b",
      };
    function F(h, c, w) {
      return Math.min(w, Math.max(c, h));
    }
    function se(h) {
      return Math.round((Number(h) + Number.EPSILON) * 100) / 100;
    }
    function B(h) {
      const c = Number(h);
      return Number.isFinite(c)
        ? Number.isInteger(c)
          ? String(c)
          : String(Number(c.toFixed(1)))
        : "";
    }
    function de(h, c, w) {
      return Math.abs(h - c) <= w;
    }
    function Z(h, c) {
      return { short: Math.min(h, c), long: Math.max(h, c) };
    }
    function J(h, c = "GBP") {
      try {
        return new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: c || "GBP",
        }).format(Number(h || 0));
      } catch {
        return `\xA3${Number(h || 0).toFixed(2)}`;
      }
    }
    function qa() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      const h = new Uint8Array(16);
      (window.crypto.getRandomValues(h),
        (h[6] = (h[6] & 15) | 64),
        (h[8] = (h[8] & 63) | 128));
      const c = [...h].map((w) => w.toString(16).padStart(2, "0"));
      return `${c.slice(0, 4).join("")}-${c.slice(4, 6).join("")}-${c.slice(6, 8).join("")}-${c.slice(8, 10).join("")}-${c.slice(10).join("")}`;
    }
    function nt(h, c, privatePoster = false) {
      if (privatePoster) return window.YI2CPosterDesktop.quote(h, c);
      const w = Z(h, c);
      if (w.short < 5.8 || w.long < 8.25)
        return {
          valid: !1,
          message: 'Poster size must be at least 5.8" \xD7 8.25".',
        };
      if (w.short > 39 || w.long > 60)
        return { valid: !1, message: 'Maximum poster size is 39" \xD7 60".' };
      const a = Object.keys(ve).find((T) => {
        const A = Z(...ve[T]);
        return (
          de(w.short, A.short, y.standardMatchToleranceInches) &&
          de(w.long, A.long, y.standardMatchToleranceInches)
        );
      });
      let s,
        R = 0;
      if (a) s = it[a];
      else {
        const T = Object.keys(ve).map((e) => ({
            area: ve[e][0] * ve[e][1],
            price: it[e],
          })),
          A = h * c;
        let _ = T[0],
          O = T[1];
        for (let e = 1; e < T.length; e += 1) {
          if (A <= T[e].area) {
            ((_ = T[e - 1]), (O = T[e]));
            break;
          }
          ((_ = T[e - 1]), (O = T[e]));
        }
        const z =
          _.price * 100 +
          (A - _.area) * (((O.price - _.price) * 100) / (O.area - _.area));
        ((s = (Math.ceil((Math.round(z) + 1) / 50) * 50 - 1) / 100), (R = 2));
      }
      const W =
        w.long > 190 / 2.54
          ? y.outOfGaugeDeliveryPrice
          : w.long > 140 / 2.54
            ? y.oversizeDeliveryPrice
            : 0;
      return {
        valid: !0,
        basePrice: s,
        customHandlingPrice: R,
        deliveryPrice: W,
        matchingId: a,
      };
    }
    // One calculation implementation: both the creator and the Prices guide call this context.
    function builderPriceContext(e, a, k, he) {
      function pa(t, o) {
        const i = Z(t, o);
        return (
          re.find((n) => {
            const r = Z(n.widthInches, n.heightInches),
              d = de(i.short, r.short, y.standardMatchToleranceInches),
              l = de(i.long, r.long, y.standardMatchToleranceInches);
            return d && l;
          }) || null
        );
      }
      function bt() {
        ((e.customMatchedStandardSize = null),
          a.customMessage.classList.remove("is-error", "is-good"));
        const t = Number(e.customWidth),
          o = Number(e.customHeight);
        if (e.customWidth === "" || e.customHeight === "")
          return (
            (a.customMessage.textContent =
              "Enter both dimensions to see your price."),
            { valid: !1 }
          );
        const i = e.customUnit === "cm" ? 1 / 2.54 : 1,
          n = e.customUnit === "cm" ? t / 2.54 : t,
          r = e.customUnit === "cm" ? o / 2.54 : o,
          d = Z(n, r);
        // The custom Poster entry uses the same limits as its authoritative quote.
        if (e.productType === "poster") {
          const result = nt(n, r, e.posterDesktopV2);
          a.customMessage.textContent = result.valid
            ? "Made to your exact dimensions."
            : result.message;
          a.customMessage.classList.toggle("is-error", !result.valid);
          return { ...result, widthInches: n, heightInches: r };
        }
        if (
          d.short < y.minimumShortSideInches ||
          d.long < y.minimumLongSideInches
        )
          return (
            a.customMessage.classList.add("is-error"),
            (a.customMessage.textContent =
              "Custom canvases start from 5 × 5 inches."),
            { valid: !1, widthInches: n, heightInches: r }
          );
        if (d.short > y.maximumShortSideInches)
          return (
            a.customMessage.classList.add("is-error"),
            (a.customMessage.textContent = `Maximum printable width is ${y.maximumShortSideInches}".`),
            { valid: !1, widthInches: n, heightInches: r }
          );
        if (d.long * 2.54 > y.maximumLongSideCm)
          return (
            a.customMessage.classList.add("is-error"),
            (a.customMessage.textContent = `Maximum printable length is ${y.maximumLongSideCm}cm.`),
            { valid: !1, widthInches: n, heightInches: r }
          );
        const l = pa(n, r);
        return (
          l
            ? ((e.customMatchedStandardSize = l),
              a.customMessage.classList.add("is-good"),
              (a.customMessage.textContent = `Good news \u2014 ${l.label} is one of our standard sizes, so we\u2019ve applied the lower standard price.`))
            : (a.customMessage.textContent = "Made to your exact dimensions."),
          {
            valid: !0,
            widthInches: n,
            heightInches: r,
            matchingStandardSize: l,
          }
        );
      }
      function Be() {
        let t = k();
        if (e.productType === "poster") {
          if (e.customMode && !bt().valid) return (he(!1), !1);
          if (!t) return (he(!1), !1);
          const u = nt(t.widthInches, t.heightInches, e.posterDesktopV2);
          return u.valid
            ? ((e.basePrice = u.basePrice),
              (e.customHandlingPrice = u.customHandlingPrice),
              (e.boxFrameExtra = 0),
              (e.deliveryExtra = u.deliveryPrice),
              (e.totalPrice = se(
                e.basePrice + e.customHandlingPrice + e.deliveryExtra,
              )),
              he(!0),
              !0)
            : (a.customMessage &&
                ((a.customMessage.textContent = u.message),
                a.customMessage.classList.add("is-error")),
              he(!1),
              !1);
        }
        let o = 0;
        if (e.customMode) {
          const u = bt();
          if (!u.valid) return (he(!1), !1);
          if (
            ((t = {
              widthInches: u.widthInches,
              heightInches: u.heightInches,
              label: k().label,
            }),
            u.matchingStandardSize)
          )
            o = u.matchingStandardSize.price;
          else {
            const p = Z(u.widthInches, u.heightInches);
            o = se(p.long * y.customMultiplier + p.short);
          }
        } else {
          const u = re.find((p) => p.id === e.selectedStandardSizeId);
          if (!u || !t) return (he(!1), !1);
          o = u.price;
        }
        (e.canvasStyle === "unframed" || e.productType === "poster") &&
          (o = se(o * y.unframedMultiplier));
        let i = 0;
        if (e.productType === "canvas" && e.canvasStyle === "box") {
          const u = (t.widthInches + t.heightInches) * 2;
          i = se(u * y.boxFramePerimeterRate + y.boxFrameFixed);
        }
        const n = Math.max(t.widthInches, t.heightInches) * 2.54;
        let r = 0;
        n > y.outOfGaugeThresholdCm
          ? (r = y.outOfGaugeDeliveryPrice)
          : n > y.oversizeThresholdCm && (r = y.oversizeDeliveryPrice);
        const d =
            e.productType === "canvas" &&
            Math.max(t.widthInches, t.heightInches) >
              y.hangingKitPairThresholdInches,
          l =
            e.productType === "canvas" && e.hangingKit
              ? d
                ? y.hangingKitPairPrice
                : y.hangingKitPrice
              : 0;
        return (
          (e.basePrice = se(o)),
          (e.customHandlingPrice = 0),
          (e.boxFrameExtra = i),
          (e.deliveryExtra = r),
          (e.totalPrice = se(
            e.basePrice + e.boxFrameExtra + e.deliveryExtra + l,
          )),
          he(!0),
          !0
        );
      }

      return { match: pa, validate: bt, calculate: Be };
    }
    function priceGuideQuote(input) {
      const productType = input.product === "poster" ? "poster" : input.product === "canvas" ? "canvas" : null;
      const unit = productType === "poster" ? "cm" : input.unit;
      if (!productType || !["in","cm"].includes(unit)) return { valid:false, message:"Choose a product and unit." };
      const width = String(input.width ?? ""), height = String(input.height ?? "");
      if (![Number(width),Number(height)].every(n=>Number.isFinite(n)&&n>0)) return {valid:false,message:"Enter a positive width and height."};
      const style = productType === "poster" ? "slim" : input.style || "slim";
      if (!["slim","chunky","box","unframed"].includes(style)) return {valid:false,message:"Choose a canvas finish."};
      const state = {productType,customMode:true,customUnit:unit,customWidth:width,customHeight:height,
        canvasStyle:style,hangingKit:productType==="canvas"&&input.kit===true,posterDesktopV2:productType==="poster"};
      const message = document.createElement("span");
      const size = {widthInches:Number(width)/(unit==="cm"?2.54:1),heightInches:Number(height)/(unit==="cm"?2.54:1),label:width+" × "+height+" "+unit};
      const context = builderPriceContext(state,{customMessage:message},()=>size,()=>{});
      const valid = context.calculate();
      return {...state,...size,valid,message:message.textContent,matchingId:state.customMatchedStandardSize?.id || (productType==="poster"?window.YI2CPosterDesktop.match(size.widthInches,size.heightInches)?.id:null)};
    }
    window.YI2CPriceGuide = Object.freeze({quote:priceGuideQuote});
    function Yt(h) {
      if (!h || h.dataset.yi2cReady === "true") return;
      h.dataset.yi2cReady = "true";
      const c = (t) => h.querySelector(t),
        w = (t) => [...h.querySelectorAll(t)],
        a = {
          fileInput: c("[data-file-input]"),
          uploadScreen: c("[data-upload-screen]"),
          uploadStatus: c("[data-upload-status]"),
          uploadProgress: c("[data-upload-progress]"),
          uploadProgressTrack: c("[data-upload-progress] [role='progressbar']"),
          uploadProgressBar: c("[data-upload-progress-bar]"),
          uploadProgressCopy: c("[data-upload-progress-copy]"),
          uploadedFile: c("[data-uploaded-file]"),
          uploadedThumbnail: c("[data-uploaded-thumbnail]"),
          uploadedFilename: c("[data-uploaded-filename]"),
          uploadedState: c("[data-uploaded-state]"),
          retainedUpload: c("[data-retained-upload]"),
          useRetainedUploadButton: c("[data-use-retained-upload]"),
          choosePhotoButton: c("[data-choose-photo]"),
          sendLargeFileButton: c("[data-send-large-file]"),
          editor: c("[data-editor]"),
          changePhotoButton: c("[data-change-photo]"),
          switchToUploadButton: c("[data-switch-to-upload]"),
          imageMethodStatus: c("[data-image-method-status]"),
          photoPlaceholder: c("[data-photo-placeholder]"),
          placeholderArtwork: c("[data-photo-placeholder-artwork]"),
          designerOverlay: c("[data-designer-overlay]"),
          previewCanvas: c("[data-preview-canvas]"),
          photoTools: c("[data-photo-tools]"),
          previewTip: c("[data-preview-tip]"),
          orientationControl: c("[data-orientation-control]"),
          orientationButtons: w("[data-orientation]"),
          squareOrientation: c("[data-square-orientation]"),
          widthMinusButton: c("[data-width-minus]"),
          widthPlusButton: c("[data-width-plus]"),
          widthValueButton: c("[data-width-value]"),
          heightMinusButton: c("[data-height-minus]"),
          heightPlusButton: c("[data-height-plus]"),
          heightValueButton: c("[data-height-value]"),
          trustText: c("[data-trust-text]"),
          zoomSlider: c("[data-zoom]"),
          zoomInButton: c("[data-zoom-in]"),
          zoomOutButton: c("[data-zoom-out]"),
          rotateButton: c("[data-rotate]"),
          resetCropButton: c("[data-reset-crop]"),
          popularSizes: c("[data-popular-sizes]"),
          allSizes: c("[data-all-sizes]"),
          toggleAllSizesButton: c("[data-toggle-all-sizes]"),
          toggleCustomSizeButton: c("[data-toggle-custom-size]"),
          customPanel: c("[data-custom-panel]"),
          customWidthInput: c("[data-custom-width]"),
          customHeightInput: c("[data-custom-height]"),
          customUnitLabels: w("[data-custom-unit-label]"),
          customMessage: c("[data-custom-message]"),
          sizeSummary: c("[data-size-summary]"),
          frameColours: c("[data-frame-colours]"),
          edgeSection: c("[data-edge-section]"),
          edgeColourWrap: c("[data-edge-colour-wrap]"),
          edgeColourInput: c("[data-edge-colour]"),
          edgeColourHexInput: c("[data-edge-colour-hex]"),
          edgeColourHueInput: c("[data-edge-colour-hue]"),
          edgeColourSwatch: c("[data-edge-colour-swatch]"),
          edgeColourResetButton: c("[data-edge-colour-reset]"),
          edgeColourPickButton: c("[data-edge-colour-pick]"),
          edgeColourCancelButton: c("[data-edge-colour-cancel]"),
          edgeColourPickStatus: c("[data-edge-colour-pick-status]"),
          hangingKitInput: c("[data-hanging-kit]"),
          hangingKitRow:
            c("[data-hanging-kit]")?.closest(".yi2c-extra-row") || null,
          hangingKitLabel: c("[data-hanging-kit-label]"),
          hangingKitPriceLabel: c("[data-hanging-kit-price]"),
          hangingKitRecommendation: c("[data-hanging-kit-recommendation]"),
          designerCropInput: c("[data-designer-crop]"),
          positionChoiceButtons: w("[data-position-choice]"),
          positionMyselfButton: c("[data-position-myself]"),
          orderNotesInput: c("[data-order-notes]"),
          totalPrice: c("[data-total-price]"),
          priceNote: c("[data-price-note]"),
          orderSummary: c("[data-order-summary]"),
          addToCartButton: c("[data-add-to-cart]"),
          mobileAddButton: c("[data-mobile-add]"),
          cartStatus: c("[data-cart-status]"),
          editLoading: c("[data-edit-loading]"),
          editLoadingTitle: c("[data-edit-loading-title]"),
          editLoadingCopy: c("[data-edit-loading-copy]"),
          editRetryButton: c("[data-edit-retry]"),
          mobileSize: c("[data-mobile-size]"),
          mobilePrice: c("[data-mobile-price]"),
          productTypeButtons: w("[data-product-type]"),
          posterNote: c("[data-poster-note]"),
          priceProductLabel: c("[data-price-product-label]"),
          builderSteps: w("[data-builder-step]"),
          mobileProductTrigger: c("[data-mobile-product-trigger]"),
          mobileProductLabel: c("[data-mobile-product-label]"),
          mobileSheetTriggers: w("[data-mobile-sheet-trigger]"),
          mobileBoxValues: w("[data-mobile-box-value]"),
          mobileAdjustButton: c("[data-mobile-adjust]"),
          mobileAdjustDoneButton: c("[data-mobile-adjust-done]"),
          mobileHangingButton: c("[data-mobile-hanging]"),
          mobileCommentButton: c("[data-mobile-comment]"),
          mobileSheetBackdrop: c("[data-mobile-sheet-close]"),
        };
      if (!a.previewCanvas) {
        console.warn("YI2C Builder: preview canvas was not found.");
        return;
      }
      const s = a.previewCanvas.getContext("2d"),
        R = document.createElement("div");
      ((R.className = "yi2c-mobile-dimensions"),
        R.setAttribute("aria-hidden", "true"));
      const W = document.createElement("span");
      W.className = "yi2c-mobile-dimension yi2c-mobile-dimension--width";
      const T = document.createElement("span");
      ((T.className = "yi2c-mobile-dimension yi2c-mobile-dimension--height"),
        R.append(W, T),
        a.previewCanvas.closest(".yi2c-preview-shell")?.append(R));
      function A() {
        try {
          const t = document.createElement("canvas"),
            o = document.createElement("canvas");
          ((t.width = o.width = 1), (t.height = o.height = 1));
          const i = t.getContext("2d"),
            n = o.getContext("2d");
          ((i.fillStyle = "#ff0000"),
            i.fillRect(0, 0, 1, 1),
            (n.filter = "grayscale(1)"),
            n.drawImage(t, 0, 0));
          const r = n.getImageData(0, 0, 1, 1).data;
          return Math.abs(r[0] - r[1]) <= 2 && Math.abs(r[1] - r[2]) <= 2;
        } catch {
          return !1;
        }
      }
      function _(t) {
        return /AppleWebKit/i.test(t) && /iPhone|iPad|iPod|Macintosh/i.test(t);
      }
      const O = !_(navigator.userAgent || "") && A();
      let z = { sourceImage: null, printColour: "", canvas: null };
      // Approved A-size offers initialise state only; all pricing and commerce stay unchanged.
      // Basket edit links retain precedence. Unknown offers preserve the ordinary builder.
      const offerQuery = new URLSearchParams(window.location.search);
      const offerId = offerQuery.has("yi2c_edit")
        ? null
        : offerQuery.get("yi2c_offer");
      const offerPresets = {
        "canvas-a5": ["canvas", "a5"],
        "canvas-a4": ["canvas", "a4"],
        "canvas-a3": ["canvas", "a3"],
        "canvas-a2": ["canvas", "a2"],
        "canvas-a1": ["canvas", "a1"],
        "poster-a5": ["poster", "a5"],
        "poster-a4": ["poster", "a4"],
        "poster-a3": ["poster", "a3"],
        "poster-a2": ["poster", "a2"],
        "poster-a1": ["poster", "a1"],
        "poster-custom": ["poster", null],
      };
      const offerPreset = Object.prototype.hasOwnProperty.call(
        offerPresets,
        offerId,
      )
        ? offerPresets[offerId]
        : null;
      // Private product-specific journey: existing offer state selects the entry
      // product; authenticated Basket Edit/handoff restoration takes precedence.
      // Explicit Prices route only. Existing order/edit and Express recovery always win.
      const priceEntryRequested = offerQuery.get("yi2c_from")==="prices-v1" &&
        !offerQuery.has("yi2c_edit") && !offerQuery.has("yi2c_express_draft");
      const requestedQuote = priceEntryRequested ? priceGuideQuote({
        product:offerQuery.get("print"),unit:offerQuery.get("unit"),width:offerQuery.get("width"),
        height:offerQuery.get("height"),style:offerQuery.get("style"),kit:offerQuery.get("kit")==="1"
      }) : null;
      const priceEntry = requestedQuote?.valid ? requestedQuote : null;
      let lockedProductType = priceEntry ? priceEntry.productType : offerPreset ? offerPreset[0] : "canvas";
      const e = {
          configurationId: "",
          recoveryToken: "",
          cropReferenceUrl: "",
          editingConfigurationId: "",
          addingToCart: !1,
          uploadPromise: null,
          originalUploadStatus: "not-started",
          originalVerifiedConfigurationId: "",
          activeUploadRequests: new Set(),
          uploadGeneration: 0,
          file: null,
          cleanedOriginalFilename: "",
          image: null,
          productType: lockedProductType,
          posterDesktopV2: lockedProductType === "poster" && h.hasAttribute("data-poster-desktop-private") && (window.matchMedia("(min-width:900px)").matches || !!window.__yi2cMountPosterMobile),
          previousCanvasStyle: "slim",
          previousEdgeStyle: "gallery",
          desktopStep: "photo",
          stepTouched: { size: !1, canvas: !1, sides: !1, photo: !1 },
          imageMethod: "upload",
          objectUrl: "",
          naturalWidth: 0,
          naturalHeight: 0,
          originalNaturalWidth: 0,
          originalNaturalHeight: 0,
          selectedStandardSizeId: offerPreset ? offerPreset[1] : "12x16",
          orientation: priceEntry ? (priceEntry.widthInches > priceEntry.heightInches ? "landscape" : "portrait") : offerPreset ? "portrait" : "landscape",
          customMode: !!priceEntry || offerId === "poster-custom",
          customUnit: priceEntry?.customUnit || "in",
          customWidth: priceEntry?.customWidth || "",
          customHeight: priceEntry?.customHeight || "",
          customMatchedStandardSize: null,
          canvasStyle: priceEntry?.canvasStyle || "slim",
          edgeStyle: "gallery",
          edgeColour: "#000000",
          samplingEdgeColour: !1,
          frameColour: "black",
          printColour: "original",
          hangingKit: priceEntry?.hangingKit || false,
          designerCrop: !1,
          orderNotes: "",
          zoom: 1,
          panX: 0,
          panY: 0,
          rotation: 0,
          retainedUploadState: null,
          basePrice: 0,
          customHandlingPrice: 0,
          boxFrameExtra: 0,
          deliveryExtra: 0,
          totalPrice: 0,
          estimatedPpi: 0,
          studioReviewRecommended: !1,
          galleryMissingSides: [],
          geometry: null,
          pointers: new Map(),
          dragStart: null,
          pinchStart: null,
        },
        Q = `yi2c-builder-recovery-v1:${h.dataset.productId || "product"}${priceEntry ? ":prices-entry" : offerPreset ? `:offer:${offerId}` : ""}`,
        ke = "/apps/yi2c";
      function ee() {
        return (
          new URLSearchParams(window.location.search).get("yi2c_edit") || ""
        );
      }
      function ce() {
        (h.classList.remove("is-edit-hydrating"),
          a.editLoading && (a.editLoading.hidden = !0));
      }
      function Se(t) {
        (h.classList.add("is-edit-hydrating"),
          a.editLoading &&
            ((a.editLoading.hidden = !1),
            a.editLoading.classList.add("is-error"),
            (a.editLoadingTitle.textContent =
              "We couldn\u2019t restore this print"),
            (a.editLoadingCopy.textContent = be(
              t,
              "Please try again. Your basket item has not been changed.",
            )),
            (a.editRetryButton.hidden = !1)));
      }
      function N() {
        clearOriginalVerification();
        (localStorage.removeItem(Q),
          (e.configurationId = ""),
          (e.recoveryToken = ""),
          (e.cropReferenceUrl = ""),
          (e.editingConfigurationId = ""),
          (e.zoom = 1),
          (e.panX = 0),
          (e.panY = 0),
          (e.rotation = 0),
          (a.zoomSlider.value = "1"),
          (a.addToCartButton.textContent = "Add to basket"),
          (a.mobileAddButton.textContent = "Add to basket"));
      }
      function le() {
        const t = k(),
          o = ["a5", "a4", "a3", "a2", "a1"],
          i = {
            productType: e.productType,
            ...(e.posterDesktopV2 && e.productType==='poster'?{pricingVersion:window.YI2CPosterDesktop.pricingVersion}:{}),
            widthInches: Number(t.widthInches.toFixed(4)),
            heightInches: Number(t.heightInches.toFixed(4)),
            standardSizeId:
              e.productType === "poster"
                ? !e.customMode && (e.posterDesktopV2 ? !!window.YI2CPosterDesktop.find(e.selectedStandardSizeId) : o.includes(e.selectedStandardSizeId))
                  ? e.selectedStandardSizeId
                  : void 0
                : e.customMode
                  ? void 0
                  : e.selectedStandardSizeId,
            imageMethod: e.imageMethod === "separate" ? "SEPARATE" : "UPLOAD",
            edgeStyle: e.edgeStyle === "black" ? "colour" : e.edgeStyle,
            edgeColour: e.edgeStyle === "black" ? "#000000" : e.edgeColour,
            frameColour: e.frameColour,
            printColour: e.printColour,
            designerCrop: e.designerCrop,
            customerNotes: e.orderNotes,
            estimatedPpi:
              e.estimatedPpi > 0 ? Math.round(e.estimatedPpi) : void 0,
          };
        return (
          e.productType === "canvas" &&
            ((i.style = e.canvasStyle), (i.hangingKit = e.hangingKit)),
          i
        );
      }
      function $(t, o = 0, i = "request") {
        const n = new Error(t || "UPLOAD_SERVICE_FAILED");
        return ((n.httpStatus = Number(o) || 0), (n.uploadStage = i), n);
      }
      async function U(t, o) {
        const i = {
          ...o,
          ...(e.posterDesktopV2 && e.productType==='poster' && !h.hasAttribute('data-poster-pricing-public')?{_posterPrivateToken:sessionStorage.getItem('yi2c-private-poster-session')||''}:{}),
          _singlePhotoPreviewKey:
            sessionStorage.getItem("yi2c-single-photo-preview-key") || "",
          _requestId: crypto.randomUUID(),
          _requestedAt: new Date().toISOString(),
        };
        let n;
        try {
          n = await fetch(`${ke}/${t}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(i),
          });
        } catch {
          throw $("UPLOAD_CONNECTION_FAILED", 0, t);
        }
        const r = await n.json().catch(() => ({}));
        if (!n.ok) throw $(r.error || "UPLOAD_SERVICE_FAILED", n.status, t);
        if(r.originalArtworkUrl)e.originalArtworkUrl=r.originalArtworkUrl;
        return r;
      }
      const V = 600 * 1e3,
        te = 30 * 1e3;
      let b = "",
        E = null;
      function ue(t) {
        const o = Number(String(t || "").split(".", 1)[0]);
        return Number.isSafeInteger(o) ? o : 0;
      }
      function Te(t, o = Date.now()) {
        return ue(t) > o + te;
      }
      function Pe(t = Date.now()) {
        return Te(b, t) ? !0 : ((b = ""), !1);
      }
      function Me(t, o = Date.now()) {
        const i = ue(t);
        return i ? Math.max(0, o - (i - V)) : null;
      }
      function ae(t) {
        return t instanceof Error ? t.message : "UPLOAD_SERVICE_FAILED";
      }
      function dt(t, o, i, n, r = b) {
        console.warn(
          "[YI2C upload] " +
            JSON.stringify({
              correlationId: t,
              stage: o,
              attempt: i,
              credentialAgeMs: Me(r),
              httpStatus: Number(n?.httpStatus) || 0,
              code: ae(n),
            }),
        );
      }
      function Zt(t) {
        const o = ae(t);
        return o === "UPLOAD_NOT_ACTIVE" ||
          /^UPLOAD_PART_FAILED_(400|401|403)$/.test(o)
          ? !0
          : o === "UPLOAD_CONNECTION_FAILED" &&
              t?.uploadStage !== "uploads/complete";
      }
      async function ct(t = !1) {
        if ((t && (b = ""), Pe())) return b;
        if (E) return E;
        const o = h.dataset.yi2cUploadVerificationUrl;
        if (!o) throw new Error("UPLOAD_VERIFICATION_REQUIRED");
        const i = window.AwsWafIntegration?.fetch?.bind(
          window.AwsWafIntegration,
        );
        return (
          (E = (async () => {
            const n = await (i || window.fetch.bind(window))(o, {
                method: "POST",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: "{}",
              }),
              r = await n.json().catch(() => ({}));
            if (!n.ok || typeof r.token != "string")
              throw new Error("UPLOAD_VERIFICATION_REQUIRED");
            return ((b = r.token), b);
          })()
            .catch(() => {
              throw ((b = ""), new Error("UPLOAD_VERIFICATION_REQUIRED"));
            })
            .finally(() => {
              E = null;
            })),
          E
        );
      }
      async function lt(t, o) {
        if (
          window.matchMedia("(min-width: 900px)").matches &&
          e.editingConfigurationId === e.configurationId &&
          e.originalUploadStatus === "verified" &&
          o.kind === "CROP_REFERENCE"
        ) {
          const recovered = await U("configurations/recover", {
            configurationId: e.configurationId,
            recoveryToken: e.recoveryToken,
            verifyExistingUpload: true,
          });
          if (typeof recovered.existingUploadVerificationToken !== "string")
            throw new Error("UPLOAD_VERIFICATION_REQUIRED");
          return U(t, {
            ...o,
            existingUploadVerificationToken:
              recovered.existingUploadVerificationToken,
          });
        }
        let i = await ct();
        try {
          return await U(t, { ...o, uploadVerificationToken: i });
        } catch (n) {
          if (ae(n) !== "UPLOAD_VERIFICATION_REQUIRED") throw n;
          return (
            (b = ""),
            (i = await ct(!0)),
            U(t, { ...o, uploadVerificationToken: i })
          );
        }
      }
      function ut() {
        ((a.editor.hidden = !window.matchMedia("(min-width: 900px)").matches),
          (a.uploadScreen.hidden = !1),
          (a.imageMethodStatus.hidden = !0));
      }
      function photoUploadPhase(phase, message) {
        e.photoPreviewPhase = phase;
        const busy = phase === "uploading" || phase === "preparing";
        h.dataset.photoUploadPhase = phase;
        a.uploadStatus.textContent = message;
        a.uploadStatus.setAttribute("role", "status");
        for (const button of [a.choosePhotoButton, a.sendLargeFileButton, a.changePhotoButton]) if (button) button.disabled = busy;
        if (!a.uploadRetryButton) {
          const button = document.createElement("button");
          button.type = "button"; button.className = "yi2c-text-btn";
          button.textContent = "Retry photograph"; button.dataset.uploadRetry = "";
          button.addEventListener("click", () => { if (e.file && e.photoPreviewPhase === "failed") Ft(e.file); });
          a.uploadStatus.after(button); a.uploadRetryButton = button;
        }
        a.uploadRetryButton.hidden = phase !== "failed";
        if (phase === "preparing") Fe(null, message);
        else if (!busy && a.uploadProgress) a.uploadProgress.hidden = true;
        if (e.file) Ae(e.file, message);
        if (busy || phase === "failed") {
          a.addToCartButton.disabled = true;
          a.mobileAddButton.disabled = true;
        }
      }
      function Fe(t = null, message = "Uploading your photograph…") {
        if (!a.uploadProgress) return;
        if (t === null) {
          ((a.uploadProgress.hidden = !1),
            a.uploadProgress.classList.add("is-indeterminate"),
            a.uploadProgressTrack?.removeAttribute("aria-valuenow"),
            (a.uploadProgressCopy.textContent =
              message),
            (a.uploadStatus.textContent = message));
          return;
        }
        const o = F(Math.round(Number(t) || 0), 0, 100);
        ((a.uploadProgress.hidden = !1),
          a.uploadProgress.classList.remove("is-indeterminate"),
          a.uploadProgressTrack?.setAttribute("aria-valuenow", String(o)),
          a.uploadProgressBar && (a.uploadProgressBar.style.width = `${o}%`),
          (a.uploadProgressCopy.textContent = `Uploading your photograph\u2026 ${o}%`),
          (a.uploadStatus.textContent = a.uploadProgressCopy.textContent));
      }
      function Ae(t, o = "") {
        a.uploadedFile &&
          ((a.uploadedFile.hidden = !t),
          a.uploadedFilename &&
            (a.uploadedFilename.textContent = t?.name || ""),
          a.uploadedState && (a.uploadedState.textContent = o));
      }
      function _e(t = e.desktopStep) {
        if (!window.matchMedia("(min-width: 900px)").matches) {
          // The inline mobile form can follow a desktop viewport without
          // inheriting that presentation's pre-upload disabled controls.
          if (h.classList.contains("yi2c-mobile-inline")) {
            const ready = e.imageMethod === "separate" || e.originalUploadStatus === "verified";
            w("[data-print-colour]").forEach((control) => { control.disabled = !ready; });
            a.hangingKitInput.disabled = !ready;
            a.orderNotesInput.disabled = !ready;
          }
          return;
        }
        const o =
            e.imageMethod === "separate" ||
            e.originalUploadStatus === "verified",
          n = e.productType === "canvas",
          i = n && e.canvasStyle !== "box" && e.canvasStyle !== "unframed";
        (!o && t !== "photo" && (t = "photo"),
          !n && (t === "style" || t === "sides") && (t = "size"),
          t === "sides" && !i && (t = "style"),
          (e.desktopStep = t),
          (h.dataset.desktopStep = t),
          h.classList.toggle("is-desktop-ready", o),
          a.productTypeButtons.forEach((u) => {
            u.disabled = !o;
          }),
          w("[data-print-colour]").forEach((u) => {
            u.disabled = !o;
          }),
          a.hangingKitInput && (a.hangingKitInput.disabled = !o),
          a.orderNotesInput && (a.orderNotesInput.disabled = !o),
          w("[data-desktop-step]").forEach((u) => {
            const p = u.dataset.desktopStep,
              m = p === "photo" || o,
              S = !m || (p === "style" && !n) || (p === "sides" && !i);
            ((u.disabled = S),
              u.setAttribute("aria-disabled", String(S)),
              u.classList.toggle("is-active", p === t),
              u.classList.toggle(
                "is-complete",
                o &&
                  p !== t &&
                  (p === "photo" ||
                    e.stepTouched[p === "style" ? "canvas" : p]),
              ),
              (u.hidden = (p === "style" && !n) || (p === "sides" && !i)));
          }));
        const s = c("[data-desktop-lock-message]");
        s && (s.hidden = o);
        const r = a.uploadScreen?.querySelector(
          '[data-desktop-continue="size"]',
        );
        r && (r.hidden = !o);
        const d = c('[data-desktop-continue="sides"]'),
          styleComplete = c("[data-style-complete]"),
          sizeContinue = c(
            '[data-builder-step="size"] [data-desktop-continue]',
          ),
          l = a.orderNotesInput?.closest("details");
        (l &&
          l.dataset.desktopNoteInitialised !== "true" &&
          ((l.open = !!e.orderNotes.trim()),
          (l.dataset.desktopNoteInitialised = "true")),
          l && e.orderNotes.trim() && (l.open = !0),
          sizeContinue &&
            ((sizeContinue.dataset.desktopContinue = n ? "style" : "review"),
            (sizeContinue.textContent = n
              ? "Continue to style"
              : "Continue to review")),
          d && ((d.hidden = !i), (d.textContent = "Continue to sides")),
          styleComplete && (styleComplete.hidden = !n || i),
          w("[data-builder-step]").forEach((u) => {
            const p = u.dataset.builderStep,
              m = p === "canvas" ? "style" : p;
            u.classList.toggle("is-desktop-active", m === t || p === "photo");
          }));
      }
      function Jt() {
        (ut(), a.uploadStatus.replaceChildren());
        if (window.location.hostname.endsWith("shopifypreview.com")) {
          a.uploadStatus.textContent =
            "Photo uploads can be tested from the authenticated theme preview.";
          return;
        }
        const t = new URL(h.dataset.yi2cUploadVerificationUrl);
        t.searchParams.set("origin", window.location.origin);
        const o = window.open(
          t,
          "yi2c-upload-verification",
          "popup,width=520,height=620",
        );
        if (o) {
          a.uploadStatus.textContent =
            "Complete the secure browser check to continue your upload.";
          return;
        }
        const i = document.createElement("span"),
          n = document.createElement("a");
        ((i.textContent = "Your browser blocked the secure check. "),
          (n.href = t.toString()),
          (n.target = "yi2c-upload-verification"),
          (n.className = "yi2c-text-btn"),
          (n.textContent = "Open the secure browser check"),
          a.uploadStatus.append(i, n));
      }
      window.addEventListener("message", (t) => {
        const o = h.dataset.yi2cUploadVerificationUrl;
        if (
          !o ||
          t.origin !== new URL(o).origin ||
          t.data?.type !== "yi2c-upload-verified" ||
          typeof t.data.token != "string"
        )
          return;
        b = t.data.token;
        const i = e.file;
        if (i && e.originalUploadStatus === "failed") {
          ((a.uploadStatus.textContent =
            "Direct upload verified \u2014 retrying your photo\u2026"),
            Ft(i));
          return;
        }
        a.uploadStatus.textContent = "Direct upload verified.";
      });
      function ht(t, o) {
        (ut(), a.uploadStatus.replaceChildren());
        const i = document.createElement("strong");
        i.textContent = t;
        const n = document.createElement("span");
        n.textContent = o;
        const r = document.createElement("button");
        ((r.type = "button"),
          (r.className = "yi2c-text-btn"),
          (r.textContent = "Send a large file"),
          r.addEventListener("click", Re, { once: !0 }),
          a.uploadStatus.append(
            i,
            document.createElement("br"),
            n,
            document.createElement("br"),
            r,
          ));
      }
      function be(t, o) {
        const i = t instanceof Error ? t.message : "",
          n = {
            UPLOAD_ORIGINAL_REQUIRED:
              "Your original photograph is not attached to this print. Please choose your photograph again and wait for the upload to finish before adding to basket. Your size and options are unchanged.",
            CONFIGURATION_NOT_EDITABLE:
              "This saved print has already been completed. Please choose your photo again to start a new print.",
            CONFIGURATION_NOT_FOUND_OR_EXPIRED:
              "This saved print is no longer available. Please choose your photo again.",
            UPLOAD_CANCELLED: "The upload was cancelled.",
            UPLOAD_CONNECTION_FAILED:
              "The upload connection was interrupted. Please try again.",
          };
        return n[i] ? n[i] : /^[A-Z][A-Z0-9_]+$/.test(i) ? o : i || o;
      }
      function clearOriginalVerification() {
        e.originalUploadStatus = "not-started";
        e.originalVerifiedConfigurationId = "";
        e.originalArtworkUrl = "";
      }
      function hasVerifiedOriginal() {
        return !!e.configurationId && e.originalUploadStatus === "verified" && e.originalVerifiedConfigurationId === e.configurationId;
      }
      async function requireOriginalForBasket() {
        if (e.imageMethod === "separate") return;
        if (!hasVerifiedOriginal()) throw new Error("UPLOAD_ORIGINAL_REQUIRED");
        const id = e.configurationId;
        try {
          const result = await U("configurations/basket-ready", {configurationId: id, recoveryToken: e.recoveryToken});
          if (!result.ready || result.imageMethod !== "UPLOAD" || result.configurationId !== id || e.configurationId !== id || !hasVerifiedOriginal()) throw new Error("UPLOAD_ORIGINAL_REQUIRED");
        } catch (error) {
          if (error?.message === "UPLOAD_ORIGINAL_REQUIRED") clearOriginalVerification();
          throw error;
        }
      }
      function gt() {
        clearOriginalVerification();
        (localStorage.removeItem(Q),
          (e.configurationId = ""),
          (e.recoveryToken = ""),
          (e.cropReferenceUrl = ""));
      }
      function Qt() {
        !e.configurationId ||
          !e.recoveryToken ||
          localStorage.setItem(
            Q,
            JSON.stringify({
              configurationId: e.configurationId,
              recoveryToken: e.recoveryToken,
              expiresAt: Date.now() + 1440 * 60 * 1e3,
            }),
          );
      }
      async function pt() {
        if(e.posterDesktopV2 && e.productType==='poster' && !h.hasAttribute('data-poster-pricing-public') && !sessionStorage.getItem('yi2c-private-poster-session'))throw new Error('PRIVATE_POSTER_SESSION_REQUIRED');
        if (!e.configurationId || !e.recoveryToken) {
          clearOriginalVerification();
          const t = await U("configurations", le());
          return (
            (e.configurationId = t.configurationId),
            (e.recoveryToken = t.recoveryToken),
            (e.cropReferenceUrl = t.cropReferenceUrl || ""),
            Qt(),
            t
          );
        }
        return U("configurations/update", {
          configurationId: e.configurationId,
          recoveryToken: e.recoveryToken,
          ...le(),
        });
      }
      async function ea(t) {
        const o = await crypto.subtle.digest("SHA-256", await t.arrayBuffer());
        let i = "";
        return (
          new Uint8Array(o).forEach((n) => {
            i += String.fromCharCode(n);
          }),
          btoa(i)
        );
      }
      function ta(t, o, i, n) {
        return new Promise((r, d) => {
          const l = new XMLHttpRequest();
          (e.activeUploadRequests.add(l),
            l.open("PUT", t),
            l.setRequestHeader("x-amz-checksum-sha256", i),
            (l.upload.onprogress = n),
            (l.onload = () => {
              (e.activeUploadRequests.delete(l),
                l.status >= 200 && l.status < 300
                  ? r(l.getResponseHeader("ETag"))
                  : d(
                      $(`UPLOAD_PART_FAILED_${l.status}`, l.status, "s3-part"),
                    ));
            }),
            (l.onerror = () => {
              (e.activeUploadRequests.delete(l),
                d($("UPLOAD_CONNECTION_FAILED", 0, "s3-part")));
            }),
            (l.onabort = () => {
              (e.activeUploadRequests.delete(l),
                d(new Error("UPLOAD_CANCELLED")));
            }),
            l.send(o));
        });
      }
      async function aa(t, o, i, n, r) {
        if ((await pt(), o === "ORIGINAL" && i !== e.uploadGeneration))
          throw new Error("UPLOAD_CANCELLED");
        const d = {
            configurationId: e.configurationId,
            recoveryToken: e.recoveryToken,
            kind: o,
          },
          l = await lt("uploads/initiate", {
            ...d,
            filename:
              t.name || (o === "ORIGINAL" ? "original" : "crop-reference.png"),
            claimedContentType: t.type || "application/octet-stream",
            byteSize: t.size,
          }),
          u = 8 * 1024 * 1024,
          p = Math.ceil(t.size / u),
          m = [];
        let S = 0;
        try {
          for (let I = 0, x = 1; I < t.size; I += u, x += 1) {
            if (o === "ORIGINAL" && i !== e.uploadGeneration)
              throw new Error("UPLOAD_CANCELLED");
            const g = t.slice(I, Math.min(I + u, t.size)),
              v = await ea(g);
            let X;
            for (let H = 1; H <= 3 && !X; H += 1)
              try {
                const M = await lt("uploads/sign-part", {
                  ...d,
                  partNumber: x,
                  byteSize: g.size,
                  checksumSha256: v,
                });
                X = await ta(M.url, g, v, (L) => {
                  o === "ORIGINAL" && i === e.uploadGeneration &&
                    L.lengthComputable &&
                    Fe(
                      Math.min(99, Math.round(((S + L.loaded) / t.size) * 100)),
                    );
                });
              } catch (M) {
                if ((dt(n, `part-${x}`, `${r}.${H}`, M), H === 3)) throw M;
              }
            (m.push({ partNumber: x, etag: X, checksumSha256: v }),
              (S += g.size));
          }
          if (o === "ORIGINAL" && i !== e.uploadGeneration)
            throw new Error("UPLOAD_CANCELLED");
          if (m.length !== p) throw new Error("UPLOAD_INCOMPLETE");
          if(o==="ORIGINAL" && i===e.uploadGeneration) photoUploadPhase("preparing", "Checking your upload and preparing preview…");
          return await U("uploads/complete", { ...d, parts: m });
        } catch (I) {
          throw (await U("uploads/cancel", d).catch(() => {}), I);
        }
      }
      async function mt(t, o, i) {
        const n = crypto.randomUUID();
        for (let r = 1; r <= 2; r += 1)
          try {
            return await aa(t, o, i, n, r);
          } catch (d) {
            if (
              (dt(n, d?.uploadStage || o.toLowerCase(), r, d),
              r === 2 || !Zt(d))
            )
              throw d;
          }
        throw new Error("UPLOAD_SERVICE_FAILED");
      }
      async function oa(t, o) {
        if (o !== e.uploadGeneration) throw new Error("UPLOAD_CANCELLED");
        clearOriginalVerification();
        photoUploadPhase("uploading", "Uploading your photograph…");
        ((e.originalUploadStatus = "uploading"),
          (a.choosePhotoButton.disabled = !0),
          (a.sendLargeFileButton.disabled = !0),
          Fe(null),
          Ae(t, "Uploading your photograph\u2026"));
        try {
          const i = await mt(t, "ORIGINAL", o);
          if (o !== e.uploadGeneration) throw new Error("UPLOAD_CANCELLED");
          if (!e.configurationId || i.configurationId !== e.configurationId || i.kind !== "ORIGINAL" || i.status !== "VERIFIED") throw new Error("UPLOAD_ORIGINAL_REQUIRED");
          return (
            (e.originalVerifiedConfigurationId = i.configurationId),
            (e.originalUploadStatus = "verified"),
            (e.cleanedOriginalFilename = i.originalFilename || "upload"),
            photoUploadPhase("preparing", "Original safely uploaded. Preparing preview…"),
            i
          );
        } catch (i) {
          throw (
            o === e.uploadGeneration &&
              ((e.originalUploadStatus = "failed"),
              photoUploadPhase("failed", "Upload failed. Retry your photograph or choose another file."),
              a.uploadProgress && (a.uploadProgress.hidden = !0),
              Ae(
                t,
                "Upload failed \u2014 choose this photograph again to retry.",
              ),
              i instanceof Error && i.message === "UPLOAD_VERIFICATION_REQUIRED"
                ? Jt()
                : i instanceof Error && i.message === "UPLOAD_RATE_LIMITED"
                  ? ht(
                      "Direct uploads have been paused for this connection",
                      "You can still place your order as normal. We\u2019re sorry if you\u2019re a genuine customer seeing this \u2014 please send your image by email or file-transfer link instead.",
                    )
                  : (a.uploadStatus.textContent =
                      "Upload failed. Choose the photograph again to retry, or use Send a large file.")),
            i
          );
        } finally {
          if (o === e.uploadGeneration && e.photoPreviewPhase === "failed") {
            a.choosePhotoButton.disabled = false; a.sendLargeFileButton.disabled = false;
          }
        }
      }
      function ia() {
        photoUploadPhase("", "");
        ((e.uploadGeneration += 1),
          e.activeUploadRequests.forEach((t) => t.abort()),
          e.activeUploadRequests.clear(),
          e.configurationId &&
            e.recoveryToken &&
            U("uploads/cancel", {
              configurationId: e.configurationId,
              recoveryToken: e.recoveryToken,
              kind: "ORIGINAL",
            }).catch(() => {}),
          (e.originalUploadStatus = "not-started"),
          (e.uploadPromise = null));
      }
      function basketReferenceBounds(canvas, geometry, transform) {
        // Capture the existing artwork AND crop guides, excluding the empty stage.
        // Geometry is in CSS coordinates; the canvas transform carries its real DPR.
        const box = geometry?.outerRectangle;
        if (!box) return {x:0, y:0, width:canvas.width, height:canvas.height};
        const densityX = transform.a, densityY = transform.d, padding = 4;
        const x = Math.max(0, Math.floor((box.x - padding) * densityX));
        const y = Math.max(0, Math.floor((box.y - padding) * densityY));
        const right = Math.min(canvas.width, Math.ceil((box.x + box.width + padding) * densityX));
        const bottom = Math.min(canvas.height, Math.ceil((box.y + box.height + padding) * densityY));
        return {x, y, width:Math.max(1, right - x), height:Math.max(1, bottom - y)};
      }
      function na() {
        if(e.productType==='canvas'&&e.canvasStyle==='box'){
          D();const display=framedDisplayCanvas(1200);
          return new Promise((resolve,reject)=>display.toBlob(blob=>blob?resolve(blob):reject(Error('CROP_REFERENCE_FAILED')),'image/jpeg',.92));
        }
        const bounds = basketReferenceBounds(a.previewCanvas, e.geometry, s.getTransform());
        const t = document.createElement("canvas");
        const scale = Math.min(1, 1600 / Math.max(bounds.width, bounds.height));
        t.width = Math.max(1, Math.round(bounds.width * scale));
        t.height = Math.max(1, Math.round(bounds.height * scale));
        const o = t.getContext("2d");
        o.imageSmoothingEnabled = scale < 1;
        o.imageSmoothingQuality = "high";
        o.fillStyle = "#ffffff";
        o.fillRect(0, 0, t.width, t.height);
        o.drawImage(a.previewCanvas, bounds.x, bounds.y, bounds.width, bounds.height, 0, 0, t.width, t.height);
        return new Promise((resolve, reject) => t.toBlob(
          blob => blob ? resolve(blob) : reject(new Error("CROP_REFERENCE_FAILED")), "image/jpeg", .88,
        ));
      }
      async function completeConvertedReference() {
        const image = new Image();
        image.src = e.workingPreviewUrl;
        await image.decode();
        const canvas = document.createElement('canvas');
        const scale = Math.min(1,1600/Math.max(image.naturalWidth,image.naturalHeight));
        canvas.width=Math.max(1,Math.round(image.naturalWidth*scale));
        canvas.height=Math.max(1,Math.round(image.naturalHeight*scale));
        const context=canvas.getContext('2d');
        context.fillStyle='#fff';context.fillRect(0,0,canvas.width,canvas.height);
        context.drawImage(image,0,0,canvas.width,canvas.height);
        return new Promise((resolve,reject)=>canvas.toBlob(blob=>blob?resolve(blob):reject(new Error('CROP_REFERENCE_FAILED')),'image/jpeg',.88));
      }
      async function placeholderCropReference() {
        const t = a.placeholderArtwork;
        if (!t) throw new Error("CROP_REFERENCE_FAILED");
        t.complete || (await t.decode());
        const o = k(),
          i = document.createElement("canvas"),
          n = o.widthInches / o.heightInches;
        ((i.width = n >= 1 ? 1600 : Math.round(1600 * n)),
          (i.height = n >= 1 ? Math.round(1600 / n) : 1600));
        const r = i.getContext("2d"),
          d = Math.max(i.width / t.naturalWidth, i.height / t.naturalHeight),
          l = t.naturalWidth * d,
          u = t.naturalHeight * d;
        return (
          r.drawImage(t, (i.width - l) / 2, (i.height - u) / 2, l, u),
          new Promise((p, m) =>
            i.toBlob(
              (S) => (S ? p(S) : m(new Error("CROP_REFERENCE_FAILED"))),
              "image/jpeg",
              0.88,
            ),
          )
        );
      }
      async function ra(t, o) {
        if (!t && !o) throw new Error("SAVED_ARTWORK_NOT_AVAILABLE");
        const i = [t, o].filter(Boolean);
        for (const n of i) {
          let r = "";
          try {
            const d = await fetch(n, { credentials: "same-origin" });
            if (!d.ok) continue;
            const l = await d.blob();
            r = URL.createObjectURL(l);
            const u = new Image();
            return (
              await new Promise((p, m) => {
                ((u.onload = p), (u.onerror = m), (u.src = r));
              }),
              (e.objectUrl = r),
              (e.image = u),
              (e.file = null),
              u
            );
          } catch {
            r && URL.revokeObjectURL(r);
          }
        }
        throw new Error("SAVED_ARTWORK_NOT_AVAILABLE");
      }
      async function sa(t, expressDraft = null) {
        const o = window.Shopify?.routes?.root || "/",
          i = (
            await fetch(`${o}cart.js`, {
              headers: { Accept: "application/json" },
            }).then((g) => g.json())
          ).items?.find((g) => g.properties?._yi2c_configuration_id === t),
          n =
            expressDraft?.recoveryToken ||
            i?.properties?._yi2c_edit_credential ||
            "";
        if (!i && !expressDraft) throw new Error("EDIT_BASKET_LINE_NOT_FOUND");
        if (!n) throw new Error("EDIT_CREDENTIAL_MISSING");
        const r = await U("configurations/recover", {
          configurationId: t,
          recoveryToken: n,
        });
        if (r.configuration?.status !== "DRAFT")
          throw new Error("CONFIGURATION_NOT_EDITABLE");
        const d = r.configuration.builderConfiguration || {},
          l = Number(d.widthInches || 0),
          u = Number(d.heightInches || 0);
        if (d.productType !== "canvas" && d.productType !== "poster")
          throw new Error("CONFIGURATION_PRODUCT_INVALID");
        lockedProductType = d.productType;
        if(d.productType==='poster'){
          e.posterLegacy=r.configuration.pricingVersion==='2026-08-authoritative-1';
          e.posterDesktopV2=!e.posterLegacy && ['poster-2026-09-29-v2','poster-2026-09-29-v2-private'].includes(r.configuration.pricingVersion) && h.hasAttribute('data-poster-desktop-private');
        }
        let p = null;
        try {
          p = JSON.parse(i.properties?._yi2c_crop_data || "null");
        } catch {
          p = null;
        }
        const m = F(Number(p?.zoom) || 1, y.minimumZoom, y.maximumZoom),
          S = (((Number(p?.rotationDegrees) || 0) % 360) + 360) % 360,
          I = Number(p?.panXRelative) || 0,
          x = Number(p?.panYRelative) || 0;
        if (
          (Object.assign(e, {
            configurationId: t,
            recoveryToken: n,
            cropReferenceUrl: r.cropReferenceUrl || "",
            editingConfigurationId: i ? t : "",
            expressDraftHandoff: !!expressDraft,
            productType: lockedProductType,
            imageMethod: d.imageMethod === "SEPARATE" ? "separate" : "upload",
            orientation: l >= u ? "landscape" : "portrait",
            selectedStandardSizeId: d.standardSizeId || "12x16",
            customMode: expressDraft
              ? !!expressDraft.custom
              : !d.standardSizeId,
            customUnit: expressDraft?.selection?.unit === "cm" ? "cm" : "in",
            customWidth:
              expressDraft?.custom && expressDraft.selection?.customWidth
                ? String(expressDraft.selection.customWidth)
                : l
                  ? String(l)
                  : "",
            customHeight:
              expressDraft?.custom && expressDraft.selection?.customHeight
                ? String(expressDraft.selection.customHeight)
                : u
                  ? String(u)
                  : "",
            canvasStyle: d.style || "slim",
            previousCanvasStyle: d.style || "slim",
            edgeStyle: d.edgeStyle || "gallery",
            edgeColour: d.edgeColour || "#000000",
            frameColour: d.frameColour || "black",
            printColour: d.printColour || "original",
            hangingKit: !!d.hangingKit,
            designerCrop: expressDraft ? false : !!d.designerCrop,
            orderNotes: r.configuration.customerNotes || "",
            originalUploadStatus:
              r.uploads?.original?.status === "VERIFIED" && r.uploads.original.configurationId === t && r.uploads.original.kind === "ORIGINAL"
                ? "verified"
                : "not-started",
            originalVerifiedConfigurationId: r.uploads?.original?.status === "VERIFIED" && r.uploads.original.configurationId === t && r.uploads.original.kind === "ORIGINAL" ? t : "",
            cleanedOriginalFilename:
              r.uploads?.original?.originalFilename || "",
            zoom: 1,
            rotation: 0,
            panX: 0,
            panY: 0,
          }),
          e.imageMethod === "separate")
        )
          (Re(), (e.orientation = l >= u ? "landscape" : "portrait"));
        else if (!expressDraft || r.uploads?.original?.status === "VERIFIED") {
e.workingPreviewUrl=r.workingPreviewUrl||"";e.originalContentType=r.uploads?.original?.verifiedContentType||"";e.originalArtworkUrl=r.originalArtworkUrl||'';
          const g = await ra(r.workingPreviewUrl||r.originalArtworkUrl, "");
          (Bt(g, { preserveSpecification: !0 }),
            (e.zoom = m),
            (e.rotation = S),
            (a.zoomSlider.value = String(m)));
        }
        if (expressDraft) {
          w("[data-unit]").forEach((n) =>
            n.classList.toggle("is-selected", n.dataset.unit === e.customUnit),
          );
          a.customUnitLabels.forEach((n) => {
            n.textContent = e.customUnit === "cm" ? "cm" : '"';
          });
        }
        return (
          (a.edgeColourInput.value = e.edgeColour),
          a.edgeColourHexInput && (a.edgeColourHexInput.value = e.edgeColour),
          (a.hangingKitInput.checked = e.hangingKit),
          (a.designerCropInput.checked = e.designerCrop),
          (a.orderNotesInput.value = e.orderNotes),
          (a.customWidthInput.value = e.customWidth),
          (a.customHeightInput.value = e.customHeight),
          (a.uploadScreen.hidden = !0),
          (a.editor.hidden = !1),
          G(),
          ge(),
          (a.addToCartButton.textContent = i
            ? "Update basket"
            : "Add to basket"),
          (a.mobileAddButton.textContent = i
            ? "Update basket"
            : "Add to basket"),
          !e.image && expressDraft
            ? (ut(), G())
            : e.imageMethod === "separate"
              ? (C(), ce())
              : (ce(),
                Nt(() => {
                  const g = e.geometry?.frontFace;
                  (g && ((e.panX = I * g.width), (e.panY = x * g.height)),
                    (e.zoom = m),
                    (e.rotation = S),
                    (a.zoomSlider.value = String(m)),
                    C());
                })),
          !0
        );
      }
      async function da() {
        try {
          const xecToken = new URLSearchParams(location.search).get(
            "yi2c_express_draft",
          );
          if (xecToken) {
            const draft = window.YI2CExpressDraft?.read(xecToken);
            if (!draft?.configurationId || !draft.recoveryToken)
              throw new Error("EXPRESS_DRAFT_EXPIRED");
            await sa(draft.configurationId, draft);
            await pt();
            window.YI2CExpressDraft.write(xecToken, {
              ...draft,
              selection: {
                unit: e.customUnit,
                customWidth: e.customWidth,
                customHeight: e.customHeight,
                hangingKit: String(e.hangingKit),
              },
              custom: e.customMode,
            });
            return;
          }
          const t = ee();
          if (((e.editingConfigurationId = t), t)) {
            await sa(t);
            return;
          }
          if (
            new URLSearchParams(window.location.search).get("yi2c_new") === "1"
          ) {
            N();
            return;
          }
          e.editingConfigurationId = "";
          // A deliberate offer entry must not recover an unrelated previous configuration.
          if (priceEntry || offerPreset) return;
          const o = JSON.parse(localStorage.getItem(Q) || "null");
          if (!o || o.expiresAt <= Date.now()) {
            localStorage.removeItem(Q);
            return;
          }
          const i = await U("configurations/recover", {
            configurationId: o.configurationId,
            recoveryToken: o.recoveryToken,
          });
          if (i.configuration?.status !== "DRAFT") {
            gt();
            return;
          }
          // Do not reuse a different product's ordinary draft in this entry.
          // Keep that draft intact for its own route or Basket Edit.
          if (i.configuration.builderConfiguration?.productType !== lockedProductType)
            return;
          if(lockedProductType==='poster'){
            e.posterLegacy=i.configuration.pricingVersion==='2026-08-authoritative-1';
            e.posterDesktopV2=!e.posterLegacy && ['poster-2026-09-29-v2','poster-2026-09-29-v2-private'].includes(i.configuration.pricingVersion) && h.hasAttribute('data-poster-desktop-private');
          }
          ((e.configurationId = o.configurationId),
            (e.recoveryToken = o.recoveryToken),
            (e.cropReferenceUrl = i.cropReferenceUrl || ""),
            (e.originalUploadStatus =
              i.uploads?.original?.status === "VERIFIED" && i.uploads.original.configurationId === o.configurationId && i.uploads.original.kind === "ORIGINAL"
                ? "verified"
                : "not-started"),
            (e.originalVerifiedConfigurationId = i.uploads?.original?.status === "VERIFIED" && i.uploads.original.configurationId === o.configurationId && i.uploads.original.kind === "ORIGINAL" ? o.configurationId : ""),
            (e.cleanedOriginalFilename =
              i.uploads?.original?.originalFilename || ""),
            e.originalUploadStatus === "verified" &&
              (a.uploadStatus.textContent =
                "Your previous original is safely stored. Re-select it to continue editing."));
        } catch (t) {
          if (
            (console.warn(
              "YI2C Builder: basket edit recovery failed.",
              t instanceof Error ? t.message : "UNKNOWN_ERROR",
            ),
            ee() ||
              new URLSearchParams(location.search).has("yi2c_express_draft"))
          ) {
            Se(t);
            return;
          }
          N();
        }
      }
      function ca() {
        if (e.expressDraftHandoff || !e.image) return;
        const t = e.naturalWidth / e.naturalHeight,
          o = 0.08;
        if (t >= 1 - o && t <= 1 + o) {
          ((e.orientation = "landscape"), (e.selectedStandardSizeId = "16x16"));
          return;
        }
        ((e.selectedStandardSizeId = "12x16"),
          e.naturalHeight > e.naturalWidth
            ? (e.orientation = "portrait")
            : (e.orientation = "landscape"));
      }
      function yt(t) {
        if (!t) return null;
        if (de(t.widthInches, t.heightInches, 0.001))
          return { widthInches: t.widthInches, heightInches: t.heightInches };
        const o = Math.min(t.widthInches, t.heightInches),
          i = Math.max(t.widthInches, t.heightInches);
        return e.orientation === "portrait"
          ? { widthInches: o, heightInches: i }
          : { widthInches: i, heightInches: o };
      }
      function la(t, o) {
        return t.group === "A Sizes"
          ? `${t.label} \xB7 ${t.metricLabel}`
          : `${B(o.widthInches)} \xD7 ${B(o.heightInches)}"`;
      }
      function k() {
        if (e.customMode) {
          const i = Number(e.customWidth),
            n = Number(e.customHeight);
          if (!i || !n) return null;
          const r = e.customUnit === "cm" ? 1 / 2.54 : 1;
          return {
            widthInches: i * r,
            heightInches: n * r,
            label: `${B(i)} \xD7 ${B(n)}${e.customUnit === "cm" ? "cm" : '"'}`,
          };
        }
        const posterPreset = e.posterDesktopV2 && e.productType === "poster" && window.YI2CPosterDesktop.find(e.selectedStandardSizeId);
        if (posterPreset) {
          const dimensions = yt(posterPreset);
          return {...dimensions, label: `${posterPreset.label} · ${B(dimensions.widthInches * 2.54)} × ${B(dimensions.heightInches * 2.54)}cm`};
        }
        const t = re.find((i) => i.id === e.selectedStandardSizeId);
        if (!t) return null;
        const o = yt(t);
        return { ...o, label: la(t, o) };
      }
      function xe() {
        return e.customUnit === "cm" ? "cm" : "in";
      }
      function ze(t) {
        return xe() === "cm" ? t * 2.54 : t;
      }
      function ft(t) {
        return xe() === "cm" ? t / 2.54 : t;
      }
      function ua() {
        return xe() === "cm" ? "cm" : '"';
      }
      function wt() {
        a.orientationButtons.forEach((t) => {
          t.classList.toggle(
            "is-selected",
            t.dataset.orientation === e.orientation,
          );
        });
      }
      function vt() {
        const t = k();
        if ((wt(), !t)) {
          (a.widthValueButton && (a.widthValueButton.textContent = "\u2014"),
            a.heightValueButton &&
              (a.heightValueButton.textContent = "\u2014"));
          return;
        }
        const o = de(t.widthInches, t.heightInches, 0.001);
        (a.orientationControl && (a.orientationControl.hidden = o),
          a.squareOrientation && (a.squareOrientation.hidden = !o),
          a.orientationButtons.forEach((d) => {
            d.disabled = o;
          }));
        const i = ze(t.widthInches),
          n = ze(t.heightInches),
          r = ua();
        (a.widthValueButton && (a.widthValueButton.textContent = `${B(i)}${r}`),
          a.heightValueButton &&
            (a.heightValueButton.textContent = `${B(n)}${r}`));
      }
      function syncExactSizeFields() {
        if (e.customMode) return;
        const t = k();
        if (!t) return;
        const o = e.customUnit === "cm" ? 2.54 : 1,
          i = e.customUnit === "cm" ? 1 : 2,
          n = Number((t.widthInches * o).toFixed(i)),
          r = Number((t.heightInches * o).toFixed(i));
        ((e.customWidth = n),
          (e.customHeight = r),
          (a.customWidthInput.value = String(n)),
          (a.customHeightInput.value = String(r)));
      }
      function ha(t, o) {
        const i = Z(t, o);
        return i.short < y.minimumShortSideInches ||
          i.long < y.minimumLongSideInches
          ? {
              valid: !1,
              message: "Custom canvases start from 5 × 5 inches.",
            }
          : i.short > y.maximumShortSideInches
            ? {
                valid: !1,
                message: `Maximum printable width is ${y.maximumShortSideInches}".`,
              }
            : i.long * 2.54 > y.maximumLongSideCm
              ? {
                  valid: !1,
                  message: `Maximum printable length is ${y.maximumLongSideCm}cm.`,
                }
              : { valid: !0 };
      }
      function qe(t, o, { showPanel: i = !1 } = {}) {
        const n = ha(t, o);
        return n.valid
          ? ((e.customMode = !0),
            xe() === "cm"
              ? ((e.customWidth = Number((t * 2.54).toFixed(1))),
                (e.customHeight = Number((o * 2.54).toFixed(1))))
              : ((e.customWidth = Number(t.toFixed(2))),
                (e.customHeight = Number(o.toFixed(2)))),
            (a.customWidthInput.value = e.customWidth),
            (a.customHeightInput.value = e.customHeight),
            i &&
              (ie(!0),
              (a.allSizes.hidden = !0),
              (a.toggleAllSizesButton.textContent = "All sizes")),
            oe(),
            e.productType === "canvas" &&
              e.edgeStyle === "gallery" &&
              (e.canvasStyle === "slim" || e.canvasStyle === "chunky") &&
              q(),
            C(),
            !0)
          : ((a.trustText.textContent = n.message), !1);
      }
      function Ne(t, o) {
        const i = k();
        if (!i) return;
        const n = (xe(), 1);
        let r = ze(i.widthInches),
          d = ze(i.heightInches);
        t === "width"
          ? (r = Number((r + o * n).toFixed(2)))
          : (d = Number((d + o * n).toFixed(2)));
        const minimum = xe() === "cm" ? 12.7 : 5;
        if (e.productType === "canvas") {
          r = Math.max(minimum, r);
          d = Math.max(minimum, d);
        }
        const l = ft(r),
          u = ft(d);
        qe(l, u);
      }
      function St(t) {
        const o = k();
        o &&
          (e.customMode
            ? ((a.customPanel.hidden = !1), (a.allSizes.hidden = !0))
            : qe(o.widthInches, o.heightInches, { showPanel: !0 }),
          requestAnimationFrame(() => {
            t === "width"
              ? (a.customWidthInput.focus(), a.customWidthInput.select())
              : (a.customHeightInput.focus(), a.customHeightInput.select());
          }));
      }
      function ga(t) {
        if (t !== "landscape" && t !== "portrait") return;
        if (e.orientation === t) {
          wt();
          return;
        }
        const o = k();
        if (((e.orientation = t), e.customMode && o)) {
          const i = Math.min(o.widthInches, o.heightInches),
            n = Math.max(o.widthInches, o.heightInches);
          qe(t === "portrait" ? i : n, t === "portrait" ? n : i);
        } else
          ((e.panX = 0),
            (e.panY = 0),
            ge(),
            e.productType === "canvas" &&
              e.edgeStyle === "gallery" &&
              (e.canvasStyle === "slim" || e.canvasStyle === "chunky") &&
              q(),
            C());
      }
      function pa(t,o) { return builderPriceContext(e,a,k,he).match(t,o); }
      function bt() { return builderPriceContext(e,a,k,he).validate(); }
      function Be() { return builderPriceContext(e,a,k,he).calculate(); }
      function he(t) {
        const o = t ? J(e.totalPrice, "GBP") : "\u2014";
        ((a.totalPrice.textContent = o),
          a.priceProductLabel &&
            (a.priceProductLabel.textContent =
              e.productType === "poster" ? "Your poster" : "Your canvas"),
          (a.mobilePrice.textContent = o));
        const i = e.imageMethod === "separate" || (!!e.image && (!e.photoPreviewPhase || e.photoPreviewPhase === "ready"));
        ((a.addToCartButton.disabled = !t || !i || e.addingToCart),
          (a.mobileAddButton.disabled = !t || !i || e.addingToCart));
        const n = k();
        if (
          ((a.sizeSummary.textContent = n ? n.label : ""),
          (a.mobileSize.textContent = n ? n.label : ""),
          vt(),
          !t)
        ) {
          a.priceNote.textContent = "Enter a valid size to continue.";
          return;
        }
        if (e.deliveryExtra === y.outOfGaugeDeliveryPrice) {
          a.priceNote.textContent = `Includes ${J(y.outOfGaugeDeliveryPrice, "GBP")} specialist large-canvas delivery.`;
          return;
        }
        if (e.deliveryExtra === y.oversizeDeliveryPrice) {
          a.priceNote.textContent = `Includes ${J(y.oversizeDeliveryPrice, "GBP")} oversized delivery.`;
          return;
        }
        if (e.canvasStyle === "box") {
          a.priceNote.textContent = `Includes ${J(e.boxFrameExtra, "GBP")} floating-frame upgrade.`;
          return;
        }
        a.priceNote.textContent =
          e.productType === "poster"
            ? "230gsm matte poster print."
            : "Price updates automatically as you make changes.";
      }
      function xt(t) {
        const o = document.createElement("button");
        ((o.type = "button"),
          (o.className = "yi2c-choice yi2c-choice--size"),
          (o.dataset.sizeId = t.id));
        const i = yt(t),
          n = document.createElement("strong"),
          r = document.createElement("span");
        if (
          (t.group === "A Sizes"
            ? (n.textContent = t.label)
            : (n.textContent = `${B(i.widthInches)} \xD7 ${B(i.heightInches)}"`),
          e.productType === "poster")
        ) {
          const d = nt(i.widthInches, i.heightInches);
          d.valid
            ? (r.textContent = J(
                d.basePrice + d.customHandlingPrice + d.deliveryPrice,
                "GBP",
              ))
            : ((r.textContent = "Unavailable"),
              (o.disabled = !0),
              (o.title = d.message));
        } else r.textContent = J(t.price, "GBP");
        return (
          o.append(n, r),
          o.addEventListener("click", () => {
            ma(t.id);
          }),
          o
        );
      }
      function ge() {
        ((a.popularSizes.innerHTML = ""),
          Gt.forEach((t) => {
            const o = re.find((i) => i.id === t);
            o && a.popularSizes.appendChild(xt(o));
          }),
          (a.allSizes.innerHTML = ""),
          ["Rectangle", "Square", "Panoramic", "A Sizes"].forEach((t) => {
            const o = document.createElement("div");
            o.className = "yi2c-size-group";
            const i = document.createElement("p");
            ((i.className = "yi2c-size-group__title"), (i.textContent = t));
            const n = document.createElement("div");
            ((n.className = "yi2c-size-group__grid"),
              re
                .filter(
                  (r) =>
                    r.group === t &&
                    !(e.productType === "poster" && r.id === "a0"),
                )
                .forEach((r) => {
                  n.appendChild(xt(r));
                }),
              o.append(i, n),
              a.allSizes.appendChild(o));
          }),
          oe());
      }
      function oe() {
        [a.customWidthInput, a.customHeightInput].forEach((input) => {
          input.min = e.customUnit === "cm" ? "12.7" : "5";
        });
        w("[data-size-id]").forEach((t) => {
          const o =
            !e.customMode && t.dataset.sizeId === e.selectedStandardSizeId;
          t.classList.toggle("is-selected", o);
        });
      }
      function ma(t) {
        ((e.selectedStandardSizeId = t),
          me("size"),
          (e.customMode = !1),
          (e.customMatchedStandardSize = null),
          ie(!1),
          Rt(),
          oe(),
          e.productType === "canvas" &&
            e.edgeStyle === "gallery" &&
            (e.canvasStyle === "slim" || e.canvasStyle === "chunky") &&
            q(),
          C());
      }
      function pe() {
        ((a.allSizes.hidden = !0),
          (e.customMode && window.matchMedia("(min-width: 900px)").matches) ||
            ie(!1),
          (a.toggleAllSizesButton.textContent = "All sizes"));
      }
      function Ct() {
        e.productType = lockedProductType;
        h.dataset.yi2cLockedProduct = lockedProductType;
        const toolLabel = document.querySelector(".yi2c-builder-tool-label");
        if (toolLabel) toolLabel.textContent = lockedProductType === "poster" ? "Poster Creator" : "Canvas Creator";
        (a.productTypeButtons.forEach((t) => {
          t.classList.toggle(
            "is-selected",
            t.dataset.productType === e.productType,
          );
        }),
          a.posterNote && (a.posterNote.hidden = e.productType !== "poster"),
          h.classList.toggle("is-poster-mode", e.productType === "poster"),
          e.productType === "poster"
            ? (e.canvasStyle !== "unframed" &&
                (e.previousCanvasStyle = e.canvasStyle),
              (e.canvasStyle = "unframed"),
              (e.hangingKit = !1),
              (a.hangingKitInput.checked = !1))
            : e.canvasStyle === "unframed" &&
              e.previousCanvasStyle &&
              (e.canvasStyle = e.previousCanvasStyle));
      }
      function ya(t) {
        if (t !== lockedProductType) return;
        (t !== "canvas" && t !== "poster") ||
          (e.productType !== t &&
            ((e.productType = t),
            t === "poster" &&
              !e.customMode &&
              e.selectedStandardSizeId === "a0" &&
              (e.selectedStandardSizeId = "a1"),
            pe(),
            (e.panX = 0),
            (e.panY = 0),
            (e.zoom = 1),
            (a.zoomSlider.value = "1"),
            ge(),
            C()));
      }
      function me(t) {
        Object.prototype.hasOwnProperty.call(e.stepTouched, t) &&
          (e.stepTouched[t] = !0);
      }
      function It() {
        const t =
          e.productType === "poster"
            ? ["size", "photo"]
            : ["size", "canvas", "sides", "photo"];
        let o = null;
        for (const i of t)
          if (!e.stepTouched[i]) {
            o = i;
            break;
          }
        a.builderSteps.forEach((i) => {
          const n = i.dataset.builderStep,
            r = t.includes(n);
          (i.classList.toggle("is-complete", r && !!e.stepTouched[n]),
            i.classList.toggle("is-next", r && n === o),
            i.classList.toggle("is-skipped", !r));
        });
      }
      function q() {
        if (
          !renderSource() ||
          e.productType !== "canvas" ||
          e.edgeStyle !== "gallery"
        )
          return;
        const t = Lt(),
          o = Tt(t.width, t.height);
        if (!o || o.mode !== "wrapped") return;
        const i = We();
        if (!i.width || !i.height) return;
        const n = Math.max(
            o.frontFace.width / i.width,
            o.frontFace.height / i.height,
          ),
          r = Math.max(
            o.outerRectangle.width / i.width,
            o.outerRectangle.height / i.height,
          );
        ((e.zoom = F(r / n, y.minimumZoom, y.maximumZoom)),
          (e.panX = 0),
          (e.panY = 0),
          (a.zoomSlider.value = String(e.zoom)));
      }
      function Et() {
        if ((Ct(), It(), a.hangingKitRow)) {
          const t = e.canvasStyle === "unframed" || e.productType === "poster";
          ((a.hangingKitRow.hidden = t),
            t &&
              e.hangingKit &&
              ((e.hangingKit = !1), (a.hangingKitInput.checked = !1)));
          const o = k(),
            i =
              !t &&
              o &&
              Math.max(o.widthInches, o.heightInches) >
                y.hangingKitPairThresholdInches;
          (a.hangingKitLabel &&
            (a.hangingKitLabel.textContent = i
              ? "Add a pair of hanging kits"
              : "Add a hanging kit"),
            a.hangingKitPriceLabel &&
              (a.hangingKitPriceLabel.textContent = i
                ? "+\xA32.50"
                : "+\xA31.25"),
            a.hangingKitRecommendation &&
              (a.hangingKitRecommendation.hidden = !i));
        }
        (G(),
          (a.frameColours.hidden = e.canvasStyle !== "box"),
          (a.edgeSection.hidden =
            e.productType !== "canvas" ||
            e.canvasStyle === "unframed" ||
            e.canvasStyle === "box"),
          (a.edgeColourWrap.hidden =
            e.productType !== "canvas" ||
            e.edgeStyle !== "colour" ||
            e.canvasStyle === "unframed"),
          w("[data-style]").forEach((t) => {
            t.classList.toggle(
              "is-selected",
              t.dataset.style === e.canvasStyle,
            );
          }),
          w("[data-edge]").forEach((t) => {
            t.classList.toggle("is-selected", t.dataset.edge === e.edgeStyle);
          }),
          w("[data-frame-colour]").forEach((t) => {
            t.classList.toggle(
              "is-selected",
              t.dataset.frameColour === e.frameColour,
            );
          }),
          w("[data-print-colour]").forEach((t) => {
            t.classList.toggle(
              "is-selected",
              t.dataset.printColour === e.printColour,
            );
          }));
      }
      function G() {
        const t = e.imageMethod === "separate";
        (a.imageMethodStatus && (a.imageMethodStatus.hidden = !t),
          a.photoPlaceholder &&
            ((a.photoPlaceholder.hidden = !t),
            (a.photoPlaceholder.style.background = t ? "transparent" : "")),
          a.placeholderArtwork &&
            ((a.placeholderArtwork.hidden = !0),
            (a.placeholderArtwork.style.display = "none")),
          syncPreviewGeometry(),
          a.retainedUpload &&
            (a.retainedUpload.hidden = !(t && !!e.retainedUploadState)),
          a.changePhotoButton && (a.changePhotoButton.hidden = t),
          t
            ? ((e.designerCrop = !0),
              (a.designerCropInput.checked = !0),
              (a.designerCropInput.disabled = !0))
            : (a.designerCropInput.disabled = !1));
        const o = !t && e.designerCrop && !!e.image;
        a.designerOverlay && (a.designerOverlay.hidden = !o);
        const i = t || o || !e.image;
        (a.photoTools && (a.photoTools.hidden = i),
          a.previewTip && (a.previewTip.hidden = i),
          a.designerCropInput?.closest(".yi2c-designer-help") &&
            (a.designerCropInput.closest(".yi2c-designer-help").hidden =
              t || !e.image || e.originalUploadStatus !== "verified"),
          a.zoomSlider && (a.zoomSlider.disabled = i),
          a.zoomInButton && (a.zoomInButton.disabled = i),
          a.zoomOutButton && (a.zoomOutButton.disabled = i),
          a.rotateButton && (a.rotateButton.disabled = i),
          a.resetCropButton && (a.resetCropButton.disabled = i),
          a.positionChoiceButtons.forEach((n) => {
            const r = n.dataset.positionChoice === "studio";
            (n.classList.toggle("is-selected", t ? r : r === e.designerCrop),
              (n.disabled = t));
          }));
      }
      function Re() {
        const t = !!e.image && e.originalUploadStatus === "verified";
        (t
          ? (e.retainedUploadState = {
              designerCrop: e.designerCrop,
              orientation: e.orientation,
              zoom: e.zoom,
              panX: e.panX,
              panY: e.panY,
              rotation: e.rotation,
            })
          : (ia(),
            e.objectUrl && URL.revokeObjectURL(e.objectUrl),
            (e.objectUrl = ""),
            (e.file = null),
            (e.image = null),
            (e.naturalWidth = 0),
            (e.naturalHeight = 0),
            (e.retainedUploadState = null)),
          (e.imageMethod = "separate"),
          t || (e.orientation = "landscape"),
          (e.zoom = 1),
          (e.panX = 0),
          (e.panY = 0),
          (e.rotation = 0),
          (a.zoomSlider.value = "1"),
          (a.uploadScreen.hidden = !0),
          (a.editor.hidden = !1),
          ge(),
          G(),
          C());
      }
      function restoreRetainedUpload() {
        const t = e.retainedUploadState;
        if (!t || !e.image) {
          Ve();
          return;
        }
        ((e.imageMethod = "upload"),
          (e.designerCrop = t.designerCrop),
          (e.orientation = t.orientation),
          (e.zoom = t.zoom),
          (e.panX = t.panX),
          (e.panY = t.panY),
          (e.rotation = t.rotation),
          (a.zoomSlider.value = String(t.zoom)),
          (a.designerCropInput.checked = t.designerCrop),
          (a.uploadScreen.hidden = !0),
          (a.editor.hidden = !1),
          (e.retainedUploadState = null),
          G(),
          C());
      }
      function fa() {
        return e.canvasStyle === "chunky"
          ? y.chunkyGalleryAllowanceInches
          : (e.canvasStyle, y.slimGalleryAllowanceInches);
      }
      function Lt() {
        const t = a.previewCanvas.getBoundingClientRect(),
          o = Math.min(
            window.devicePixelRatio || 1,
            window.matchMedia("(min-width:900px)").matches
              ? Infinity
              : y.maximumPreviewPixelRatio,
          ),
          i = Math.max(320, Math.round(t.width)),
          n = Math.max(360, Math.round(t.height)),
          r = Math.round(i * o),
          d = Math.round(n * o);
        return (
          (a.previewCanvas.width !== r || a.previewCanvas.height !== d) &&
            ((a.previewCanvas.width = r), (a.previewCanvas.height = d)),
          s.setTransform(o, 0, 0, o, 0, 0),
          (s.imageSmoothingEnabled = true),
          (s.imageSmoothingQuality = "high"),
          { width: i, height: n }
        );
      }
      function He(t, o, i, n) {
        const r = Math.max(10, t - n * 2),
          d = Math.max(10, o - n * 2);
        let l = r,
          u = l / i;
        return (
          u > d && ((u = d), (l = u * i)),
          { x: (t - l) / 2, y: (o - u) / 2, width: l, height: u }
        );
      }
      function kt(t, o) {
        return {
          x: t.x - o,
          y: t.y - o,
          width: t.width + o * 2,
          height: t.height + o * 2,
        };
      }
      function Tt(t, o) {
        const i = k();
        if (!i) return null;
        const n = i.widthInches / i.heightInches;
        if (e.productType === "canvas" && e.canvasStyle === "box") {
          return YI2CFloatFrame.frameGeometry(i.widthInches*25.4,i.heightInches*25.4,t,o,32);
        }
        if (e.productType === "poster" || e.canvasStyle === "unframed") {
          const S = He(t, o, n, 32);
          return { mode: "unframed", frontFace: S, outerRectangle: S };
        }
        const r = fa(),
          d = i.widthInches + r * 2,
          l = i.heightInches + r * 2,
          u = He(t, o, d / l, 32),
          p = u.width * (r / d),
          m = u.height * (r / l);
        return {
          mode: "wrapped",
          frontFace: {
            x: u.x + p,
            y: u.y + m,
            width: u.width - p * 2,
            height: u.height - m * 2,
          },
          outerRectangle: u,
          wrapInches: r,
        };
      }
      function syncPreviewGeometry() {
        if (!a.photoPlaceholder) return;
        const t = Lt(),
          o = Tt(t.width, t.height),
          i =
            o?.mode === "box" ? o.frontFace : o?.outerRectangle || o?.frontFace;
        if (!i) return;
        (h.style.setProperty("--yi2c-placeholder-x", `${i.x}px`),
          h.style.setProperty("--yi2c-placeholder-y", `${i.y}px`),
          h.style.setProperty("--yi2c-placeholder-width", `${i.width}px`),
          h.style.setProperty("--yi2c-placeholder-height", `${i.height}px`));
      }
      function renderSource() {
        return e.imageMethod === "separate" ? a.placeholderArtwork : e.image;
      }
      function renderSourceSize() {
        const t = renderSource();
        return e.imageMethod === "separate"
          ? { width: t?.naturalWidth || 0, height: t?.naturalHeight || 0 }
          : { width: e.naturalWidth, height: e.naturalHeight };
      }
      function We() {
        const t = renderSourceSize();
        return e.rotation % 180 !== 0
          ? { width: t.height, height: t.width }
          : t;
      }
      function Pt(t) {
        const o = We(),
          i = Math.max(t.width / o.width, t.height / o.height) * e.zoom,
          n = o.width * i,
          r = o.height * i,
          d = Math.max(0, (n - t.width) / 2),
          l = Math.max(0, (r - t.height) / 2);
        ((e.panX = F(e.panX, -d, d)), (e.panY = F(e.panY, -l, l)));
        const u = t.x + t.width / 2 + e.panX,
          p = t.y + t.height / 2 + e.panY;
        return {
          scale: i,
          centreX: u,
          centreY: p,
          displayedWidth: n,
          displayedHeight: r,
          imageLeft: u - n / 2,
          imageRight: u + n / 2,
          imageTop: p - r / 2,
          imageBottom: p + r / 2,
        };
      }
      function wa(t) {
        if (!O) {
          t.filter = "none";
          return;
        }
        if (e.printColour === "bw") {
          t.filter = "grayscale(1)";
          return;
        }
        if (e.printColour === "sepia") {
          t.filter = "sepia(.82) saturate(.78) contrast(.96)";
          return;
        }
        t.filter = "none";
      }
      function va(t, o) {
        for (let i = 0; i < t.length; i += 4) {
          const n = t[i],
            r = t[i + 1],
            d = t[i + 2];
          if (o === "bw") {
            const v = 0.2126 * n + 0.7152 * r + 0.0722 * d;
            ((t[i] = v), (t[i + 1] = v), (t[i + 2] = v));
            continue;
          }
          const l = 0.82,
            u = (1 - 0.607 * l) * n + 0.769 * l * r + 0.189 * l * d,
            p = 0.349 * l * n + (1 - 0.314 * l) * r + 0.168 * l * d,
            m = 0.272 * l * n + 0.534 * l * r + (1 - 0.869 * l) * d,
            S = 0.78,
            I =
              (0.213 + 0.787 * S) * u +
              (0.715 - 0.715 * S) * p +
              (0.072 - 0.072 * S) * m,
            x =
              (0.213 - 0.213 * S) * u +
              (0.715 + 0.285 * S) * p +
              (0.072 - 0.072 * S) * m,
            g =
              (0.213 - 0.213 * S) * u +
              (0.715 - 0.715 * S) * p +
              (0.072 + 0.928 * S) * m;
          ((t[i] = (I - 128) * 0.96 + 128),
            (t[i + 1] = (x - 128) * 0.96 + 128),
            (t[i + 2] = (g - 128) * 0.96 + 128));
        }
      }
      function Sa() {
        const t = renderSource();
        if (!t || e.printColour === "original" || O) return t;
        if (z.sourceImage === t && z.printColour === e.printColour && z.canvas)
          return z.canvas;
        const o = 2048,
          i = Math.max(1, t.naturalWidth || t.width),
          n = Math.max(1, t.naturalHeight || t.height),
          r = Math.min(1, o / Math.max(i, n)),
          d = document.createElement("canvas");
        ((d.width = Math.max(1, Math.round(i * r))),
          (d.height = Math.max(1, Math.round(n * r))));
        const l = d.getContext("2d", { willReadFrequently: !0 });
        try {
          l.drawImage(t, 0, 0, d.width, d.height);
          const u = l.getImageData(0, 0, d.width, d.height);
          (va(u.data, e.printColour), l.putImageData(u, 0, 0));
        } catch {
          return t;
        }
        return (
          (z = { sourceImage: t, printColour: e.printColour, canvas: d }),
          d
        );
      }
      let photoSampleCache = null;
      function photoSample(source, scale) {
        if (photoSampleCache?.source !== source) photoSampleCache = {source, levels:[source]};
        const levels = photoSampleCache.levels;
        const targetWidth = (source.naturalWidth || source.width) * scale;
        const targetHeight = (source.naturalHeight || source.height) * scale;
        let level = 0;
        while ((levels[level].naturalWidth || levels[level].width) > targetWidth * 2 &&
               (levels[level].naturalHeight || levels[level].height) > targetHeight * 2) {
          if (!levels[level+1]) {
            const previous = levels[level], canvas = document.createElement("canvas");
            canvas.width = Math.max(1,Math.round((previous.naturalWidth || previous.width)/2));
            canvas.height = Math.max(1,Math.round((previous.naturalHeight || previous.height)/2));
            const context = canvas.getContext("2d");
            context.imageSmoothingEnabled = true; context.imageSmoothingQuality = "high";
            context.drawImage(previous,0,0,canvas.width,canvas.height);
            levels.push(canvas);
          }
          level++;
        }
        return levels[level];
      }
      function Mt(t, o, i = 0, n = 0, r = null) {
        const transform = t.getTransform();
        const d = photoSample(Sa(), o.scale * Math.hypot(transform.a, transform.b)),
          l = renderSourceSize();
        if (!d || !l.width || !l.height) return;
        (t.save(),
          r && (t.beginPath(), t.rect(r.x, r.y, r.width, r.height), t.clip()),
          t.translate(o.centreX - i, o.centreY - n),
          t.rotate((e.rotation * Math.PI) / 180),
          wa(t),
          t.drawImage(
            d,
            (-l.width * o.scale) / 2,
            (-l.height * o.scale) / 2,
            l.width * o.scale,
            l.height * o.scale,
          ),
          t.restore(),
          (t.filter = "none"));
      }
      function photoDisplayDensity(
        transform,
        requested = window.devicePixelRatio || 1,
      ) {
        // Raster density changes only; geometry remains in the existing CSS coordinates.
        const limit = window.matchMedia("(min-width:900px)").matches ? requested : Math.min(requested, y.maximumPreviewPixelRatio);
        return Math.min(limit, 1 / transform.scale);
      }
      let photoFaceCache = null;
      function Oe(t, o, density = 1) {
        const source = Sa();
        const key = [t.x,t.y,t.width,t.height,o.scale,o.centreX,o.centreY,density,e.rotation,e.printColour].join("|");
        if (photoFaceCache?.source === source && photoFaceCache.key === key) return photoFaceCache.canvas;
        const canvas = photoFaceCache?.canvas || document.createElement("canvas");
        const width = Math.max(1,Math.round(t.width*density)), height = Math.max(1,Math.round(t.height*density));
        if (canvas.width !== width) canvas.width = width;
        if (canvas.height !== height) canvas.height = height;
        const context = canvas.getContext("2d");
        context.setTransform(1,0,0,1,0,0); context.clearRect(0,0,width,height);
        context.imageSmoothingEnabled = true; context.imageSmoothingQuality = "high";
        context.save(); context.setTransform(density,0,0,density,0,0);
        context.beginPath(); context.rect(0,0,t.width,t.height); context.clip();
        Mt(context,o,t.x,t.y); context.restore();
        photoFaceCache = {source,key,canvas};
        return canvas;
      }
      function At(t, o = null) {
        (s.save(),
          s.setLineDash([7, 5]),
          (s.lineWidth = 3),
          (s.strokeStyle = "rgba(24,35,48,.72)"),
          s.strokeRect(t.x + 0.5, t.y + 0.5, t.width - 1, t.height - 1),
          (s.lineWidth = 1.25),
          (s.strokeStyle = "rgba(255,255,255,.98)"),
          s.strokeRect(t.x + 0.5, t.y + 0.5, t.width - 1, t.height - 1),
          s.restore());
      }
      function ba(t, o, i) {
        const n = Math.max(0, i.x - o.x),
          r = Math.max(0, o.x + o.width - (i.x + i.width)),
          d = Math.max(0, i.y - o.y),
          l = Math.max(0, o.y + o.height - (i.y + i.height));
        if ((s.drawImage(t, i.x, i.y, i.width, i.height), n > 0)) {
          const u = Math.min(
            t.width,
            Math.max(
              1,
              Math.round(
                n *
                  (t.width / i.width),
              ),
            ),
          );
          (s.save(),
            s.translate(i.x, i.y),
            s.scale(-1, 1),
            s.drawImage(t, 0, 0, u, t.height, 0, 0, n, i.height),
            s.restore());
        }
        if (r > 0) {
          const u = Math.min(
            t.width,
            Math.max(
              1,
              Math.round(
                r *
                  (t.width / i.width),
              ),
            ),
          );
          (s.save(),
            s.translate(i.x + i.width, i.y),
            s.scale(-1, 1),
            s.drawImage(t, t.width - u, 0, u, t.height, -r, 0, r, i.height),
            s.restore());
        }
        if (d > 0) {
          const u = Math.min(
            t.height,
            Math.max(
              1,
              Math.round(
                d *
                  (t.height / i.height),
              ),
            ),
          );
          (s.save(),
            s.translate(i.x, i.y),
            s.scale(1, -1),
            s.drawImage(t, 0, 0, t.width, u, 0, 0, i.width, d),
            s.restore());
        }
        if (l > 0) {
          const u = Math.min(
            t.height,
            Math.max(
              1,
              Math.round(
                l *
                  (t.height / i.height),
              ),
            ),
          );
          (s.save(),
            s.translate(i.x, i.y + i.height),
            s.scale(1, -1),
            s.drawImage(t, 0, t.height - u, t.width, u, 0, -l, i.width, l),
            s.restore());
        }
      }
      function xa(t, o) {
        const i = t.outerRectangle,
          n = t.frontFace,
          r = Math.max(0, n.x - i.x),
          d = Math.max(0, i.x + i.width - (n.x + n.width)),
          l = Math.max(0, n.y - i.y),
          u = Math.max(0, i.y + i.height - (n.y + n.height)),
          p = { x: i.x, y: n.y, width: r, height: n.height },
          m = { x: n.x + n.width, y: n.y, width: d, height: n.height },
          S = { x: n.x, y: i.y, width: n.width, height: l },
          I = { x: n.x, y: n.y + n.height, width: n.width, height: u },
          x = [p, m, S, I];
        ((s.fillStyle = "#f0efec"), s.fillRect(i.x, i.y, i.width, i.height));
        const g = Oe(n, o, photoDisplayDensity(o));
        (s.drawImage(g, n.x, n.y, n.width, n.height),
          e.edgeStyle === "gallery"
            ? x.forEach((v) => {
                v.width <= 0 ||
                  v.height <= 0 ||
                  (s.save(),
                  s.beginPath(),
                  s.rect(v.x, v.y, v.width, v.height),
                  s.clip(),
                  Mt(s, o, 0, 0, v),
                  s.restore());
              })
            : e.edgeStyle === "mirror"
              ? ba(g, i, n)
              : e.edgeStyle === "white"
                ? (s.save(),
                  (s.fillStyle = "#ffffff"),
                  x.forEach((v) => {
                    v.width > 0 &&
                      v.height > 0 &&
                      s.fillRect(v.x, v.y, v.width, v.height);
                  }),
                  s.restore(),
                  s.drawImage(g, n.x, n.y, n.width, n.height))
                : (e.edgeStyle === "colour" || e.edgeStyle === "black") &&
                  (s.save(),
                  (s.fillStyle =
                    e.edgeStyle === "black"
                      ? "#000000"
                      : e.edgeColour || "#000000"),
                  x.forEach((v) => {
                    v.width > 0 &&
                      v.height > 0 &&
                      s.fillRect(v.x, v.y, v.width, v.height);
                  }),
                  s.restore(),
                  s.drawImage(g, n.x, n.y, n.width, n.height)),
          s.save(),
          (s.strokeStyle = "rgba(24,35,48,.18)"),
          (s.lineWidth = 1),
          s.strokeRect(i.x + 0.5, i.y + 0.5, i.width - 1, i.height - 1),
          s.restore(),
          At(n, i));
      }
      function Ca(t, o) {
        const i = t.frontFace,
          n = Oe(i, o, photoDisplayDensity(o));
        (s.drawImage(n, i.x, i.y, i.width, i.height),
          s.save(),
          (s.strokeStyle = "rgba(24,35,48,.22)"),
          (s.lineWidth = 1),
          s.strokeRect(i.x + 0.5, i.y + 0.5, i.width - 1, i.height - 1),
          s.restore());
      }
      function Ia(t, o) {
        YI2CFloatFrame.drawFrame(s,t,e.frameColour);
        const f=t.frontFace,face=Oe(f,o,photoDisplayDensity(o));
        s.drawImage(face,f.x,f.y,f.width,f.height);
      }
      function framedDisplayCanvas(width) {
        if (!e.geometry || !renderSource()) return null;
        const f=e.geometry.frontFace,transform=Pt(f),size=k();
        const density=width/(f.width*(size.widthInches*25.4+34)/(size.widthInches*25.4));
        const face=Oe(f,transform,Math.min(density,1/transform.scale));
        return YI2CFloatFrame.frameFace(face,size,e.frameColour,width);
      }
      function Ea(t, o) {
        if (
          ((e.galleryMissingSides = []),
          !t || t.mode !== "wrapped" || e.edgeStyle !== "gallery")
        )
          return;
        const i = 1.5,
          n = t.outerRectangle;
        (o.imageLeft > n.x + i && e.galleryMissingSides.push("left"),
          o.imageRight < n.x + n.width - i &&
            e.galleryMissingSides.push("right"),
          o.imageTop > n.y + i && e.galleryMissingSides.push("top"),
          o.imageBottom < n.y + n.height - i &&
            e.galleryMissingSides.push("bottom"));
      }
      function La() {
        const t = k();
        if (!t || !e.image || e.imageMethod === "separate") {
          ((e.estimatedPpi = 0), (e.studioReviewRecommended = !1));
          return;
        }
        const o = We(),
          i = o.width / e.zoom,
          n = o.height / e.zoom;
        ((e.estimatedPpi = Math.floor(
          Math.min(i / t.widthInches, n / t.heightInches),
        )),
          (e.studioReviewRecommended =
            e.estimatedPpi > 0 && e.estimatedPpi < y.internalReviewPpi));
      }
      function _t() {
        if (e.customMode) {
          const o = bt();
          if (!o.valid) {
            a.trustText.textContent =
              "Adjust the size above and your preview will update automatically.";
            return;
          }
          if (o.matchingStandardSize) {
            a.trustText.textContent = `Good news \u2014 ${o.matchingStandardSize.label} is a standard size, so we\u2019ve automatically used the lower standard price.`;
            return;
          }
        }
        if (e.imageMethod === "separate") {
          a.trustText.textContent =
            "All set \u2014 send your original after ordering and our studio will position and check it before print.";
          return;
        }
        if (e.designerCrop) {
          a.trustText.textContent =
            "Leave it with us \u2014 choose your size and finish and our studio will take care of the final crop.";
          return;
        }
        if (!e.image) return;
        if (e.canvasStyle === "box") {
          a.trustText.textContent =
            "Your canvas will float inside the frame with a 5mm recessed shadow gap.";
          return;
        }
        if (e.edgeStyle === "gallery" && e.galleryMissingSides.length) {
          a.trustText.textContent =
            e.productType === "canvas"
              ? "Blank edge? Our studio will extend the background without changing your crop."
              : "No worries if your image doesn’t cover every edge — our studio will carefully extend the background to complete any uncovered canvas sides, without changing the crop you’ve chosen.";
          return;
        }
        if (e.studioReviewRecommended) {
          a.trustText.textContent =
            "We\u2019ll give this image a little extra studio attention before it goes to print.";
          return;
        }
        const t = k();
        if (t) {
          a.trustText.textContent = e.productType === "canvas"
            ? "Great fit — we’ll give your photograph a final studio check before printing."
            : `Great fit \u2014 your photograph should look lovely at ${t.label}. We\u2019ll give it a final studio check before printing.`;
          return;
        }
        a.trustText.textContent =
          "Looking good \u2014 we\u2019ll check everything before it goes to print.";
      }
      function Ha(t) {
        if (t) {
          if ((s.save(), t.mode === "box")) {
            YI2CFloatFrame.drawFrame(s,t,e.frameColour);
            s.fillStyle='#ffffff';s.fillRect(t.frontFace.x,t.frontFace.y,t.frontFace.width,t.frontFace.height);
          } else
            t.mode === "unframed"
              ? ((s.fillStyle = "#ffffff"),
                s.fillRect(
                  t.frontFace.x,
                  t.frontFace.y,
                  t.frontFace.width,
                  t.frontFace.height,
                ),
                (s.strokeStyle = "rgba(24,35,48,.18)"),
                s.strokeRect(
                  t.frontFace.x + 0.5,
                  t.frontFace.y + 0.5,
                  t.frontFace.width - 1,
                  t.frontFace.height - 1,
                ))
              : (e.edgeStyle === "colour"
                  ? (s.fillStyle = e.edgeColour)
                  : e.edgeStyle === "black"
                    ? (s.fillStyle = "#000000")
                    : e.edgeStyle === "white"
                      ? (s.fillStyle = "#ffffff")
                      : (s.fillStyle = "#dfe2e4"),
                s.fillRect(
                  t.outerRectangle.x,
                  t.outerRectangle.y,
                  t.outerRectangle.width,
                  t.outerRectangle.height,
                ),
                (s.fillStyle = "#ffffff"),
                s.fillRect(
                  t.frontFace.x,
                  t.frontFace.y,
                  t.frontFace.width,
                  t.frontFace.height,
                ),
                At(t.frontFace));
          s.restore();
        }
      }
      function D() {
        if (!renderSource()) return;
        const t = Lt();
        s.clearRect(0, 0, t.width, t.height);
        const o = Tt(t.width, t.height);
        const previous=e.geometry;
        if(previous&&o&&(previous.mode==='box'||o.mode==='box')){
          e.panX*=o.frontFace.width/previous.frontFace.width;
          e.panY*=o.frontFace.height/previous.frontFace.height;
        }
        if (((e.geometry = o), !o || !renderSource())) return;
        const i = Pt(o.frontFace);
        (o.mode === "box"
          ? Ia(o, i)
          : o.mode === "unframed"
            ? Ca(o, i)
            : xa(o, i),
          Ea(o, i),
          La(),
          _t());
      }
      const zt = new Set();
      let j = "",
        $e = null;
      function ka(t, o, i) {
        if (!t) return;
        let n = t.querySelector(":scope > .yi2c-mobile-sheet-header");
        (n ||
          ((n = document.createElement("div")),
          (n.className = "yi2c-mobile-sheet-header"),
          (n.innerHTML =
            '<strong></strong><button type="button">Done</button>'),
          t.prepend(n)),
          (n.querySelector("button").onclick = () => Y(!0)),
          (n.querySelector("strong").textContent = o),
          ($e = i || null),
          requestAnimationFrame(() => n.querySelector("button").focus()));
      }
      function Y(t = !1) {
        (window.matchMedia("(max-width: 899px)").matches &&
          a.customPanel.contains(document.activeElement) &&
          document.activeElement.blur(),
          t && j && zt.add(j),
          w(".is-mobile-sheet-open").forEach((i) =>
            i.classList.remove("is-mobile-sheet-open"),
          ),
          w(".is-mobile-comment-sheet").forEach((i) =>
            i.classList.remove("is-mobile-comment-sheet"),
          ),
          h.classList.remove("is-mobile-product-open"),
          a.mobileProductTrigger?.setAttribute("aria-expanded", "false"),
          a.mobileCommentButton?.classList.remove("is-comment-open"),
          a.mobileCommentButton?.setAttribute("aria-expanded", "false"),
          a.mobileCommentButton?.setAttribute("aria-label", "Add a comment"),
          (j = ""),
          a.mobileSheetBackdrop && (a.mobileSheetBackdrop.hidden = !0),
          je());
        const o = $e;
        (($e = null), o?.focus());
      }
      function je() {
        a.mobileProductLabel &&
          (a.mobileProductLabel.textContent =
            e.productType === "poster" ? "Poster" : "Canvas");
        const t = k();
        ((W.textContent = t ? `W ${B(t.widthInches)}\u2033` : ""),
          (T.textContent = t ? `H ${B(t.heightInches)}\u2033` : ""),
          window.matchMedia("(max-width: 899px)").matches &&
            (a.toggleAllSizesButton &&
              (a.toggleAllSizesButton.textContent = a.allSizes.hidden
                ? "View all sizes"
                : "Hide all sizes"),
            a.toggleCustomSizeButton &&
              a.customPanel.hidden &&
              (a.toggleCustomSizeButton.textContent = "Made to your size")));
        const o = {
          size: t?.label || "Choose",
          canvas:
            {
              slim: "Slim",
              chunky: "Chunky",
              box: "Floating frame",
              unframed: "Unframed",
            }[e.canvasStyle] || "Choose",
          sides:
            {
              gallery: "Gallery",
              mirror: "Mirror",
              white: "White",
              colour: "Colour",
            }[e.edgeStyle] || "Choose",
          photo:
            { original: "Original", bw: "B&W", sepia: "Sepia" }[
              e.printColour
            ] || "Choose",
        };
        (a.mobileBoxValues.forEach((i) => {
          i.textContent = o[i.dataset.mobileBoxValue] || "";
        }),
          a.mobileSheetTriggers.forEach((i) =>
            i.classList.toggle(
              "is-confirmed",
              zt.has(i.dataset.mobileSheetTrigger),
            ),
          ),
          a.mobileHangingButton?.classList.toggle(
            "is-selected",
            !!e.hangingKit,
          ),
          a.mobileCommentButton?.classList.toggle(
            "is-selected",
            !!e.orderNotes.trim(),
          ));
      }
      function C() {
        (syncExactSizeFields(),
          Ct(),
          e.imageMethod === "separate" &&
            ((e.zoom = 1),
            (e.panX = 0),
            (e.panY = 0),
            (e.rotation = 0),
            (a.zoomSlider.value = "1"),
            e.productType === "canvas" &&
              e.edgeStyle === "gallery" &&
              (e.canvasStyle === "slim" || e.canvasStyle === "chunky") &&
              q()),
          It(),
          Et(),
          Be(),
          qt(),
          G(),
          je(),
          _e(),
          we(),
          D());
      }
      function Nt(t) {
        requestAnimationFrame(() => {
          (C(),
            requestAnimationFrame(() => {
              (e.productType === "canvas" &&
                e.edgeStyle === "gallery" &&
                (e.canvasStyle === "slim" || e.canvasStyle === "chunky") &&
                q(),
                C(),
                t && t());
            }));
        });
      }
      function Bt(t, o = {}) {
        const i = !!o.preserveSpecification || !!offerPreset || !!priceEntry,
          n = !!o.wasSendingSeparately;
        ((e.image = t),
          (e.imageMethod = "upload"),
          (e.naturalWidth = t.naturalWidth),
          (e.naturalHeight = t.naturalHeight),
          (e.originalNaturalWidth = o.originalWidth || t.naturalWidth),
          (e.originalNaturalHeight = o.originalHeight || t.naturalHeight),
          (e.retainedUploadState = null),
          (e.rotation = 0),
          (e.zoom = 1),
          (e.panX = 0),
          (e.panY = 0),
          (a.zoomSlider.value = "1"),
          e.file && Ae(e.file, "Original safely uploaded. Preparing preview…"),
          n && ((e.designerCrop = !1), (a.designerCropInput.checked = !1)),
          i ||
            (ca(),
            (e.stepTouched.size = !0),
            (e.stepTouched.canvas = !1),
            (e.stepTouched.sides = !1),
            (e.stepTouched.photo = !1),
            (e.customMode = !1),
            (e.customMatchedStandardSize = null)),
          (a.uploadScreen.hidden = !0),
          (a.editor.hidden = !1),
          G(),
          ge());
      }
      function Rt() {
        ((e.zoom = 1),
          (e.panX = 0),
          (e.panY = 0),
          a.zoomSlider && (a.zoomSlider.value = "1"),
          e.productType === "canvas" &&
            e.edgeStyle === "gallery" &&
            (e.canvasStyle === "slim" || e.canvasStyle === "chunky") &&
            q(),
          D());
      }
      function Ce(t) {
        if (e.designerCrop || e.imageMethod === "separate") return;
        const o = Number(t);
        Number.isFinite(o) &&
          ((e.zoom = F(o, y.minimumZoom, y.maximumZoom)),
          (a.zoomSlider.value = String(e.zoom)),
          D());
      }
      function Ot(t) {
        const o = a.previewCanvas.getBoundingClientRect();
        return { x: t.clientX - o.left, y: t.clientY - o.top };
      }
      function Ut(t, o) {
        return Math.hypot(t.x - o.x, t.y - o.y);
      }
      function Ke(t) {
        const o = String(t || "")
          .trim()
          .toUpperCase();
        return /^#[0-9A-F]{6}$/.test(o) ? o : null;
      }
      function Ta(t) {
        const o = ((Number(t) % 360) + 360) % 360,
          i = 0.62,
          n = 0.48,
          r = i * Math.min(n, 1 - n),
          d = (l) => {
            const u = (l + o / 30) % 12,
              p = n - r * Math.max(-1, Math.min(u - 3, 9 - u, 1));
            return Math.round(255 * p)
              .toString(16)
              .padStart(2, "0");
          };
        return `#${d(0)}${d(8)}${d(4)}`.toUpperCase();
      }
      function Pa(t) {
        const o = Ke(t);
        if (!o) return 0;
        const i = parseInt(o.slice(1, 3), 16) / 255,
          n = parseInt(o.slice(3, 5), 16) / 255,
          r = parseInt(o.slice(5, 7), 16) / 255,
          d = Math.max(i, n, r),
          l = Math.min(i, n, r),
          u = d - l;
        if (!u) return 0;
        const p =
          d === i
            ? ((n - r) / u) % 6
            : d === n
              ? (r - i) / u + 2
              : (i - n) / u + 4;
        return Math.round((p * 60 + 360) % 360);
      }
      function ye() {
        const t = Ke(e.edgeColour) || "#000000";
        ((e.edgeColour = t),
          a.edgeColourInput && (a.edgeColourInput.value = t),
          a.edgeColourHexInput && (a.edgeColourHexInput.value = t),
          a.edgeColourHueInput && (a.edgeColourHueInput.value = String(Pa(t))),
          a.edgeColourSwatch && (a.edgeColourSwatch.style.backgroundColor = t));
      }
      function Ue() {
        ((e.samplingEdgeColour = !1),
          a.previewCanvas.classList.remove("is-colour-sampling"),
          a.edgeColourPickStatus && (a.edgeColourPickStatus.hidden = !0),
          a.edgeColourPickButton?.setAttribute("aria-pressed", "false"));
      }
      function Ma() {
        !e.image ||
          e.productType !== "canvas" ||
          e.edgeStyle !== "colour" ||
          ((e.samplingEdgeColour = !0),
          a.previewCanvas.classList.add("is-colour-sampling"),
          a.edgeColourPickStatus && (a.edgeColourPickStatus.hidden = !1),
          a.edgeColourPickButton?.setAttribute("aria-pressed", "true"));
      }
      function Aa(t) {
        const o = e.geometry?.frontFace;
        if (
          !o ||
          t.x < o.x ||
          t.y < o.y ||
          t.x >= o.x + o.width ||
          t.y >= o.y + o.height
        )
          return !1;
        const i = Pt(o),
          n = Oe(o, i),
          r = n.getContext("2d", { willReadFrequently: !0 }),
          d = F(Math.floor(t.x - o.x), 0, n.width - 1),
          l = F(Math.floor(t.y - o.y), 0, n.height - 1),
          u = r.getImageData(d, l, 1, 1).data;
        return (
          (e.edgeColour =
            `#${[u[0], u[1], u[2]].map((p) => p.toString(16).padStart(2, "0")).join("")}`.toUpperCase()),
          ye(),
          Ue(),
          D(),
          !0
        );
      }
      function _a(t) {
        if (
          !e.image ||
          !e.geometry ||
          e.designerCrop ||
          e.imageMethod === "separate"
        )
          return;
        const o = Ot(t);
        if (e.samplingEdgeColour) {
          (t.preventDefault(), Aa(o));
          return;
        }
        if (
          (a.previewCanvas.setPointerCapture &&
            a.previewCanvas.setPointerCapture(t.pointerId),
          e.pointers.set(t.pointerId, o),
          e.pointers.size === 1)
        ) {
          ((e.dragStart = { pointer: o, panX: e.panX, panY: e.panY }),
            (e.pinchStart = null));
          return;
        }
        if (e.pointers.size === 2) {
          const i = [...e.pointers.values()];
          ((e.pinchStart = { distance: Ut(i[0], i[1]), zoom: e.zoom }),
            (e.dragStart = null));
        }
      }
      function za(t) {
        if (!e.pointers.has(t.pointerId)) return;
        const o = Ot(t);
        if (
          (e.pointers.set(t.pointerId, o), e.pointers.size === 1 && e.dragStart)
        ) {
          ((e.panX = e.dragStart.panX + (o.x - e.dragStart.pointer.x)),
            (e.panY = e.dragStart.panY + (o.y - e.dragStart.pointer.y)),
            D());
          return;
        }
        if (e.pointers.size === 2 && e.pinchStart) {
          const i = [...e.pointers.values()],
            n = Ut(i[0], i[1]);
          if (!e.pinchStart.distance) return;
          const r = n / e.pinchStart.distance;
          Ce(e.pinchStart.zoom * r);
        }
      }
      function Dt(t) {
        if ((e.pointers.delete(t.pointerId), e.pointers.size === 1)) {
          const o = [...e.pointers.values()][0];
          ((e.dragStart = { pointer: o, panX: e.panX, panY: e.panY }),
            (e.pinchStart = null));
          return;
        }
        ((e.dragStart = null), (e.pinchStart = null));
      }
      function Ve() {
        a.fileInput.click();
      }
      function Ft(t) {
        if (t && t === e.file && ["uploading", "preparing"].includes(e.photoPreviewPhase)) return;
        if (!t) return;
        const nativePreview=/\.(tiff?|heic|heif)$/i.test(t.name);e.workingPreviewUrl="";e.originalContentType=t.type||"";
        if (t.size > 250 * 1024 * 1024) {
          ((a.fileInput.value = ""),
            ht(
              "This file is over 250 MB",
              "Choose a JPG or PNG up to 250 MB, or send the original using our Send a large file option.",
            ));
          return;
        }
        const o = e.imageMethod === "separate",
          i =
            e.configurationId && e.recoveryToken
              ? U("uploads/cancel", {
                  configurationId: e.configurationId,
                  recoveryToken: e.recoveryToken,
                  kind: "ORIGINAL",
                }).catch(() => {})
              : Promise.resolve(),
          n = ee();
        ((!n || n !== e.editingConfigurationId) && N(),
          e.activeUploadRequests.forEach((u) => u.abort()),
          e.activeUploadRequests.clear());
        const r = e.uploadGeneration + 1;
        ((e.uploadGeneration = r),
          photoUploadPhase("uploading", "Uploading your photograph…"),
          (e.file = t),
          (e.image = null),
          (e.imageMethod = "upload"));
        const d = i.then(() => oa(t, r));
        ((e.uploadPromise = d.catch(() => null)),
          e.objectUrl && URL.revokeObjectURL(e.objectUrl),
          (e.objectUrl = nativePreview ? "" : URL.createObjectURL(t)),
          a.uploadedThumbnail && (a.uploadedThumbnail.src = e.objectUrl));
        const l = new Image();
        let originalWidth = 0, originalHeight = 0, installed = false;
        const current = () => r === e.uploadGeneration;
        const failPreview = () => {
          if (!current()) return;
          photoUploadPhase("failed", "Your original is safely uploaded, but its preview could not be prepared. Retry photograph or choose another file.");
        };
        const install = () => {
          if (!current() || installed) return;
          installed = true;
          Bt(l, {wasSendingSeparately:o, originalWidth, originalHeight});
          Nt(() => {
            if (!current()) return;
            photoUploadPhase("ready", "Preview ready · Original safely uploaded");
            Be();
          });
        };
        l.onload = async () => {
          if (!current()) return;
          if (!l.dataset.bounded) {
            originalWidth = l.naturalWidth; originalHeight = l.naturalHeight;
          }
          const max = Math.max(l.naturalWidth,l.naturalHeight);
          if (max > 4096 && !l.dataset.bounded) {
            const factor = 4096/max, canvas = document.createElement("canvas");
            canvas.width = Math.max(1,Math.round(l.naturalWidth*factor));
            canvas.height = Math.max(1,Math.round(l.naturalHeight*factor));
            const context = canvas.getContext("2d");
            context.imageSmoothingQuality = "high";
            context.drawImage(l,0,0,canvas.width,canvas.height);
            canvas.toBlob(blob => {
              if (!current()) return;
              if (!blob) { d.then(failPreview).catch(()=>{}); return; }
              URL.revokeObjectURL(e.objectUrl);
              e.objectUrl = URL.createObjectURL(blob); l.dataset.bounded = "true"; l.src = e.objectUrl;
            },"image/jpeg",.9);
            return;
          }
          try { await d; if (current()) install(); } catch {}
        };
        l.onerror = async () => {
          try { await d; failPreview(); } catch {}
        };
        if (!nativePreview) l.src = e.objectUrl;
        else d.then(async result => {
          if (!current()) return;
          photoUploadPhase("preparing", "Original safely uploaded. Preparing preview…");
          const preview = await U("preview/status",{configurationId:e.configurationId,recoveryToken:e.recoveryToken});
          if (!current()) return;
          if (preview.status !== "READY" || !preview.workingPreviewUrl) throw Error("UPLOAD_PREVIEW_CONVERSION_FAILED");
          e.workingPreviewUrl = preview.workingPreviewUrl; e.originalContentType = result.verifiedContentType;
          originalWidth = result.pixelWidth; originalHeight = result.pixelHeight;
          l.dataset.bounded = "true"; l.src = preview.workingPreviewUrl;
          if (a.uploadedThumbnail) a.uploadedThumbnail.src = preview.workingPreviewUrl;
          if (result.notice) K(result.notice);
        }).catch(() => { if (current() && e.originalUploadStatus === "verified") failPreview(); });
      }

      function ie(t) {
        ((a.customPanel.hidden = !t),
          (a.toggleCustomSizeButton.textContent = t
            ? "Hide custom size"
            : "Made to your size"));
      }
      function Na() {
        if (!a.customPanel.hidden) {
          if (e.customMode && window.matchMedia("(min-width: 900px)").matches)
            return;
          (ie(!1), C());
          return;
        }
        const t = k();
        ((e.customMode = !0),
          ie(!0),
          (a.allSizes.hidden = !0),
          (a.toggleAllSizesButton.textContent = "All sizes"),
          (!e.customWidth || !e.customHeight) &&
            t &&
            (e.customUnit === "cm"
              ? ((e.customWidth = Number((t.widthInches * 2.54).toFixed(1))),
                (e.customHeight = Number((t.heightInches * 2.54).toFixed(1))))
              : ((e.customWidth = Number(t.widthInches.toFixed(1))),
                (e.customHeight = Number(t.heightInches.toFixed(1)))),
            (a.customWidthInput.value = e.customWidth),
            (a.customHeightInput.value = e.customHeight)),
          oe(),
          C());
      }
      function Ba(t) {
        if (t === e.customUnit) return;
        // Invalid entries may have been typed in the intended new unit.
        const convertDimensions = !e.customMode || bt().valid;
        const o = Number(e.customWidth),
          i = Number(e.customHeight);
        (convertDimensions &&
          o &&
          i &&
          (t === "cm"
            ? ((e.customWidth = Number((o * 2.54).toFixed(1))),
              (e.customHeight = Number((i * 2.54).toFixed(1))))
            : ((e.customWidth = Number((o / 2.54).toFixed(1))),
              (e.customHeight = Number((i / 2.54).toFixed(1)))),
          (a.customWidthInput.value = e.customWidth),
          (a.customHeightInput.value = e.customHeight)),
          (e.customUnit = t),
          [a.customWidthInput, a.customHeightInput].forEach((input) => {
            input.min = t === "cm" ? "12.7" : "5";
          }),
          w("[data-unit]").forEach((n) => {
            n.classList.toggle("is-selected", n.dataset.unit === t);
          }),
          a.customUnitLabels.forEach((n) => {
            n.textContent = t === "cm" ? "cm" : '"';
          }),
          C());
      }
// Presentation only: never infer a preset from dimensions or alter the snapshot.
function posterSizeLabel(spec) {
  if (String(spec.productType).toLowerCase() !== 'poster') return '';
  const width = Number(spec.widthInches), height = Number(spec.heightInches);
  if (![width, height].every(value => Number.isFinite(value) && value > 0)) return '';
  const cm = value => String(Math.round((value * 2.54 + Number.EPSILON) * 100) / 100);
  const id = String(spec.standardSizeId || '').toUpperCase();
  const name = spec.customSize !== true && /^A[0-5]$/.test(id) ? id + ' · ' : '';
  return name + cm(width) + ' × ' + cm(height) + ' cm';
}

      function Ge() {
        const t = k();
        if (t && e.productType === "poster") return posterSizeLabel({productType:"poster",widthInches:t.widthInches,heightInches:t.heightInches,standardSizeId:e.selectedStandardSizeId,customSize:e.customMode});
        return t
          ? e.customMode && e.customMatchedStandardSize
            ? `${t.label} (standard price applied)`
            : t.label
          : "";
      }
      function Ye() {
        return (
          {
            slim: 'Slim 0.75"',
            chunky: 'Chunky 1.75"',
            box: "Floating frame · " + ({black:'Black',white:'White',darkwood:'Dark wood',barewood:'Bare wood'}[e.frameColour] || 'Colour not recorded'),
            unframed: "Rolled/unframed print",
          }[e.canvasStyle] || e.canvasStyle
        );
      }
      function Xe() {
        return (
          {
            gallery: "Gallery wrap",
            mirror: "Mirror sides",
            white: "White sides",
            black: "Black sides",
            colour: "Colour sides",
          }[e.edgeStyle] || e.edgeStyle
        );
      }
      function De() {
        return (
          { original: "Original", bw: "Black & white", sepia: "Sepia" }[
            e.printColour
          ] || e.printColour
        );
      }
      function qt() {
        if (!a.orderSummary) return;
        const t = k(),
          o = [
            ["Product", e.productType === "poster" ? "Poster" : "Canvas"],
            ["Size", t?.label || "\u2014"],
          ];
        (e.productType === "canvas" && o.push(["Style", Ye()]),
          e.productType === "canvas" &&
            e.canvasStyle !== "unframed" &&
            o.push(["Sides", Xe()]),
          o.push(["Colour treatment", De()]),
          e.productType === "canvas" &&
            o.push(["Hanger", e.hangingKit ? "Added" : "Not added"]),
          e.orderNotes.trim() && o.push(["Comment", "Added"]),
          o.push(["Price", a.totalPrice.textContent.trim() || "\u2014"]));
        const i = document.createDocumentFragment();
        (o.forEach(([n, r]) => {
          const d = document.createElement("span");
          ((d.className = "yi2c-order-summary__label"), (d.textContent = n));
          const l = document.createElement("span");
          ((l.className = "yi2c-order-summary__value"),
            (l.textContent = r),
            i.append(d, l));
        }),
          a.orderSummary.replaceChildren(i));
      }
      function Ra() {
        if (!e.geometry || !e.image || e.imageMethod === "separate")
          return null;
        const t = e.geometry.frontFace;
        return {
          zoom: Number(e.zoom.toFixed(4)),
          panXRelative: Number((e.panX / t.width).toFixed(6)),
          panYRelative: Number((e.panY / t.height).toFixed(6)),
          rotationDegrees: e.rotation,
          naturalWidthPixels: e.naturalWidth,
          naturalHeightPixels: e.naturalHeight,
          estimatedPpi: e.estimatedPpi,
          studioReviewRecommended: e.studioReviewRecommended,
          galleryMissingSides: e.galleryMissingSides,
        };
      }
      function K(t, o = "error") {
        const role = o === "error" && t ? "alert" : "status";
        a.cartStatus.setAttribute("role", role);
        if (!a.mobileCartFeedback) {
          a.mobileCartFeedback = document.createElement("p");
          a.mobileCartFeedback.dataset.mobileCartFeedback = "";
          a.mobileCartFeedback.setAttribute("aria-atomic", "true");
          h.append(a.mobileCartFeedback);
        }
        a.mobileCartFeedback.setAttribute("role", role);
        a.mobileCartFeedback.textContent = t;
        a.mobileCartFeedback.hidden = !t;
        ((a.cartStatus.textContent = t),
          a.cartStatus.classList.toggle("is-success", o === "success"),
          a.cartStatus.classList.toggle("is-error", o === "error" && !!t));
      }
      function Oa(t) {
        const o = t instanceof Error ? t.message : "";
        return (
          {
            CANVAS_BELOW_MINIMUM:
              "This canvas size is below the supported minimum. Please choose a larger size.",
            CANVAS_ABOVE_MAXIMUM:
              "This canvas size is above the supported maximum. Please choose a smaller size.",
            POSTER_BELOW_MINIMUM:
              "This poster size is below the supported minimum. Please choose a larger size.",
            POSTER_BELOW_A5_MINIMUM:
              "This poster size is below the supported minimum. Please choose a larger size.",
            POSTER_ABOVE_MAXIMUM:
              "This poster size is above the supported maximum. Please choose a smaller size.",
            STANDARD_CANVAS_DIMENSIONS_MISMATCH:
              "That standard canvas size could not be validated. Please reselect the size and try again.",
            STANDARD_POSTER_DIMENSIONS_MISMATCH:
              "That standard poster size could not be validated. Please reselect the size and try again.",
            UNKNOWN_STANDARD_CANVAS_SIZE:
              "That canvas size is not available. Please choose another size.",
            INVALID_DIMENSIONS:
              "That print size is not valid. Please review the dimensions and try again.",
            PRIVATE_POSTER_SESSION_REQUIRED:
              "This private review session has expired. Please reopen your authorised Poster preview link.",
            PRIVATE_POSTER_APPROVAL_UNAVAILABLE:
              "Your private print could not be authorised yet. Your image is saved; please try again.",
            INVALID_CANVAS_STYLE:
              "That canvas finish is not valid. Please choose another finish.",
            INVALID_EDGE_STYLE:
              "That edge finish is not valid. Please choose another finish.",
            INVALID_PRINT_COLOUR:
              "That colour option is not valid. Please choose another option.",
            INVALID_HANGING_KIT:
              "That hanging-kit option is not valid. Please review your choice.",
            POSTER_CANNOT_HAVE_HANGING_KIT:
              "A hanging kit is not available for poster prints. Please review your choices.",
          }[o] ||
          be(
            t,
            "Could not validate this print configuration. Please review your choices and try again.",
          )
        );
      }
      function Ze(t) {
        K(t, "neutral");
      }
      function Ua(t) {
        const o = Array.isArray(t?.items) ? t.items[0] : t,
          i =
            o?.properties && typeof o.properties == "object"
              ? o.properties
              : {},
          n = Object.entries(i).map(([r, d]) => ({
            name: r,
            type: d === null ? "object" : typeof d,
            length: typeof d == "string" || Array.isArray(d) ? d.length : 0,
            empty: d === "",
            null: d === null,
            undefined: d === void 0,
          }));
        return {
          topLevelKeys: Object.keys(t || {}),
          itemCount: Array.isArray(t?.items) ? t.items.length : 1,
          itemKeys: Object.keys(o || {}),
          propertyCount: Object.keys(i).length,
          propertyKeys: Object.keys(i),
          variantId: o?.id,
          quantity: o?.quantity,
          payloadBytes: new TextEncoder().encode(JSON.stringify(t)).length,
          propertyDetails: n,
        };
      }
      async function Je(t) {
        const o = h.dataset.yi2cUploadVerificationUrl;
        if (!o) return;
        const i = new URL("/api/cart-failure-diagnostic", o).toString();
        try {
          await fetch(i, {
            method: "POST",
            mode: "cors",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(t),
            keepalive: !0,
          });
        } catch {}
      }
      window.addEventListener("pageshow", (event) => {
        if (event.persisted && e.image && e.imageMethod === "upload" && !hasVerifiedOriginal() && e.originalUploadStatus !== "uploading") {
          const message = be(new Error("UPLOAD_ORIGINAL_REQUIRED"));
          a.uploadStatus.textContent = message;
          K(message);
          C();
        }
      });
      async function Ht() {
        if (e.addingToCart) return;
        if (e.imageMethod !== "separate" && e.photoPreviewPhase && e.photoPreviewPhase !== "ready") { K("Please finish preparing your photograph before adding to basket."); return; }
        K("", "neutral");
        const t = !!e.image || e.imageMethod === "separate";
        if (!Be() || !t) {
          K("Choose a valid size before adding to basket.");
          return;
        }
        const o = Number(h.dataset.variantId);
        if (!o) {
          K(
            "The canvas product isn\u2019t connected to a Shopify variant yet.",
          );
          return;
        }
        e.addingToCart = !0;
        const i = a.addToCartButton.textContent,
          mobileLabel = a.mobileAddButton.textContent,
          pendingLabel = e.editingConfigurationId
            ? "Updating basket\u2026"
            : "Adding to basket\u2026";
        ((a.addToCartButton.disabled = !0),
          (a.mobileAddButton.disabled = !0),
          (a.addToCartButton.textContent = pendingLabel),
          (a.mobileAddButton.textContent = pendingLabel),
          a.addToCartButton.setAttribute("aria-busy", "true"),
          a.mobileAddButton.setAttribute("aria-busy", "true"));
        const n = () => {
          ((e.addingToCart = !1),
            (a.addToCartButton.textContent = i),
            (a.mobileAddButton.textContent = mobileLabel),
            a.addToCartButton.removeAttribute("aria-busy"),
            a.mobileAddButton.removeAttribute("aria-busy"),
            Be());
        };
        Ze("Preparing your image\u2026");
        try {
          if (
            e.imageMethod === "upload" &&
            (e.uploadPromise && (await e.uploadPromise),
            !hasVerifiedOriginal())
          )
            throw new Error("UPLOAD_ORIGINAL_REQUIRED");
        } catch (x) {
          (K(be(x, "Could not finish uploading your image. Please try again.")),
            n());
          return;
        }
        try {
          await pt();
          await requireOriginalForBasket();
        } catch (x) {
          (K(x?.message === "UPLOAD_ORIGINAL_REQUIRED" ? be(x) : Oa(x)), n());
          return;
        }
        if (e.image && e.imageMethod !== "separate") {
          Ze("Preparing your image\u2026");
          try {
            if(e.workingPreviewUrl&&!e.originalArtworkUrl){const recovered=await U("configurations/recover",{configurationId:e.configurationId,recoveryToken:e.recoveryToken});e.originalArtworkUrl=recovered.originalArtworkUrl||"";if(!e.originalArtworkUrl)throw Error("ORIGINAL_REFERENCE_UNAVAILABLE");}
            const x = await (e.canvasStyle==='box'&&e.productType==='canvas'&&renderSource()?na():e.workingPreviewUrl ? completeConvertedReference() : na()),
              g = new File([x], "crop-reference.jpg", { type: "image/jpeg" });
            await mt(g, "CROP_REFERENCE");
          } catch (x) {
            (K(
              be(
                x,
                "We couldn't prepare the image this time. Your photo and design are still here \u2014 select Review and add to basket to retry.",
              ),
            ),
              n());
            return;
          }
        }
        Ze("Preparing your basket preview\u2026");
        const r = k(),
          d = Ra(),
          l = e.hangingKit
            ? Math.max(r.widthInches, r.heightInches) >
              y.hangingKitPairThresholdInches
              ? "Pair"
              : "Single"
            : "No",
          u =
            e.imageMethod === "separate" || e.designerCrop
              ? "Designer to position"
              : "Customer positioned",
          p =
            e.imageMethod === "separate"
              ? "Artwork to be supplied separately"
              : "\u2713 Artwork securely received",
          b =
            e.imageMethod === "separate"
              ? "Image supplied separately"
              : "Artwork securely received",
          m = {
            Size: e.productType === "canvas" ? `${Ge()} \xB7 ${Ye()}` : Ge(),
            Finish: e.productType === "canvas" ? `${Xe()} \xB7 ${De()}` : De(),
            Artwork: b,
            ...(e.designerCrop
              ? { Positioning: "Studio positioning selected" }
              : {}),
            _yi2c_summary_version: "2",
            _yi2c_product_type: e.productType,
            _yi2c_three_for_two_eligible: "Yes \u2014 Canvas + Posters",
            _yi2c_canvas_size: Ge(),
            _yi2c_canvas_style: Ye(),
            _yi2c_print_colour: De(),
            _yi2c_hanging_kit: l,
            _yi2c_studio_crop_help: e.designerCrop ? "Yes" : "No",
            _yi2c_designer_crop: e.designerCrop ? "Yes" : "No",
            _yi2c_crop_handling: u,
            _yi2c_artwork_status: p,
            _yi2c_image:
              e.imageMethod === "separate"
                ? "Send separately after ordering"
                : "\u2713 Successfully uploaded",
            _yi2c_file:
              e.imageMethod === "separate"
                ? "To be supplied"
                : e.cleanedOriginalFilename || "Uploaded image",
            _yi2c_display_price_non_authoritative: e.totalPrice.toFixed(2),
            _yi2c_base_price: e.basePrice.toFixed(2),
            _yi2c_custom_handling_price: e.customHandlingPrice.toFixed(2),
            _yi2c_box_frame_extra: e.boxFrameExtra.toFixed(2),
            _yi2c_delivery_extra: e.deliveryExtra.toFixed(2),
            _yi2c_width_in: r.widthInches.toFixed(4),
            _yi2c_height_in: r.heightInches.toFixed(4),
            _yi2c_width_cm: (r.widthInches * 2.54).toFixed(2),
            _yi2c_height_cm: (r.heightInches * 2.54).toFixed(2),
            _yi2c_original_file: e.cleanedOriginalFilename,
            _yi2c_original_type: e.originalContentType||"",
            _yi2c_working_preview_url: e.workingPreviewUrl||"",
            _yi2c_original_source_url: e.originalArtworkUrl||"",
            _yi2c_crop_data: JSON.stringify(d),
            _yi2c_upload_status:
              e.imageMethod === "separate"
                ? "send-separately-pending"
                : "original-verified",
            _yi2c_configuration_id: e.configurationId,
            _yi2c_edit_credential: e.recoveryToken,
          };
        let C = h.dataset.yi2cCheckoutThumbnailUrl;
        if (e.imageMethod !== "separate") {
          try {
            for (let attempt = 0; attempt < 12; attempt++) {
              const preview = await U("single-photo-checkout-preview", {
                configurationId: e.configurationId,
                recoveryToken: e.recoveryToken,
                _singlePhotoPreviewKey:
                  sessionStorage.getItem("yi2c-single-photo-preview-key") || "",
              });
              if (preview.imageUrl) {
                C = preview.imageUrl;
                break;
              }
              if (!preview.pending) break;
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          } catch (error) {
            if(e.workingPreviewUrl){K('Your checkout preview is still being prepared. Please retry.');n();return;}
            /* Preserve the existing generic thumbnail and valid artwork/cart flow. */
          }
        }
        if(e.workingPreviewUrl&&!/^https:\/\/cdn\.shopify\.com\/s\/files\//.test(C||'')){K('Your checkout preview is still being prepared. Please retry.');n();return;}
        C &&
          (m._yi2c_checkout_thumbnail_url = new URL(
            C,
            window.location.href,
          ).href);
        const T =
          e.imageMethod === "separate"
            ? a.placeholderArtwork?.currentSrc || a.placeholderArtwork?.src
            : e.workingPreviewUrl ? C : e.cropReferenceUrl;
        T && (m._yi2c_crop_reference_url = T);
        const S = ["a5", "a4", "a3", "a2", "a1"],
          I = {
            configurationId: e.configurationId,
            pricingVersion: e.posterDesktopV2 && e.productType==='poster'?window.YI2CPosterDesktop.pricingVersion:y.pricingVersion,
            productType: e.productType,
            widthInches: Number(r.widthInches.toFixed(4)),
            heightInches: Number(r.heightInches.toFixed(4)),
            standardSizeId:
              e.productType === "poster"
                ? !e.customMode && (e.posterDesktopV2 ? !!window.YI2CPosterDesktop.find(e.selectedStandardSizeId) : S.includes(e.selectedStandardSizeId))
                  ? e.selectedStandardSizeId
                  : void 0
                : e.customMode
                  ? void 0
                  : e.selectedStandardSizeId,
            style: e.canvasStyle,
            hangingKit: e.productType === "canvas" && e.hangingKit,
          };
        if (
          ((m._yi2c_configuration = JSON.stringify(I)),
          e.canvasStyle !== "unframed" && (m._yi2c_edge_finish = Xe()),
          e.edgeStyle === "colour" &&
            e.canvasStyle !== "unframed" &&
            (m._yi2c_side_colour = e.edgeColour),
          e.canvasStyle === "box")
        ) {
          const x = {
            black: "Black",
            white: "White",
            darkwood: "Dark wood",
            barewood: "Bare wood",
          };
          ((m._yi2c_frame_colour = x[e.frameColour] || e.frameColour),
            (m._yi2c_float_gap_mm = "5"));
        }
        (e.customMode &&
          ((m._yi2c_custom_size = "true"),
          (m._yi2c_standard_match = e.customMatchedStandardSize?.id || "")),
          e.orderNotes.trim() && (m._yi2c_customer_note = e.orderNotes.trim()));
        try {
          const x = window.Shopify?.routes?.root || "/",
            g = await fetch(`${x}cart.js`, {
              headers: { Accept: "application/json" },
            }).then((L) => L.json());
          let v = null;
          if (
            e.editingConfigurationId &&
            ((v = g.items?.find(
              (L) =>
                L.properties?._yi2c_configuration_id ===
                e.editingConfigurationId,
            )),
            !v)
          )
            throw new Error("This basket item is no longer available to edit.");
          const X = v
              ? { id: v.key, quantity: v.quantity, properties: m }
              : { items: [{ id: o, quantity: 1, properties: m }] },
            H = {
              operation: v ? "change" : "add",
              requestContentType: "application/json",
              credentialsMode: "same-origin",
              requestMethod: "POST",
              payloadShape: {
                ...Ua(X),
                cartItemCountBefore: g.item_count,
                cartLineCountBefore: Array.isArray(g.items)
                  ? g.items.length
                  : 0,
                sameConfigurationInCart: !!g.items?.some(
                  (L) =>
                    L.properties?._yi2c_configuration_id === e.configurationId,
                ),
                sameEditIdentifierInCart: !!(
                  e.recoveryToken &&
                  g.items?.some(
                    (L) =>
                      L.properties?._yi2c_edit_credential === e.recoveryToken,
                  )
                ),
              },
            };
          let M;
          try {
            if (m._yi2c_configuration_id !== e.configurationId) throw new Error("UPLOAD_ORIGINAL_REQUIRED");
            await requireOriginalForBasket();
            Ze(v ? "Updating basket\u2026" : "Adding to basket\u2026");
            M = await fetch(`${x}${v ? "cart/change.js" : "cart/add.js"}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify(X),
            });
          } catch (L) {
            throw (
              await Je({
                ...H,
                httpStatus: 0,
                exceptionName: L instanceof Error ? L.name : "UnknownError",
                exceptionMessage:
                  L instanceof Error
                    ? L.message
                    : "Unknown cart request failure",
              }),
              L
            );
          }
          if (!M.ok) {
            const L = await M.text().catch(() => "");
            let Ee = {};
            try {
              Ee = JSON.parse(L);
            } catch {
              Ee = {};
            }
            const ot = Ee.description || Ee.message || Ee.error || "";
            await Je({
              ...H,
              httpStatus: M.status,
              responseContentType: M.headers.get("content-type") || "",
              responseError: ot,
              responseBody: L,
            });
            const Fa =
              M.status === 422 && ot === "An error occurred in your cart.";
            throw new Error(
              Fa
                ? "We couldn\u2019t add this print because your basket appears to contain an older incompatible item. Your print is safe. Please clear your basket and try again, or contact us if you need help."
                : ot || "Could not add this canvas to the basket.",
            );
          }
          (new URLSearchParams(window.location.search).get(
            "yi2c_cart_diagnostic",
          ) === "1" &&
            (await Je({ ...H, outcome: "success", httpStatus: M.status })),
            K(
              v ? "\u2713 Basket updated" : "\u2713 Added to basket",
              "success",
            ),
            gt(),
            window.Shopify?.designMode || (window.location.href = `${x}cart`));
        } catch (x) {
          K(
            be(x, "Could not add this canvas to the basket. Please try again."),
          );
        } finally {
          n();
        }
      }
      a.fileInput.accept+=", .tif, .tiff, .heic, .heif";
      (a.choosePhotoButton.addEventListener("click", Ve),
        a.sendLargeFileButton &&
          a.sendLargeFileButton.addEventListener("click", Re),
        a.changePhotoButton.addEventListener("click", Ve),
        a.switchToUploadButton &&
          a.switchToUploadButton.addEventListener("click", () =>
            e.retainedUploadState ? restoreRetainedUpload() : Ve(),
          ),
        a.useRetainedUploadButton &&
          a.useRetainedUploadButton.addEventListener(
            "click",
            restoreRetainedUpload,
          ),
        a.fileInput.addEventListener("change", (t) => {
          Ft(t.target.files?.[0]);
        }),
        a.orientationButtons.forEach((t) => {
          t.addEventListener("click", () => {
            ga(t.dataset.orientation);
          });
        }),
        a.widthMinusButton &&
          a.widthMinusButton.addEventListener("click", () => {
            Ne("width", -1);
          }),
        a.widthPlusButton &&
          a.widthPlusButton.addEventListener("click", () => {
            Ne("width", 1);
          }),
        a.heightMinusButton &&
          a.heightMinusButton.addEventListener("click", () => {
            Ne("height", -1);
          }),
        a.heightPlusButton &&
          a.heightPlusButton.addEventListener("click", () => {
            Ne("height", 1);
          }),
        a.widthValueButton &&
          a.widthValueButton.addEventListener("click", () => {
            St("width");
          }),
        a.heightValueButton &&
          a.heightValueButton.addEventListener("click", () => {
            St("height");
          }),
        a.toggleAllSizesButton.addEventListener("click", () => {
          const t = a.allSizes.hidden;
          ((a.allSizes.hidden = !t),
            ie(!1),
            t && ((e.customMode = !1), oe()),
            (a.toggleAllSizesButton.textContent = t
              ? "Hide sizes"
              : "All sizes"),
            C());
        }),
        a.toggleCustomSizeButton.addEventListener("click", Na),
        document.addEventListener("keydown", (t) => {
          t.key === "Escape" &&
            !h.classList.contains("yi2c-mobile-inline") &&
            !a.customPanel.hidden &&
            !(
              e.customMode && window.matchMedia("(min-width: 900px)").matches
            ) &&
            (ie(!1), C());
        }),
        document.addEventListener(
          "pointerdown",
          (t) => {
            if (
              window.matchMedia("(max-width: 899px)").matches &&
              (h.classList.contains("yi2c-mobile-inline") || j || c(".is-mobile-comment-sheet"))
            )
              return;
            const o = t.target,
              i = a.customPanel.contains(o),
              n = a.allSizes.contains(o),
              r = a.toggleCustomSizeButton.contains(o),
              d = a.toggleAllSizesButton.contains(o),
              l =
                window.matchMedia("(min-width: 900px)").matches &&
                !!o.closest?.(
                  "[data-width-minus], [data-width-plus], [data-height-minus], [data-height-plus]",
                );
            !i &&
              !n &&
              !r &&
              !d &&
              !l &&
              (!a.customPanel.hidden || !a.allSizes.hidden) &&
              (pe(), C());
          },
          !0,
        ));
      const ne = c(".yi2c-preview-control-bar"),
        Wt = ne?.parentNode || null,
        Ie = ne ? document.createComment("yi2c-preview-controls") : null;
      ne && Wt && Wt.insertBefore(Ie, ne);
      const privateHeight = c(".yi2c-dimension-control--height");
      const privateHeightHome = privateHeight?.parentNode;
      const privateHeightNext = privateHeight?.nextSibling;
      function Qe(t) {
        if (privateHeight && ne) {
          if (window.matchMedia("(max-width: 899px)").matches && t === "size")
            ne.append(privateHeight);
          else
            privateHeightHome?.insertBefore(privateHeight, privateHeightNext);
        }
        !ne ||
          !Ie ||
          (window.matchMedia("(max-width: 899px)").matches && t === "size"
            ? c('[data-builder-step="size"]')?.prepend(ne)
            : Ie.parentNode && Ie.parentNode.insertBefore(ne, Ie.nextSibling));
      }
      (a.mobileSheetTriggers.forEach((t) =>
        t.addEventListener("click", () => {
          (Y(!!j),
            (j = t.dataset.mobileSheetTrigger),
            Qe(j),
            c(`[data-builder-step="${j}"]`)?.classList.add(
              "is-mobile-sheet-open",
            ),
            ka(
              c(`[data-builder-step="${j}"]`),
              t.querySelector("span")?.textContent || "Options",
              t,
            ),
            a.mobileSheetBackdrop && (a.mobileSheetBackdrop.hidden = !1));
        }),
      ),
        a.mobileSheetBackdrop?.addEventListener("click", () => {
          window.matchMedia("(max-width: 899px)").matches || (Qe(""), Y(!0));
        }),
        a.mobileProductTrigger?.addEventListener("click", () => {
          (Y(!1),
            h.classList.add("is-mobile-product-open"),
            a.mobileProductTrigger.setAttribute("aria-expanded", "true"),
            a.mobileSheetBackdrop && (a.mobileSheetBackdrop.hidden = !1));
        }));
      function Da() {
        (h.classList.remove("is-mobile-adjusting"),
          (a.resetCropButton.textContent = "Re-centre"),
          a.mobileAdjustButton?.focus({ preventScroll: !0 }));
      }
      (a.mobileAdjustDoneButton?.addEventListener("pointerdown", Da, {
        capture: !0,
      }),
        a.mobileAdjustButton?.addEventListener("click", () => {
          (h.classList.add("is-mobile-adjusting"),
            (a.resetCropButton.textContent = "Reset"));
        }),
        a.mobileHangingButton?.addEventListener("click", () =>
          a.hangingKitInput.click(),
        ),
        a.mobileCommentButton?.setAttribute("aria-expanded", "false"),
        a.mobileCommentButton?.setAttribute("aria-label", "Add a comment"),
        a.mobileCommentButton?.addEventListener("click", () => {
          const t = a.orderNotesInput.closest("details"),
            o = t.closest(".yi2c-option-section");
          if (o?.classList.contains("is-mobile-comment-sheet")) {
            (a.orderNotesInput.blur(),
              (t.open = !1),
              t.classList.remove("is-mobile-sheet-open"),
              o.classList.remove(
                "is-mobile-sheet-open",
                "is-mobile-comment-sheet",
              ),
              a.mobileCommentButton.classList.remove("is-comment-open"),
              a.mobileCommentButton.setAttribute("aria-expanded", "false"),
              a.mobileCommentButton.setAttribute("aria-label", "Add a comment"),
              a.mobileSheetBackdrop && (a.mobileSheetBackdrop.hidden = !0),
              a.mobileCommentButton.focus({ preventScroll: !0 }));
            return;
          }
          (Y(!1),
            (t.open = !0),
            t.classList.add("is-mobile-sheet-open"),
            o?.classList.add("is-mobile-sheet-open", "is-mobile-comment-sheet"),
            a.mobileCommentButton.classList.add("is-comment-open"),
            a.mobileCommentButton.setAttribute("aria-expanded", "true"),
            a.mobileCommentButton.setAttribute("aria-label", "Close comment"),
            a.mobileSheetBackdrop && (a.mobileSheetBackdrop.hidden = !1),
            window.matchMedia("(max-width: 899px)").matches ||
              a.orderNotesInput.focus());
        }),
        [a.customWidthInput, a.customHeightInput].forEach((t) => {
          t.addEventListener("keydown", (o) => {
            o.key === "Enter" && t.blur();
          });
        }),
        document.addEventListener("keydown", (t) => {
          t.key === "Escape" &&
            (Qe(""),
            h.classList.remove("is-mobile-adjusting"),
            (a.resetCropButton.textContent = "Re-centre"),
            Y(!0));
        }),
        a.productTypeButtons.forEach((t) => {
          t.addEventListener("click", () => {
            (ya(t.dataset.productType), Y(!1));
          });
        }),
        w("[data-desktop-step]").forEach((t) => {
          t.addEventListener("click", () => {
            t.disabled || _e(t.dataset.desktopStep);
          });
        }),
        w("[data-desktop-continue]").forEach((t) => {
          t.addEventListener("click", () => {
            const o = t.dataset.desktopContinue,
              i =
                e.productType === "canvas" &&
                e.canvasStyle !== "box" &&
                e.canvasStyle !== "unframed";
            if (o === "review" || (o === "sides" && !i)) {
              a.addToCartButton?.focus({ preventScroll: !0 });
              return;
            }
            _e(o);
          });
        }),
        w("[data-unit]").forEach((t) => {
          t.addEventListener("click", () => {
            Ba(t.dataset.unit);
          });
        }),
        a.customWidthInput.addEventListener("input", () => {
          ((e.customWidth = a.customWidthInput.value),
            (e.customMode = !0),
            me("size"),
            oe(),
            e.productType === "canvas" &&
              e.edgeStyle === "gallery" &&
              (e.canvasStyle === "slim" || e.canvasStyle === "chunky") &&
              q(),
            C());
        }),
        a.customHeightInput.addEventListener("input", () => {
          ((e.customHeight = a.customHeightInput.value),
            (e.customMode = !0),
            me("size"),
            oe(),
            e.productType === "canvas" &&
              e.edgeStyle === "gallery" &&
              (e.canvasStyle === "slim" || e.canvasStyle === "chunky") &&
              q(),
            C());
        }),
        w("[data-style]").forEach((t) => {
          t.addEventListener("click", () => {
            const o = e.canvasStyle === "box" && t.dataset.style !== "box";
            (t.dataset.style === "box" && e.canvasStyle !== "box"
              ? ((e.previousEdgeStyle = [
                  "gallery",
                  "mirror",
                  "white",
                  "black",
                  "colour",
                ].includes(e.edgeStyle)
                  ? e.edgeStyle
                  : "gallery"),
                (e.edgeStyle = "mirror"))
              : o && (e.edgeStyle = e.previousEdgeStyle || "gallery"),
              pe(),
              (e.previousCanvasStyle = t.dataset.style),
              (e.canvasStyle = t.dataset.style),
              me("canvas"),
              (e.panX = 0),
              (e.panY = 0),
              e.productType === "canvas" &&
                e.edgeStyle === "gallery" &&
                (e.canvasStyle === "slim" || e.canvasStyle === "chunky") &&
                q(),
              C());
          });
        }),
        w("[data-edge]").forEach((t) => {
          t.addEventListener("click", () => {
            e.canvasStyle === "box" ||
              e.canvasStyle === "unframed" ||
              e.productType !== "canvas" ||
              (pe(),
              (e.edgeStyle = t.dataset.edge),
              (e.previousEdgeStyle = e.edgeStyle),
              me("sides"),
              e.edgeStyle === "gallery"
                ? q()
                : ((e.zoom = 1),
                  (e.panX = 0),
                  (e.panY = 0),
                  (a.zoomSlider.value = "1")),
              C());
          });
        }),
        w("[data-frame-colour]").forEach((t) => {
          t.addEventListener("click", () => {
            (pe(), (e.frameColour = t.dataset.frameColour), C());
          });
        }),
        w("[data-print-colour]").forEach((t) => {
          t.addEventListener("click", () => {
            (pe(), (e.printColour = t.dataset.printColour), me("photo"), C());
          });
        }),
        a.edgeColourInput.addEventListener("input", () => {
          ((e.edgeColour = a.edgeColourInput.value.toUpperCase()), ye(), D());
        }),
        a.edgeColourHueInput?.addEventListener("input", () => {
          ((e.edgeColour = Ta(a.edgeColourHueInput.value)), ye(), D());
        }),
        a.hangingKitInput.addEventListener("change", () => {
          ((e.hangingKit = a.hangingKitInput.checked), C());
        }),
        a.designerCropInput.addEventListener("change", () => {
          ((e.designerCrop = a.designerCropInput.checked), D(), G(), _t());
        }),
        a.positionChoiceButtons.forEach((t) => {
          t.addEventListener("click", () => {
            if (e.imageMethod === "separate") return;
            ((a.designerCropInput.checked =
              t.dataset.positionChoice === "studio"),
              a.designerCropInput.dispatchEvent(
                new Event("change", { bubbles: !0 }),
              ));
          });
        }),
        a.positionMyselfButton?.addEventListener("click", () => {
          ((a.designerCropInput.checked = !1),
            a.designerCropInput.dispatchEvent(
              new Event("change", { bubbles: !0 }),
            ));
        }),
        a.orderNotesInput.addEventListener("input", () => {
          ((e.orderNotes = a.orderNotesInput.value), qt(), je());
        }),
        a.zoomSlider.addEventListener("input", () => {
          Ce(a.zoomSlider.value);
        }),
        a.zoomInButton.addEventListener("click", () => {
          Ce(e.zoom + 0.01);
        }),
        a.zoomOutButton.addEventListener("click", () => {
          Ce(e.zoom - 0.01);
        }),
        a.resetCropButton.addEventListener("click", Rt),
        a.rotateButton.addEventListener("click", () => {
          ((e.rotation = (e.rotation + 90) % 360),
            (e.zoom = 1),
            (e.panX = 0),
            (e.panY = 0),
            (a.zoomSlider.value = "1"),
            e.productType === "canvas" &&
              e.edgeStyle === "gallery" &&
              (e.canvasStyle === "slim" || e.canvasStyle === "chunky") &&
              q(),
            C());
        }),
        a.previewCanvas.addEventListener("pointerdown", _a),
        a.previewCanvas.addEventListener("pointermove", za),
        a.previewCanvas.addEventListener("pointerup", Dt),
        a.previewCanvas.addEventListener("pointercancel", Dt),
        a.previewCanvas.addEventListener(
          "wheel",
          (t) => {
            if (!e.image || e.designerCrop || e.imageMethod === "separate")
              return;
            t.preventDefault();
            const o = t.deltaY > 0 ? -0.08 : 0.08;
            Ce(e.zoom + o);
          },
          { passive: !1 },
        ),
        a.addToCartButton.addEventListener("click", Ht),
        a.mobileAddButton.addEventListener("click", Ht),
        a.editRetryButton?.addEventListener("click", () =>
          window.location.reload(),
        ),
        a.edgeColourHexInput?.addEventListener("change", () => {
          const t = Ke(a.edgeColourHexInput.value);
          t ? ((e.edgeColour = t), ye(), D()) : ye();
        }));
      const handleEdgeColourAction = (t) => {
        const o =
          t.target instanceof Element
            ? t.target.closest(
                "[data-edge-colour-reset], [data-edge-colour-pick], [data-edge-colour-cancel]",
              )
            : null;
        if (!o || !h.contains(o)) return;
        if (o.matches("[data-edge-colour-reset]")) {
          ((e.edgeColour = "#000000"), ye(), D());
          return;
        }
        o.matches("[data-edge-colour-cancel]") ? Ue() : Ma();
      };
      h.addEventListener("click", handleEdgeColourAction);
      const $t = (t) => {
        t.key === "Escape" && e.samplingEdgeColour && Ue();
      };
      document.addEventListener("keydown", $t);
      const et = window.matchMedia("(min-width: 900px)"),
        f = document.getElementById("yi2c-canvas-prototype"),
        tt = a.previewCanvas.parentNode,
        jt = document.createComment("yi2c-editor-canvas-home");
      tt.insertBefore(jt, a.previewCanvas);
      const P = (t) => t && !t.disabled && t.click(),
        fe = (t, o, i) =>
          t.forEach((n) => n.classList.toggle("is-selected", i(n) === o));
      function we() {
        if (!f) return;
        const t = k(),
          o = f.querySelector(".yi2c-artwork"),
          i = et.matches;
        if (
          (i &&
            o &&
            a.previewCanvas.parentNode !== o &&
            o.append(a.previewCanvas),
          !i &&
            a.previewCanvas.parentNode !== tt &&
            tt.insertBefore(a.previewCanvas, jt.nextSibling),
          o)
        ) {
          if (
            (o.classList.toggle(
              "has-image",
              !!e.image && e.imageMethod !== "separate",
            ),
            o.classList.toggle("is-large-file", e.imageMethod === "separate"),
            t)
          ) {
            const v = window.innerWidth >= 1800 && window.innerHeight >= 1e3,
              X = v ? 820 : 720,
              H = v ? 615 : 540,
              M = Math.min(X / t.widthInches, H / t.heightInches);
            ((o.style.width = `${Math.round(t.widthInches * M)}px`),
              (o.style.height = `${Math.round(t.heightInches * M)}px`));
          }
          let g = o.querySelector("[data-large-file-notice]");
          e.imageMethod === "separate"
            ? g ||
              ((g = document.createElement("div")),
              (g.dataset.largeFileNotice = ""),
              (g.className = "yi2c-approved-large-file-notice"),
              (g.innerHTML =
                '<strong>Send your original after ordering</strong><span>Our studio will position and check your photograph before print.</span><button type="button">Return to standard upload</button>'),
              g
                .querySelector("button")
                .addEventListener("click", () => P(a.switchToUploadButton)),
              o.append(g))
            : g?.remove();
        }
        (t &&
          (f
            .querySelectorAll('[data-output="width"]')
            .forEach((g) => (g.textContent = `${B(t.widthInches)}\u2033`)),
          f
            .querySelectorAll('[data-output="height"]')
            .forEach((g) => (g.textContent = `${B(t.heightInches)}\u2033`))),
          fe(
            f.querySelectorAll(".yi2c-orientation button"),
            e.orientation,
            (g) => (g === g.parentNode.children[0] ? "portrait" : "landscape"),
          ),
          fe(f.querySelectorAll("[data-product]"), e.productType, (g) =>
            g.dataset.product.toLowerCase(),
          ),
          fe(
            f.querySelectorAll("[data-finish-choice]"),
            e.canvasStyle,
            (g) =>
              ({ frame: "box" })[g.dataset.finishChoice] ||
              g.dataset.finishChoice,
          ),
          fe(
            f.querySelectorAll("[data-edge-choice]"),
            e.edgeStyle,
            (g) =>
              ({ wrap: "gallery" })[g.dataset.edgeChoice] ||
              g.dataset.edgeChoice,
          ),
          fe(
            f.querySelectorAll('[data-panel="colour"] .yi2c-visual-choice'),
            e.printColour,
            (g) =>
              ["original", "bw", "sepia"][
                [...g.parentNode.children].indexOf(g)
              ],
          ),
          fe(
            f.querySelectorAll("[data-static-frame-colour]"),
            e.frameColour,
            (g) => g.dataset.staticFrameColour,
          ));
        const n = f.querySelector("[data-static-frame-colours]"),
          r = f.querySelector("[data-edge-colour-panel]");
        (n &&
          (n.hidden = e.productType !== "canvas" || e.canvasStyle !== "box"),
          r &&
            (r.hidden = e.productType !== "canvas" || e.edgeStyle !== "colour"),
          f
            .querySelectorAll("[data-canvas-only]")
            .forEach(
              (g) =>
                (g.hidden =
                  e.productType !== "canvas" ||
                  (g.matches("[data-panel]") &&
                    g.dataset.panel !==
                      f.querySelector("[data-step].is-active")?.dataset.step)),
            ));
        const d = f.querySelector("[data-edge-native]"),
          l = f.querySelector("[data-edge-hex]");
        (d && (d.value = e.edgeColour),
          l && (l.value = e.edgeColour),
          f
            .querySelectorAll("[data-edge-live-swatch],[data-edge-preview]")
            .forEach((g) =>
              g.style.setProperty("--edge-colour", e.edgeColour),
            ));
        const u = f.querySelector(".yi2c-order-extra[data-canvas-only] input"),
          p = f.querySelector("#yi2c-comment-toggle"),
          m = f.querySelector("#yi2c-order-comment");
        (u && (u.checked = e.hangingKit),
          p && (p.checked = !!e.orderNotes),
          m && (m.value = e.orderNotes));
        const S = f.querySelector(".yi2c-price"),
          I = f.querySelector("[data-product-summary]"),
          x = f.querySelector("[data-product-heading]");
        (S && (S.textContent = J(e.totalPrice)),
          I &&
            t &&
            (I.textContent = `${e.productType === "poster" ? "Poster" : "Canvas"} \xB7 ${t.label} \xB7 ${e.orientation === "portrait" ? "Portrait" : "Landscape"}`),
          x &&
            (x.textContent =
              e.productType === "poster" ? "Your poster" : "Your canvas"));
      }
      if (f && !f.dataset.controllerBound) {
        ((f.dataset.controllerBound = "true"),
          f
            .querySelector(".yi2c-upload")
            ?.addEventListener("click", () => P(a.choosePhotoButton)),
          f
            .querySelector(".yi2c-send-file")
            ?.addEventListener("click", () => P(a.sendLargeFileButton)),
          f
            .querySelectorAll("[data-product]")
            .forEach((i) =>
              i.addEventListener("click", () =>
                P(
                  a.productTypeButtons.find(
                    (n) =>
                      n.dataset.productType === i.dataset.product.toLowerCase(),
                  ),
                ),
              ),
            ));
        const t = {
          "8 \xD7 8\u2033": "8x8",
          "12 \xD7 12\u2033": "12x12",
          "16 \xD7 16\u2033": "16x16",
          "20 \xD7 20\u2033": "20x20",
          "30 \xD7 30\u2033": "30x30",
          "10 \xD7 8\u2033": "8x10",
          "16 \xD7 12\u2033": "12x16",
          "20 \xD7 16\u2033": "16x20",
          "30 \xD7 25\u2033": "25x30",
          "40 \xD7 30\u2033": "30x40",
          A5: "a5",
          A4: "a4",
          A3: "a3",
          A2: "a2",
          A1: "a1",
          "16 \xD7 9\u2033": "9x16",
          "24 \xD7 10\u2033": "10x24",
          "30 \xD7 12\u2033": "12x30",
          "40 \xD7 20\u2033": "20x40",
        };
        (f
          .querySelectorAll('[data-panel="size"] .yi2c-size-options button')
          .forEach((i) =>
            i.addEventListener("click", () =>
              P(
                h.querySelector(
                  `[data-size-id="${t[i.querySelector("strong")?.textContent.trim()]}"]`,
                ),
              ),
            ),
          ),
          f
            .querySelectorAll("[data-finish-choice]")
            .forEach((i) =>
              i.addEventListener("click", () =>
                P(
                  h.querySelector(
                    `[data-style="${{ frame: "box" }[i.dataset.finishChoice] || i.dataset.finishChoice}"]`,
                  ),
                ),
              ),
            ),
          f
            .querySelectorAll("[data-edge-choice]")
            .forEach((i) =>
              i.addEventListener("click", () =>
                P(
                  h.querySelector(
                    `[data-edge="${{ wrap: "gallery" }[i.dataset.edgeChoice] || i.dataset.edgeChoice}"]`,
                  ),
                ),
              ),
            ),
          f
            .querySelectorAll('[data-panel="colour"] .yi2c-visual-choice')
            .forEach((i, n) =>
              i.addEventListener("click", () =>
                P(
                  h.querySelector(
                    `[data-print-colour="${["original", "bw", "sepia"][n]}"]`,
                  ),
                ),
              ),
            ),
          f
            .querySelectorAll("[data-static-frame-colour]")
            .forEach((i) =>
              i.addEventListener("click", () =>
                P(
                  h.querySelector(
                    `[data-frame-colour="${i.dataset.staticFrameColour}"]`,
                  ),
                ),
              ),
            ),
          f
            .querySelectorAll(".yi2c-orientation button")
            .forEach((i, n) =>
              i.addEventListener("click", () =>
                P(
                  a.orientationButtons.find(
                    (r) =>
                      r.dataset.orientation === (n ? "landscape" : "portrait"),
                  ),
                ),
              ),
            ),
          f
            .querySelectorAll("[data-dimension][data-change]")
            .forEach((i) =>
              i.addEventListener("click", () =>
                P(
                  i.dataset.dimension === "width"
                    ? Number(i.dataset.change) < 0
                      ? a.widthMinusButton
                      : a.widthPlusButton
                    : Number(i.dataset.change) < 0
                      ? a.heightMinusButton
                      : a.heightPlusButton,
                ),
              ),
            ));
        const o = f.querySelectorAll(".yi2c-icon-action");
        (o[0]?.addEventListener("click", () => P(a.rotateButton)),
          o[1]?.addEventListener("click", () => P(a.resetCropButton)),
          f
            .querySelector(".yi2c-position-row input")
            ?.addEventListener("input", (i) => {
              ((a.zoomSlider.value = String(
                1 + ((Number(i.target.value) - 100) / 80) * 3,
              )),
                a.zoomSlider.dispatchEvent(
                  new Event("input", { bubbles: !0 }),
                ));
            }),
          f
            .querySelector("[data-edge-native]")
            ?.addEventListener("input", (i) => {
              ((a.edgeColourInput.value = i.target.value),
                a.edgeColourInput.dispatchEvent(
                  new Event("input", { bubbles: !0 }),
                ),
                we());
            }),
          f
            .querySelector("[data-edge-hex]")
            ?.addEventListener("change", (i) => {
              ((a.edgeColourHexInput.value = i.target.value),
                a.edgeColourHexInput.dispatchEvent(
                  new Event("change", { bubbles: !0 }),
                ),
                we());
            }),
          f.querySelector("[data-edge-hue]")?.addEventListener("input", (i) => {
            const n = Number(i.target.value) / 60,
              r = 0.44,
              d = r * (1 - Math.abs((n % 2) - 1)),
              l = 0.36,
              u =
                n < 1
                  ? [r, d, 0]
                  : n < 2
                    ? [d, r, 0]
                    : n < 3
                      ? [0, r, d]
                      : n < 4
                        ? [0, d, r]
                        : n < 5
                          ? [d, 0, r]
                          : [r, 0, d],
              p = `#${u
                .map((m) =>
                  Math.round((m + l) * 255)
                    .toString(16)
                    .padStart(2, "0"),
                )
                .join("")}`;
            ((a.edgeColourInput.value = p),
              a.edgeColourInput.dispatchEvent(
                new Event("input", { bubbles: !0 }),
              ),
              we());
          }),
          f
            .querySelector("[data-edge-colour-reset]")
            ?.addEventListener("click", () => {
              ((e.edgeColour = "#000000"), ye(), D(), we());
            }),
          f
            .querySelector("[data-edge-photo-pick], [data-edge-colour-pick]")
            ?.addEventListener("click", Ma),
          f
            .querySelector(".yi2c-order-extra[data-canvas-only] input")
            ?.addEventListener("change", (i) => {
              ((a.hangingKitInput.checked = i.target.checked),
                a.hangingKitInput.dispatchEvent(
                  new Event("change", { bubbles: !0 }),
                ));
            }),
          f
            .querySelector("#yi2c-order-comment")
            ?.addEventListener("input", (i) => {
              ((a.orderNotesInput.value = i.target.value),
                a.orderNotesInput.dispatchEvent(
                  new Event("input", { bubbles: !0 }),
                ));
            }),
          f
            .querySelector(".yi2c-add")
            ?.addEventListener("click", () => P(a.addToCartButton)));
      }
      const Kt = () => {
        (Y(), Qe(""), Ue(), we(), _e(), window.setTimeout(D, 0));
      };
      (et.addEventListener("change", Kt), we());
      let at = null;
      const Vt = () => {
        (window.clearTimeout(at),
          (at = window.setTimeout(() => {
            (D(), syncPreviewGeometry());
          }, 80)));
      };
      (window.addEventListener("resize", Vt),
        (h.__yi2cCleanup = () => {
          (window.removeEventListener("resize", Vt),
            et.removeEventListener("change", Kt),
            document.removeEventListener("keydown", $t),
            h.removeEventListener("click", handleEdgeColourAction),
            window.clearTimeout(at),
            (e.uploadGeneration += 1),
            e.activeUploadRequests.forEach((t) => t.abort()),
            e.activeUploadRequests.clear(),
            e.objectUrl && URL.revokeObjectURL(e.objectUrl));
        }),
        ge(),
        G(),
        Et(),
        Be(),
        vt(),
        _e(),
        (h.__yi2cPrivateRestore = da()));
      // Flush the existing verified upload/configuration save before header navigation.
      // Never silently change a basket item's configuration from a navigation click.
      let navigationBaseline;
      h.__yi2cPrivateRestore.then(()=>{navigationBaseline=JSON.stringify(le());});
      window.YI2CBuilderBeforeNavigate=async()=>{
        await h.__yi2cPrivateRestore;
        document.activeElement?.blur();
        if(e.addingToCart)throw Object.assign(new Error('Basket busy'),{navigationMessage:'Please wait for your basket update, then try the link again.'});
        if(e.uploadPromise)await e.uploadPromise;
        if(e.editingConfigurationId){
          if(JSON.stringify(le())!==navigationBaseline)throw Object.assign(new Error('Pending basket edit'),{navigationMessage:'Please review and update your basket item before leaving, so your latest changes are kept.'});
          return;
        }
        if(e.image||e.imageMethod==='separate')await pt();
        if(e.configurationId)Qt();
      };
      if (priceEntry) {
        // Initialise the visible controls once, before the desktop/mobile presentations mount.
        a.customWidthInput.value = e.customWidth;
        a.customHeightInput.value = e.customHeight;
        ie(true);
        C();
      }
      if (!priceEntry && offerId === "poster-custom") {
        // Entry-only UI setup. Never reapply the URL to subsequent customer choices.
        a.customWidthInput.value = "";
        a.customHeightInput.value = "";
        ie(true);
        bt();
      }
      // Read-only presentation snapshot. No configuration or crop state is copied back.
      h.__yi2cMobileCanvasView = () => {
        if (!matchMedia("(max-width:899px)").matches || e.productType !== "canvas") return null;
        const size = k();
        return {
          ready: !!e.image && e.imageMethod === "upload" && !a.editor.hidden,
          filename: e.cleanedOriginalFilename || e.file?.name || "Your photograph",
          imageUrl: e.workingPreviewUrl || e.objectUrl || e.image?.src || "",
          sizeLabel: size?.label || "Custom size",
          specificationKey: JSON.stringify([size?.widthInches, size?.heightInches, e.orientation, e.canvasStyle, e.edgeStyle, e.edgeColour, e.frameColour]),
          style: e.canvasStyle, frameColour: e.frameColour, edge: e.edgeStyle, designer: e.designerCrop,
          galleryBlank: e.edgeStyle === "gallery" && e.galleryMissingSides.length > 0 && !e.designerCrop,
          rotation: e.rotation,
        };
      };
      const mountDesktop = () => window.__yi2cMountDesktop?.(h, {
        state: e,
        elements: a,
        posterUnitCm: () => {
          if(e.productType !== "poster")return;
          if(e.customUnit !== "cm") {
            e.customWidth=e.customWidth === "" ? "" : Number((Number(e.customWidth)*2.54).toFixed(3));
            e.customHeight=e.customHeight === "" ? "" : Number((Number(e.customHeight)*2.54).toFixed(3));
            e.customUnit="cm";
            a.customWidthInput.value=e.customWidth;a.customHeightInput.value=e.customHeight;
          }
        },
        posterPreset: id => {
          if(e.productType !== "poster" || !window.YI2CPosterDesktop.find(id))return;
          e.selectedStandardSizeId=id;e.customMode=false;e.customUnit="cm";
          ie(false);C();
        },
        posterCustom: () => {
          if(e.productType !== "poster")return;
          const size=k();e.customUnit="cm";
          if(!e.customMode && size){e.customWidth=Number((size.widthInches*2.54).toFixed(3));e.customHeight=Number((size.heightInches*2.54).toFixed(3));}
          e.customMode=true;e.selectedStandardSizeId=null;
          a.customWidthInput.value=e.customWidth;a.customHeightInput.value=e.customHeight;C();
        },
        posterDimension: (dimension,value) => {
          if(e.productType !== "poster")return;
          e.customMode=true;e.customUnit="cm";e.selectedStandardSizeId=null;
          if(dimension === "width"){e.customWidth=value;a.customWidthInput.value=value;}
          else {e.customHeight=value;a.customHeightInput.value=value;}
          if(Number(e.customWidth)>0&&Number(e.customHeight)>0)e.orientation=Number(e.customWidth)>=Number(e.customHeight)?"landscape":"portrait";
          C();
        },
        posterOrientation: orientation => {
          if(e.productType !== "poster")return;
          if(e.customMode && e.orientation!==orientation) {
            [e.customWidth,e.customHeight]=[e.customHeight,e.customWidth];
            a.customWidthInput.value=e.customWidth;a.customHeightInput.value=e.customHeight;
            e.orientation=orientation;C();
          } else ga(orientation);
        },
        posterRefresh: C,
        size: k,
        validSize: () => !e.customMode || bt().valid,
        reviewAspect: () => {const size=k();return e.productType==='canvas'&&e.canvasStyle==='box'?(size.widthInches*25.4+34)/(size.heightInches*25.4+34):size.widthInches/size.heightInches;},
        reviewFace: (width) => {
          if(e.productType==='canvas'&&e.canvasStyle==='box')return framedDisplayCanvas(width*(window.devicePixelRatio||1));
          if (!e.geometry) return null;
          const face = e.geometry.frontFace,
            transform = Pt(face);
          return Oe(
            face,
            transform,
            photoDisplayDensity(
              transform,
              ((width || face.width) / face.width) *
                (window.devicePixelRatio || 1),
            ),
          );
        },
      });
      // A private Basket Edit must restore its saved product before mounting the
      // product-specific panel; do not initially mount Canvas over a Poster draft.
      if (h.hasAttribute('data-poster-desktop-private') && (ee() || lockedProductType==='poster')) h.__yi2cPrivateRestore.then(mountDesktop);
      else mountDesktop();
    }
    function Xt(h) {
      if (!h || h.dataset.yi2cDesktopPresentationReady === "true") return;
      const orientationTarget = h.querySelector(
          "[data-desktop-orientation-target]",
        ),
        orientation = h.querySelector(".yi2c-preview-orientation"),
        orientationHome = orientation?.parentNode || null,
        orientationMarker = orientationHome
          ? document.createComment("yi2c-orientation-home")
          : null,
        colourSection = h.querySelector('[data-builder-step="photo"]'),
        colourHome = colourSection?.parentNode || null,
        colourMarker = colourHome
          ? document.createComment("yi2c-colour-controls-home")
          : null,
        photoTools = h.querySelector("[data-photo-tools]"),
        designerHelp = h.querySelector(".yi2c-designer-help"),
        designerHelpHome = designerHelp?.parentNode || null,
        designerHelpMarker = designerHelpHome
          ? document.createComment("yi2c-designer-help-home")
          : null,
        desktopPhotoPositioningTarget = h.querySelector(
          "[data-desktop-photo-positioning-target]",
        ),
        productSwitch = h.querySelector(".yi2c-product-switch"),
        productSwitchHome = productSwitch?.parentNode || null,
        productSwitchMarker = productSwitchHome
          ? document.createComment("yi2c-product-switch-home")
          : null,
        desktopPhotoProductTarget = h.querySelector(
          "[data-desktop-photo-product-target]",
        ),
        desktop = window.matchMedia("(min-width: 900px)");
      orientationHome &&
        orientationMarker &&
        orientation &&
        orientationHome.insertBefore(orientationMarker, orientation);
      colourHome &&
        colourMarker &&
        colourSection &&
        colourHome.insertBefore(colourMarker, colourSection);
      designerHelpHome &&
        designerHelpMarker &&
        designerHelp &&
        designerHelpHome.insertBefore(designerHelpMarker, designerHelp);
      productSwitchHome &&
        productSwitchMarker &&
        productSwitch &&
        productSwitchHome.insertBefore(productSwitchMarker, productSwitch);
      const syncPresentation = () => {
        if (desktop.matches) {
          orientationTarget?.append(orientation);
          photoTools?.append(colourSection);
          desktopPhotoPositioningTarget?.append(designerHelp);
          desktopPhotoProductTarget?.append(productSwitch);
          return;
        }
        orientationHome &&
          orientationMarker &&
          orientation &&
          orientationHome.insertBefore(
            orientation,
            orientationMarker.nextSibling,
          );
        colourHome &&
          colourMarker &&
          colourSection &&
          colourHome.insertBefore(colourSection, colourMarker.nextSibling);
        designerHelpHome &&
          designerHelpMarker &&
          designerHelp &&
          designerHelpHome.insertBefore(
            designerHelp,
            designerHelpMarker.nextSibling,
          );
        productSwitchHome &&
          productSwitchMarker &&
          productSwitch &&
          productSwitchHome.insertBefore(
            productSwitch,
            productSwitchMarker.nextSibling,
          );
      };
      h.dataset.yi2cDesktopPresentationReady = "true";
      syncPresentation();
      desktop.addEventListener("change", syncPresentation);
      const adjustDone = h.querySelector("[data-mobile-adjust-done]");
      const adjustTools = h.querySelector(".yi2c-photo-tools");
      const adjustStack = document.createElement("div");
      adjustStack.className = "yi2c-private-adjust-stack";
      const mobileAdjust = window.matchMedia("(max-width:899px)");
      const adjustNodes = [adjustTools, designerHelp, adjustDone].filter(
        Boolean,
      );
      const adjustHomes = adjustNodes.map((node) => {
        const marker = document.createComment("private-adjust-home");
        node.before(marker);
        return marker;
      });
      h.append(adjustStack);
      const syncAdjustStack = () => {
        if (mobileAdjust.matches) adjustStack.append(...adjustNodes);
        else
          adjustNodes.forEach((node, i) => {
            if (node.parentNode === adjustStack) adjustHomes[i].after(node);
          });
      };
      syncAdjustStack();
      mobileAdjust.addEventListener("change", syncAdjustStack);
      const cleanup = h.__yi2cCleanup;
      h.__yi2cCleanup = () => {
        mobileAdjust.removeEventListener("change", syncAdjustStack);
        desktop.removeEventListener("change", syncPresentation);
        cleanup?.();
      };
    }
    function rt(h = document) {
      h.querySelectorAll("[data-yi2c-builder]").forEach(Xt);
    }
    function st(h = document) {
      h.querySelectorAll("[data-yi2c-builder]").forEach(Yt);
    }
    ((window.__yi2cInitialiseAllBuilders = st),
      st(),
      rt(),
      window.__yi2cDesktopSizePresentationListenersReady ||
        ((window.__yi2cDesktopSizePresentationListenersReady = !0),
        document.addEventListener("shopify:section:load", (h) => {
          rt(h.target || document);
        })),
      window.__yi2cThemeEditorListenersReady ||
        ((window.__yi2cThemeEditorListenersReady = !0),
        document.addEventListener("shopify:section:load", (h) => {
          window.__yi2cInitialiseAllBuilders(h.target || document);
        }),
        document.addEventListener("shopify:section:unload", (h) => {
          h.target
            ?.querySelectorAll?.("[data-yi2c-builder]")
            .forEach((c) => c.__yi2cCleanup?.());
        })));
  })();
})();
